import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence, MotionConfig, useSpring, useTransform, useMotionValue, useReducedMotion, useMotionValueEvent, animate } from "framer-motion";
import { loadPincodeDb } from '../utils/pincodeDb';
import {
  User as UserIcon, Phone as PhoneIcon, Mail as MailIcon, MapPin as MapPinIcon,
  Store as StoreIcon, Package as PackageIcon, Truck as TruckIcon, IndianRupee as RupeeIcon, Tag as TagIcon, Box as BoxIcon,
  Check as CheckIcon, Sparkles as SparklesIcon, Search as SearchIcon, RefreshCw as RefreshIcon,
  LayoutGrid as GridIcon, Plus as PlusIcon, PackageOpen as PackageOpenIcon, ExternalLink as ExternalLinkIcon,
  X as XIcon, ArrowRight, ArrowLeft, Copy as CopyIcon, Printer as PrinterIcon,
  FileText as FileTextIcon, Zap as ZapIcon, Pencil as PencilIcon, Trash2 as TrashIcon, MoreVertical as MoreVerticalIcon,
  Archive as ArchiveIcon, RotateCcw as RestoreIcon, Lightbulb as LightbulbIcon,
  ShieldCheck as ShieldCheckIcon, TrendingUp as TrendingUpIcon, ChevronDown as ChevronDownIcon,
  Clock as ClockIcon, Plane as PlaneIcon, Lock as LockIcon,
} from "lucide-react";
import { API } from '../utils/apiUrls';
import { deepTrimStrings } from '../utils/helpers';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletBalance } from '../redux/slices/walletSlice';
import PickupAddressModal, { deletePickupAddress, DeleteConfirmModal } from '../components/PickupAddressModal';
import { useOnboardingGate } from '../components/onboarding/OnboardingGate';
import OrderCreationIssueModal from '../components/orders/OrderCreationIssueModal';
import { interpretOrderCreationError, interpretOrderCreationResponse } from '../utils/orderCreationError';

/* ─────────────────────────────────────────────────────────────────────────────
   The idea: the page is a desk. On the left you fill things in, quietly and
   fast. On the right sits the shipment's ticket — a living artifact where the
   route draws itself, a truck travels from origin to destination as the steps
   complete, and the details print line by line. Booking stamps it.

   Tokens follow the shipment-details page exactly: solid #3877FC accent
   (#2A59BD on hover), slate everywhere else, emerald only for done.
──────────────────────────────────────────────────────────────────────────── */
const CARD = "rounded-2xl border border-[#E4E9F1] border-b-[#D8DFEA] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-16px_rgba(15,23,42,0.10),0_24px_48px_-24px_rgba(30,58,138,0.10)]";

/* One voice for every field message - shown live on blur and after Continue.
   validateStep's own strings still gate the step; these are what the merchant reads. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* A stable SKU suggestion from a product name - the same name always suggests
   the same id, which is exactly what keeps one product from growing five SKUs. */
const suggestSkuFromName = (name) =>
  String(name || "").toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 24);

/* Saved-location display helpers (render-only): source data often arrives in
   all-caps - title-case it for reading; initials feed the monogram chip. */
const titleCaseLoc = (v) => String(v || "").toLowerCase().replace(/(^|[^a-z0-9])([a-z])/g, (m, pre, ch) => pre + ch.toUpperCase());
const displayLocName = (v) => { const t = String(v || "").trim(); return t && t === t.toUpperCase() ? titleCaseLoc(t) : t; };
const locInitials = (v) => {
  const w = String(v || "").trim().split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] || "") + (w[1]?.[0] || w[0]?.[1] || "")).toUpperCase();
};

const FIELD_MSG = {
  phoneNumber: "Must be 10 digits — drop the +91 or leading 0",
  name: "Add the recipient's name",
  email: "Enter a valid email address",
  pickup: "Choose a pickup location to continue",
  address: "Add the full address — at least 10 characters",
  city: "Add the city",
  state: "Add the state",
  pincode: "Enter the 6-digit pincode",
  clientOrderId: "Add an order reference, or hit Generate",
  invoiceValue: "Enter an invoice value above ₹0",
  codAmount: "Enter the amount to collect on delivery",
  "item-name": "Add the item name",
  "item-skuId": "Add a SKU, or generate one",
  "item-quantity": "Quantity must be a whole number, 1 or more",
  "item-unitPrice": "Add the unit price",
  weight: "Enter a weight above 0, up to 200 kg",
  length: "Enter a size above 0, up to 500 cm",
  breadth: "Enter a size above 0, up to 500 cm",
  height: "Enter a size above 0, up to 500 cm",
};
const FIELD_STEP = { phoneNumber: 1, name: 1, email: 1, address: 1, city: 1, state: 1, pincode: 1, pickup: 2, clientOrderId: 3, invoiceValue: 3, codAmount: 3, weight: 4, length: 4, breadth: 4, height: 4 };
const LABEL = "block text-[13px] font-semibold text-slate-800 mb-1.5";
const SPRING = { type: "spring", stiffness: 320, damping: 32 };

const inputClass = (hasError, extra = "") =>
  `h-11 w-full rounded-xl border bg-white px-3.5 text-sm font-semibold text-slate-900 placeholder:font-medium placeholder:text-slate-500 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none transition-[border-color,box-shadow] duration-150 ` +
  `focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ` +
  `${hasError ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"} ${extra}`;

const BTN_PRIMARY = "group inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-xl bg-[#3877FC] px-5 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_1px_2px_rgba(15,23,42,0.10),0_8px_20px_-8px_rgba(56,119,252,0.50)] transition-all duration-150 hover:bg-[#2A59BD] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_10px_24px_-8px_rgba(42,89,189,0.55)] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 focus-visible:ring-offset-2 disabled:opacity-60 disabled:shadow-none";
const BTN_OUTLINE = "inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-xl bg-white px-4 text-sm font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-[#D8DFEA] transition-all duration-150 hover:bg-[#F8FAFD] hover:text-slate-900 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 disabled:opacity-60";
const BTN_GHOST = "group inline-flex h-11 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 cursor-pointer";

/* Form-layout preference: "horizontal" (one section at a time, the stepper) or "vertical"
   (sections stack up on one page as you continue). Kept in a cookie for a year so the
   merchant's choice survives sessions. Default: horizontal. */
const LAYOUT_COOKIE = "wv_cfo_layout";
const readLayoutPreference = () => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${LAYOUT_COOKIE}=([^;]*)`));
    return m && decodeURIComponent(m[1]) === "vertical" ? "vertical" : "horizontal";
  } catch { return "horizontal"; }
};
const writeLayoutPreference = (mode) => {
  try { document.cookie = `${LAYOUT_COOKIE}=${mode}; path=/; max-age=31536000; SameSite=Lax`; } catch { /* cookies blocked: the choice still applies this session */ }
};
// Tiny glyphs for the layout picker: a row of steps vs a stack of sections.
function LayoutStepsGlyph({ active }) {
  const c = active ? "#3877FC" : "#94A3B8";
  return (
    <svg width="44" height="22" viewBox="0 0 44 22" fill="none" aria-hidden="true">
      <rect x="1" y="4" width="12" height="14" rx="3" fill={c} />
      <rect x="16" y="4" width="12" height="14" rx="3" stroke={c} strokeWidth="1.5" />
      <rect x="31" y="4" width="12" height="14" rx="3" stroke={c} strokeWidth="1.5" strokeDasharray="2.5 2" />
    </svg>
  );
}
function LayoutStackGlyph({ active }) {
  const c = active ? "#3877FC" : "#94A3B8";
  return (
    <svg width="44" height="22" viewBox="0 0 44 22" fill="none" aria-hidden="true">
      <rect x="6" y="1" width="32" height="5" rx="2" fill={c} />
      <rect x="6" y="8.5" width="32" height="5" rx="2" stroke={c} strokeWidth="1.5" />
      <rect x="6" y="16" width="32" height="5" rx="2" stroke={c} strokeWidth="1.5" strokeDasharray="2.5 2" />
    </svg>
  );
}

/* <input type="number"> accepts "e", "+", "-" (and repeats of ".") as "number characters" — the
   box shows "10-e" while the DOM reports value "" and nothing validates. These guards keep such
   keys out at the source; the blur hook catches whatever a browser still lets through. */
const numericKeyGuard = (integer = false) => (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const k = e.key;
  if (k === "e" || k === "E" || k === "+" || k === "-") { e.preventDefault(); return; }
  if (k === "." && (integer || String(e.currentTarget.value).includes("."))) e.preventDefault();
};
const numericPasteGuard = (integer = false) => (e) => {
  const text = (e.clipboardData && e.clipboardData.getData("text")) || "";
  if (!(integer ? /^\d+$/ : /^\d*\.?\d*$/).test(text.trim())) e.preventDefault();
};
const fmtInr = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const initialsOf = (name) =>
  String(name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "•";

/* Courier presentation helpers (manual selection) ───────────────────────────── */
// A raw name like "EKART SURFACE" carries the carrier and its service mode; split them
// so each can be styled. Carriers title-case (EKART → Ekart) but short acronyms (DTDC)
// stay upper.
const CARRIER_MODES = ["SURFACE", "AIR", "EXPRESS", "PREMIUM", "STANDARD", "HEAVY", "ECONOMY", "PRIORITY", "SECURE", "LITE"];
const formatCarrier = (raw) => {
  const parts = String(raw || "").trim().split(/\s+/).filter(Boolean);
  let mode = null;
  if (parts.length > 1 && CARRIER_MODES.includes(parts[parts.length - 1].toUpperCase())) {
    const m = parts.pop();
    mode = m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
  }
  const carrier = parts
    .map((w) => (w.length <= 4 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ");
  return { carrier: carrier || String(raw || "").trim(), mode };
};
// A stable, tasteful accent per carrier (soft-tinted avatar) so the list reads as distinct
// brands without shouting. Selection stays the app blue.
const CARRIER_ACCENTS = ["#3877FC", "#6D5EF6", "#0EA5A6", "#D9643B", "#DB4A6B", "#2563EB", "#0E9F6E", "#0891B2"];
const carrierAccent = (name) => {
  let h = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CARRIER_ACCENTS[h % CARRIER_ACCENTS.length];
};
// Service mode → a soft tinted chip + a mode glyph (ground vs air), lighter than the
// Cheapest/Fastest ranking pills so the ranking stays the primary signal.
const MODE_STYLE = {
  Surface: "bg-slate-100 text-slate-600 ring-slate-200/80",
  Air: "bg-sky-50 text-sky-700 ring-sky-100",
  Express: "bg-amber-50 text-amber-700 ring-amber-100",
  Premium: "bg-violet-50 text-violet-700 ring-violet-100",
  Priority: "bg-rose-50 text-rose-700 ring-rose-100",
};
const MODE_ICON = { Surface: TruckIcon, Air: PlaneIcon };
// TAT (max days from pickup) → a tangible "latest by" date, compact and consistent
// (guaranteed 3-letter month/day, so rows stay tidy on mobile). "By" already implies latest.
const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const estDeliveryDate = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n < 0) return null;
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${WEEKDAY_ABBR[d.getDay()]}, ${d.getDate()} ${MONTH_ABBR[d.getMonth()]}`;
};

const CreateForwardOrder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [layoutMode, setLayoutMode] = useState(readLayoutPreference); // "horizontal" | "vertical", see LAYOUT_COOKIE
  const [pendingDraftLanding, setPendingDraftLanding] = useState(false); // draft/clone hydrated → place the stepper from its data
  const stacked = layoutMode === "vertical";
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [bookedAt, setBookedAt] = useState(null); // stamped moment of booking (client clock)
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [pincodeLookupFailed, setPincodeLookupFailed] = useState(false); // lookup miss is not an error - the merchant just types city/state
  /* The saved-locations list scrolls inside the card, with edge fades that
     appear only when more content lies beyond the edge. */
  const [pickupMenuFor, setPickupMenuFor] = useState(null); // which card's ... menu is open
  const [refreshingPickups, setRefreshingPickups] = useState(false); // refresh glyph spinning (min one turn)
  const pickupListRef = useRef(null);
  const pickupListRO = useRef(null);
  const stepRowRef = useRef(null); // the horizontally-scrollable step pills row (mobile)
  const stepBodyRef = useRef(null); // the current step's scrolling body — drives the "more below" cue
  const footerRef = useRef(null);   // the sticky action dock (so the cue clears it)
  const [moreBelow, setMoreBelow] = useState(false); // is there form content past the fold?
  const moreBelowComputeRef = useRef(() => {}); // latest cue compute — callable from inner-list observers
  const [pickupEdges, setPickupEdges] = useState({ top: true, bottom: true });
  const updatePickupEdges = () => {
    const el = pickupListRef.current;
    if (!el) return;
    const top = el.scrollTop <= 2;
    const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setPickupEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    moreBelowComputeRef.current(); // the list's own scroll/resize changes what's hidden below
  };
  const [courierServiceability, setCourierServiceability] = useState([]);
  const [customerLookupResults, setCustomerLookupResults] = useState([])
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const location = useLocation();
  const cloneFrom = location?.state?.cloneFrom;
  const [draftOrderId, setDraftOrderId] = useState(location?.state?.draftOrderId || null);
  const cloneMode = Boolean(cloneFrom);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSuccessModal, setDraftSuccessModal] = useState({ open: false, draftId: null, isUpdate: false });
  const [orderIssue, setOrderIssue] = useState(null);
  const dispatch = useDispatch();
  const walletType = useSelector((s) => s.wallet?.type); // fallback settlement source (redux wallet type)
  // Authoritative settlement type from OMS: resolves SQL wallet type OR Mongo onboarding preference.
  const [resolvedSettlement, setResolvedSettlement] = useState(null);
  const { requireBusinessOps } = useOnboardingGate();

  const [formData, setFormData] = useState({
    customerDetails: {
      name: "",
      phoneNumber: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
    },
    orderDetails: {
      clientOrderId: "",
      invoiceValue: 0,
      itemDetails: [
        {
          name: "",
          quantity: 1,
          unitPrice: "",
          skuId: "",
          hsnCode: "",
          isFragile: false,
        },
      ]
    },
    packageDetails: {
      weight: "",
      length: "",
      breadth: "",
      height: "",
    },
    courierDetails: {
      courierPartner: "",
      useRecommendation: true,
    },
    paymentMode: "Prepaid",
  });

  const resetForm = () => {
    setFormData({
      customerDetails: {
        name: "",
        phoneNumber: "",
        email: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
      },
      orderDetails: {
        clientOrderId: "",
        invoiceValue: 0,
        itemDetails: [
          {
            name: "",
            quantity: 1,
            unitPrice: "",
            skuId: "",
            hsnCode: "",
            isFragile: false,
          },
        ]
      },
      packageDetails: {
        weight: "",
        length: "",
        breadth: "",
        height: "",
      },
      courierDetails: {
        courierPartner: "",
        useRecommendation: true,
      },
      paymentMode: "Prepaid",
    });

    setStep(1);
    setHighestStepReached(1); // Reset highest step reached
    setResponseData(null);
    setSelectedPickup(null);
    setErrors({});
    setTouched({});
    setExpandedHSNItems({});
  };

  const [pickupDetails, setPickupDetails] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [courierOptions, setCourierOptions] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({}); // fields visited with content - live validation shows for these (or after Continue)

  /* Order-reference precheck: while the merchant types a reference, ask OMS whether one already
     exists for it (read-only) so we can block before they fill and submit the whole form.
     Fails open (endpoint down / not deployed → allow). cloneMode/retry is skipped (that flow
     brings its own "_retry" reference). */
  const [orderRefCheck, setOrderRefCheck] = useState({ ref: "", outcome: "NONE", awb: null });
  const orderRefSeq = useRef(0);
  const orderRefTimer = useRef(null);
  useEffect(() => {
    const ref = String(formData.orderDetails.clientOrderId || "").trim();
    const seq = ++orderRefSeq.current; // invalidate any in-flight response
    if (orderRefTimer.current) clearTimeout(orderRefTimer.current);
    if (cloneMode || !ref) { setOrderRefCheck({ ref: "", outcome: "NONE", awb: null }); return; }
    orderRefTimer.current = setTimeout(() => {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      axios.get(API.orderReferencePrecheck(ref), { headers: { Authorization: authToken, "x-merchant-id": merchantId }, validateStatus: () => true })
        .then((resp) => {
          if (seq !== orderRefSeq.current) return;
          if (resp.status >= 200 && resp.status < 300 && resp.data && resp.data.exists) {
            setOrderRefCheck({ ref, outcome: String(resp.data.outcome || "UNKNOWN"), awb: resp.data.awbNumber || null });
          } else {
            setOrderRefCheck({ ref, outcome: "NONE", awb: null }); // non-exists or any error → allow
          }
        })
        .catch(() => { if (seq === orderRefSeq.current) setOrderRefCheck({ ref, outcome: "NONE", awb: null }); });
    }, 450);
    return () => { if (orderRefTimer.current) clearTimeout(orderRefTimer.current); };
  }, [formData.orderDetails.clientOrderId, cloneMode]);
  const [units, setUnits] = useState({ weight: 'gm', length: 'cm', breadth: 'cm', height: 'cm' });
  const reverseMatcher = /(_B2CREVERSE_|REVERSE_)/i;
  const [highestStepReached, setHighestStepReached] = useState(1); // Track highest step user has reached
  const [expandedHSNItems, setExpandedHSNItems] = useState({}); // Track which items have HSN code expanded

  // Address management state
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  const [addrModalMode, setAddrModalMode] = useState("add"); // "add" | "edit"
  const [addrModalLocation, setAddrModalLocation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pickupToDelete, setPickupToDelete] = useState(null);
  const [deletingPickup, setDeletingPickup] = useState(false);

  // Presentation-only state: never touches the payload.
  const [pickupQuery, setPickupQuery] = useState("");
  const [showUnserviceable, setShowUnserviceable] = useState(false);
  const [courierSort, setCourierSort] = useState("cheapest"); // "cheapest" | "fastest" — display order only
  const [awbCopied, setAwbCopied] = useState(false);

  // Helper: decode JWT and extract username
  const getUsernameFromToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || '';
    } catch (e) {
      return '';
    }
  };

  // Generate random SKU: first 3 letters of username + '-' + 6-digit random
  const generateRandomSku = () => {
    const username = getUsernameFromToken();
    const prefix = username.slice(0, 3).toUpperCase();
    const randomSix = Math.floor(100000 + Math.random() * 900000);
    return `${prefix}-${randomSix}`;
  };

  const generateSkuForItem = (index) => {
    handleItemChange(index, 'skuId', generateRandomSku());
  };

  // ---Gen-ID helper ---
  const handleGenerateOrderId = () => {
    setFormData((f) => ({
      ...f,
      orderDetails: {
        ...f.orderDetails,
        clientOrderId: uuidv4(),
      },
    }));
  };

  // --- fetchSKUDetails populates name/price/quantity + package dims ---
  /* SKU typeahead: a debounced catalog search under the SKU field, plus a small
     "add new SKU" dialog when nothing matches. Selection reuses fetchSKUDetails,
     so the autofill chain stays the single source of truth. */
  const [skuSearch, setSkuSearch] = useState({ index: null, q: "", results: [], open: false, loading: false, hi: -1, byName: false });
  const skuSearchTimer = useRef(null);
  const skuSearchSeq = useRef(0);
  const [newSkuDialog, setNewSkuDialog] = useState(null);
  const [savingNewSku, setSavingNewSku] = useState(false);

  // Full-catalog browser modal: blank query lists all SKUs (paged); a query runs
  // the bounded search. Opened from the SKU field's Browse icon.
  const CATALOG_PAGE_SIZE = 50;
  const [catalog, setCatalog] = useState({ open: false, index: null, q: "", items: [], total: 0, activeTotal: 0, archivedTotal: 0, page: 0, mode: "list", view: "browse", loading: false, loadingMore: false, error: false, showArchived: false });
  const [catalogSelected, setCatalogSelected] = useState(() => new Set()); // product_sku_id values checked in the modal
  const [catalogArchive, setCatalogArchive] = useState(null); // catalogue row awaiting archive confirmation
  const [archivingSku, setArchivingSku] = useState(false);
  const [restoringSku, setRestoringSku] = useState(null); // product_sku_id currently being restored
  const catalogSeq = useRef(0);
  const catalogDebounce = useRef(null);

  const runCatalogQuery = (q, includeArchived) => {
    const seq = ++catalogSeq.current;
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    const trimmed = String(q || "").trim();
    setCatalog((c) => ({ ...c, loading: true, error: false }));
    const url = trimmed ? API.searchProductSkus(merchantId, trimmed, 20, includeArchived) : API.listProductSkus(merchantId, 0, CATALOG_PAGE_SIZE, includeArchived);
    axios.get(url, { headers: { Authorization: authToken }, validateStatus: () => true })
      .then((res) => {
        if (seq !== catalogSeq.current) return; // a newer query superseded this one
        if (res.status >= 200 && res.status < 300) {
          const items = trimmed ? (Array.isArray(res.data) ? res.data : []) : (Array.isArray(res.data?.items) ? res.data.items : []);
          const total = trimmed ? items.length : (typeof res.data?.total === "number" ? res.data.total : items.length);
          setCatalog((c) => ({ ...c, items, total, page: 0, mode: trimmed ? "search" : "list", loading: false, error: false }));
        } else {
          setCatalog((c) => ({ ...c, loading: false, error: true }));
        }
      })
      .catch(() => { if (seq === catalogSeq.current) setCatalog((c) => ({ ...c, loading: false, error: true })); });
  };

  // Catalogue-wide active/archived tallies for the header, independent of the
  // current filter/search. Two tiny (size=1) count calls; refreshed on open and
  // after any archive / restore / create so the header always shows both.
  const refreshCatalogCounts = () => {
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    const headers = { Authorization: authToken };
    const totalOf = (res) => (res && res.status >= 200 && res.status < 300 && typeof res.data?.total === "number") ? res.data.total : null;
    Promise.all([
      axios.get(API.listProductSkus(merchantId, 0, 1, false), { headers, validateStatus: () => true }),
      axios.get(API.listProductSkus(merchantId, 0, 1, true), { headers, validateStatus: () => true }),
    ]).then(([activeRes, allRes]) => {
      const active = totalOf(activeRes);
      const all = totalOf(allRes);
      setCatalog((c) => ({
        ...c,
        activeTotal: active != null ? active : c.activeTotal,
        archivedTotal: (all != null && active != null) ? Math.max(0, all - active) : c.archivedTotal,
      }));
    }).catch(() => {});
  };

  const openCatalog = (index) => {
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    setCatalogSelected(new Set());
    setCatalog({ open: true, index, q: "", items: [], total: 0, activeTotal: 0, archivedTotal: 0, page: 0, mode: "list", view: "browse", loading: true, loadingMore: false, error: false, showArchived: false });
    runCatalogQuery("", false);
    refreshCatalogCounts();
  };

  const closeCatalog = () => {
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    catalogSeq.current += 1;
    setCatalogSelected(new Set());
    setNewSkuDialog(null);
    setCatalogArchive(null);
    setCatalog((c) => ({ ...c, open: false, view: "browse" }));
  };

  const onCatalogSearchChange = (q) => {
    setCatalog((c) => ({ ...c, q }));
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    catalogDebounce.current = setTimeout(() => runCatalogQuery(q, catalog.showArchived), 300);
  };

  // "Show archived" flips the server-side filter and re-runs the current query.
  const toggleShowArchived = () => {
    const next = !catalog.showArchived;
    setCatalog((c) => ({ ...c, showArchived: next }));
    if (catalogDebounce.current) clearTimeout(catalogDebounce.current);
    runCatalogQuery(catalog.q, next);
  };

  // Escape closes the catalogue layer by layer, wherever focus happens to be
  // (the modal's own onKeyDown only fires while focus is inside it).
  useEffect(() => {
    if (!catalog.open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (catalogArchive) { if (!archivingSku) setCatalogArchive(null); return; }
      if (catalog.view === "create") { cancelCatalogCreate(); return; }
      closeCatalog();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [catalog.open, catalog.view, catalogArchive, archivingSku]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreCatalog = () => {
    if (String(catalog.q || "").trim() || catalog.loadingMore) return; // no paging in search mode
    if (catalog.items.length >= catalog.total) return;
    const next = catalog.page + 1;
    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    setCatalog((c) => ({ ...c, loadingMore: true }));
    axios.get(API.listProductSkus(merchantId, next, CATALOG_PAGE_SIZE, catalog.showArchived), { headers: { Authorization: authToken }, validateStatus: () => true })
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          const more = Array.isArray(res.data?.items) ? res.data.items : [];
          const moreTotal = typeof res.data?.total === "number" ? res.data.total : null;
          setCatalog((c) => ({ ...c, items: [...c.items, ...more], page: next, total: moreTotal ?? c.total, loadingMore: false }));
        } else {
          setCatalog((c) => ({ ...c, loadingMore: false }));
        }
      })
      .catch(() => setCatalog((c) => ({ ...c, loadingMore: false })));
  };

  // "New SKU" inside the catalogue switches this modal to its create view
  // (in place) rather than opening a separate dialog.
  const addSkuFromCatalog = () => {
    const idx = catalog.index;
    const q = String(catalog.q || "").trim();
    setNewSkuDialog({ index: idx, skuId: q ? suggestSkuFromName(q) : "", name: q, price: "", hsn: "", matches: [] });
    setCatalog((c) => ({ ...c, view: "create" }));
  };

  const cancelCatalogCreate = () => {
    setNewSkuDialog(null);
    setCatalog((c) => ({ ...c, view: "browse" }));
  };

  // Edit an existing SKU from the catalogue: same form, SKU id locked (the id is
  // the upsert key — changing it would create a second SKU, not rename this one).
  const editSkuFromCatalog = (row) => {
    setNewSkuDialog({
      index: catalog.index,
      editing: true,
      skuId: row.product_sku_id || "",
      name: row.product_name || "",
      price: row.item_pricing_value?.unit_price != null ? String(row.item_pricing_value.unit_price) : "",
      hsn: row.product_hsn_code || "",
      matches: [],
    });
    setCatalog((c) => ({ ...c, view: "create" }));
  };

  // Archive = soft delete. The row stays in the catalogue (preserved for product
  // analytics + order history) but drops out of the active picker. Reversible via
  // restore. With "show archived" off it leaves the list; with it on it stays,
  // re-styled as archived.
  const STATUS_ARCHIVED = "ARCHIVED";
  const confirmArchiveSku = async () => {
    if (!catalogArchive || archivingSku) return;
    const sku = catalogArchive.product_sku_id;
    setArchivingSku(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      await axios.post(API.archiveProductSku(merchantId, sku), null, { headers: { Authorization: authToken } });
      setCatalog((c) => {
        if (c.showArchived) {
          return { ...c, items: (c.items || []).map((x) => x.product_sku_id === sku ? { ...x, status: STATUS_ARCHIVED } : x) };
        }
        const rest = (c.items || []).filter((x) => x.product_sku_id !== sku);
        return { ...c, items: rest, total: c.mode === "list" ? Math.max(0, (c.total || 0) - 1) : c.total };
      });
      setCatalogSelected((prev) => { const next = new Set(prev); next.delete(sku); return next; });
      setCatalogArchive(null);
      refreshCatalogCounts();
      toast.success("SKU archived. Restore it anytime from “Show archived”.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't archive the SKU. Please try again.");
    } finally {
      setArchivingSku(false);
    }
  };

  // Restore is the inverse of archive and low-risk (reversible), so it acts
  // immediately without a confirmation step.
  const restoreSku = async (row) => {
    const sku = row.product_sku_id;
    if (!sku || restoringSku) return;
    setRestoringSku(sku);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      await axios.post(API.unarchiveProductSku(merchantId, sku), null, { headers: { Authorization: authToken } });
      setCatalog((c) => ({ ...c, items: (c.items || []).map((x) => x.product_sku_id === sku ? { ...x, status: "ACTIVE" } : x) }));
      refreshCatalogCounts();
      toast.success("SKU restored to your catalogue.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't restore the SKU. Please try again.");
    } finally {
      setRestoringSku(null);
    }
  };

  // The Add-SKU form body, shared by the standalone dialog and the catalogue's
  // in-place create view so both stay identical.
  const renderSkuFormFields = () => (
    <>
      {!newSkuDialog?.editing && newSkuDialog?.matches && newSkuDialog.matches.length > 0 && (
        <div className="rounded-xl border border-amber-200/70 bg-amber-50 px-3 py-2.5">
          <p className="text-[11.5px] font-semibold text-amber-800">This product may already be in your catalog:</p>
          <div className="mt-1.5 space-y-1">
            {newSkuDialog.matches.map((match) => (
              <div key={match.product_sku_id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-[12px] text-amber-900" title={match.product_name || match.product_sku_id}>
                  {match.product_name || "—"} <span className="font-mono text-[10.5px] text-amber-700">{match.product_sku_id}</span>
                </span>
                <button type="button" onClick={() => { const idx = newSkuDialog.index; setNewSkuDialog(null); closeCatalog(); chooseSkuResult(idx, match); }} className="flex-shrink-0 cursor-pointer rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100">
                  Use this SKU
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className={LABEL}>SKU</label>
        <div className="relative">
          <input
            type="text"
            value={newSkuDialog?.skuId || ""}
            onChange={(e) => setNewSkuDialog((d) => ({ ...d, skuId: e.target.value }))}
            disabled={Boolean(newSkuDialog?.editing)}
            className={inputClass(false, "h-10 pr-11 font-mono text-[12.5px] disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500")}
            placeholder="SKU code"
          />
          {!newSkuDialog?.editing && (
            <button
              type="button"
              onClick={() => setNewSkuDialog((d) => ({ ...d, skuId: generateRandomSku() }))}
              title="Generate a random SKU"
              aria-label="Generate a random SKU"
              className="absolute right-1 top-1 grid h-8 w-8 cursor-pointer place-items-center rounded-md text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
            >
              <SparklesIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {newSkuDialog?.editing && (
          <p className="mt-1.5 text-[11.5px] text-slate-400">The SKU code can't be changed — it identifies this product. Add a new SKU for a different code.</p>
        )}
      </div>
      <div>
        <label className={LABEL}>Product name</label>
        <input type="text" value={newSkuDialog?.name || ""} onChange={(e) => setNewSkuDialog((d) => ({ ...d, name: e.target.value }))} className={inputClass(false, "h-10")} placeholder="e.g. Cotton kurta set" />
      </div>
      <div className="grid grid-cols-2 gap-3.5">
        <div>
          <label className={LABEL}>Unit price <span className="font-medium text-slate-400">· optional · incl. GST</span></label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-slate-400">₹</span>
            <input type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()} min="0" step="0.01" value={newSkuDialog?.price || ""} onChange={(e) => setNewSkuDialog((d) => ({ ...d, price: e.target.value }))} className={inputClass(false, "h-10 pl-7 text-right tabular-nums")} placeholder="e.g. 100" />
          </div>
        </div>
        <div>
          <label className={LABEL}>HSN <span className="font-medium text-slate-400">· optional</span></label>
          <input type="text" value={newSkuDialog?.hsn || ""} onChange={(e) => setNewSkuDialog((d) => ({ ...d, hsn: e.target.value }))} className={inputClass(false, "h-10 font-mono text-[12.5px]")} placeholder="e.g. 610910" />
        </div>
      </div>
    </>
  );

  const toggleCatalogSelect = (sku) => {
    const id = String(sku || "").trim();
    if (!id) return;
    setCatalogSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Add each checked SKU as its own line. A SKU already in the order is skipped
  // (no duplicates) and reported. Package weight/dimensions seed from the first
  // newly-added SKU that carries them; the merchant can adjust them in the package step.
  const addSelectedFromCatalog = () => {
    const chosen = catalog.items.filter((it) => catalogSelected.has(it.product_sku_id));
    if (!chosen.length) return;
    const idx = catalog.index;
    const existing = new Set((formData.orderDetails.itemDetails || []).map((it) => String(it.skuId || "").trim()).filter(Boolean));
    const toAdd = chosen.filter((r) => !existing.has(String(r.product_sku_id || "").trim()));
    const skipped = chosen.length - toAdd.length;
    closeCatalog();
    if (toAdd.length) {
      setFormData((prev) => {
        const items = [...prev.orderDetails.itemDetails];
        const pkg = { ...prev.packageDetails };
        const present = new Set(items.map((it) => String(it.skuId || "").trim()).filter(Boolean));
        let seededDims = false;
        let filledEmpty = false;
        for (const r of toAdd) {
          const sku = String(r.product_sku_id || "").trim();
          if (!sku || present.has(sku)) continue;
          present.add(sku);
          if (!seededDims) {
            const w = r.product_product_sku_master_item_weight ?? r.product_weight ?? null;
            if (w?.product_weight != null) pkg.weight = w.product_weight;
            if (r.product_dimensions?.product_length != null) pkg.length = r.product_dimensions.product_length;
            if (r.product_dimensions?.product_width != null) pkg.breadth = r.product_dimensions.product_width;
            if (r.product_dimensions?.product_height != null) pkg.height = r.product_dimensions.product_height;
            seededDims = true;
          }
          const line = {
            name: r.product_name || "",
            skuId: sku,
            quantity: "1",
            unitPrice: r.item_pricing_value?.unit_price != null ? String(r.item_pricing_value.unit_price) : "",
            hsnCode: r.product_hsn_code || "",
            isFragile: false,
          };
          if (!filledEmpty && idx != null && items[idx] && !String(items[idx].skuId || "").trim() && !String(items[idx].name || "").trim()) {
            items[idx] = { ...items[idx], ...line };
            filledEmpty = true;
          } else {
            items.push(line);
          }
        }
        return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items }, packageDetails: pkg };
      });
      toast.success(`${toAdd.length} item${toAdd.length === 1 ? "" : "s"} added.`);
    }
    if (skipped) toast.error(`${skipped} already in your order — ${skipped === 1 ? "it was" : "they were"} skipped.`);
  };

  const openBulkSkuPage = () => {
    window.open("/tools?tab=skuMaster", "_blank", "noopener,noreferrer");
  };

  const closeSkuSearch = () => {
    if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);
    skuSearchSeq.current += 1;
    setSkuSearch((prev) => (prev.open || prev.index !== null ? { index: null, q: "", results: [], open: false, loading: false, hi: -1, byName: false } : prev));
  };

  const queueSkuSearch = (index, raw, byName = false) => {
    const q = String(raw || "").trim();
    if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);
    if (q.length < 2) { skuSearchSeq.current += 1; setSkuSearch({ index, q, results: [], open: false, loading: false, hi: -1, byName }); return; }
    skuSearchTimer.current = setTimeout(async () => {
      const seq = ++skuSearchSeq.current;
      setSkuSearch({ index, q, results: [], open: true, loading: true, hi: -1, byName });
      try {
        const authToken = localStorage.getItem("authToken");
        const merchantId = localStorage.getItem("merchantId");
        const response = await axios.get(API.searchProductSkus(merchantId, q), {
          headers: { Authorization: authToken },
          validateStatus: () => true,
        });
        if (seq !== skuSearchSeq.current) return;
        const list = Array.isArray(response.data) ? response.data : [];
        setSkuSearch({ index, q, results: list.slice(0, 6), open: true, loading: false, hi: list.length ? 0 : -1, byName });
      } catch (err) {
        if (seq === skuSearchSeq.current) setSkuSearch({ index, q, results: [], open: true, loading: false, hi: -1, byName });
      }
    }, 300);
  };

  // A SKU uniquely identifies one product variant and may not appear on two lines.
  // Returns true if another line (or, with exceptIndex omitted, any line) already
  // carries this non-empty SKU. Empty SKUs are never treated as duplicates.
  const DUPLICATE_SKU_MSG = "This item is already in your order — duplicates aren't allowed.";
  const orderHasSku = (rawSku, exceptIndex = -1) => {
    const sku = String(rawSku || "").trim();
    if (!sku) return false;
    const items = formData.orderDetails.itemDetails || [];
    return items.some((it, j) => j !== exceptIndex && String(it.skuId || "").trim() === sku);
  };

  const chooseSkuResult = (index, result) => {
    const sku = result.product_sku_id || "";
    closeSkuSearch();
    if (sku && orderHasSku(sku, index)) { toast.error(DUPLICATE_SKU_MSG); return; }
    handleItemChange(index, "skuId", sku);
    if (sku) fetchSKUDetails(index, sku);
  };

  const openNewSkuDialog = (index) => {
    const q = skuSearch.q;
    const byName = skuSearch.byName;
    closeSkuSearch();
    const item = formData.orderDetails.itemDetails[index] || {};
    const name = byName ? q : (item.name || "");
    setNewSkuDialog({ index, skuId: byName ? suggestSkuFromName(q) : q, name, price: item.unitPrice || "", hsn: item.hsnCode || "", matches: [] });
    const dedupeQuery = String(name || item.name || "").trim();
    if (dedupeQuery.length >= 2) {
      (async () => {
        try {
          const authToken = localStorage.getItem("authToken");
          const merchantId = localStorage.getItem("merchantId");
          const response = await axios.get(API.searchProductSkus(merchantId, dedupeQuery, 4), {
            headers: { Authorization: authToken },
            validateStatus: () => true,
          });
          const list = Array.isArray(response.data) ? response.data.slice(0, 3) : [];
          if (list.length) setNewSkuDialog((d) => (d ? { ...d, matches: list } : d));
        } catch (err) { /* dedupe hints are best-effort */ }
      })();
    }
  };

  const saveNewSku = async () => {
    if (!newSkuDialog) return;
    const { index, skuId, name, price, hsn, editing } = newSkuDialog;
    if (!String(skuId || "").trim() || !String(name || "").trim()) {
      toast.error("SKU and product name are required.");
      return;
    }
    setSavingNewSku(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      const body = {
        product_sku_id: String(skuId).trim(),
        product_name: String(name).trim(),
        product_hsn_code: String(hsn || "").trim() || null,
        unit_price: price !== "" && Number.isFinite(Number(price)) ? Number(price) : null,
        currency: "INR",
      };
      const response = await axios.post(API.createProductSku(merchantId), body, {
        headers: { Authorization: authToken, "Content-Type": "application/json" },
      });
      const saved = response.data || {};
      const savedSku = saved.product_sku_id || body.product_sku_id;
      setNewSkuDialog(null);
      if (editing) {
        // Edit saves back to the catalogue only: refresh the row in place and
        // return to browse. Lines already in the order keep their snapshot.
        setCatalog((c) => ({
          ...c,
          view: "browse",
          items: (c.items || []).map((x) => x.product_sku_id === savedSku
            ? { ...x, product_name: body.product_name, product_hsn_code: body.product_hsn_code, item_pricing_value: { ...(x.item_pricing_value || {}), unit_price: body.unit_price, unit_currency: "INR" } }
            : x),
        }));
        toast.success("SKU updated.");
      } else if (catalog.open) {
        // Created from inside the catalogue: drop it into the browse list, pre-select
        // it, and return to browse so it can be added alongside other picks.
        const newRow = saved.product_sku_id ? saved : {
          product_sku_id: savedSku, product_name: body.product_name, product_hsn_code: body.product_hsn_code,
          item_pricing_value: { unit_price: body.unit_price, unit_currency: "INR" },
        };
        setCatalog((c) => {
          const rest = (c.items || []).filter((x) => x.product_sku_id !== savedSku);
          return { ...c, view: "browse", items: [newRow, ...rest], total: c.mode === "list" ? rest.length + 1 : c.total };
        });
        setCatalogSelected((prev) => new Set(prev).add(savedSku));
        refreshCatalogCounts();
        toast.success("SKU added to your catalogue.");
      } else if (orderHasSku(savedSku, index)) {
        toast.success("SKU added to your catalog.");
        toast.error(DUPLICATE_SKU_MSG);
      } else {
        setFormData((prev) => {
          const items = [...prev.orderDetails.itemDetails];
          items[index] = { ...items[index], skuId: savedSku, name: body.product_name, unitPrice: body.unit_price != null ? String(body.unit_price) : items[index].unitPrice, hsnCode: body.product_hsn_code || items[index].hsnCode };
          return { ...prev, orderDetails: { ...prev.orderDetails, itemDetails: items } };
        });
        toast.success("SKU added to your catalog.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || (editing ? "Couldn't save the changes. Please try again." : "Couldn't add the SKU. Please try again."));
    } finally {
      setSavingNewSku(false);
    }
  };

  const fetchSKUDetails = async (index, sku) => {
    if (!sku) return;
    try {
      const authToken = localStorage.getItem('authToken');
      const merchantId = localStorage.getItem('merchantId');
      const skuUrl = API.getProductSku(merchantId, sku);

      const response = await axios.get(skuUrl, {
        headers: { Authorization: authToken },
        validateStatus: () => true,
      });

      const data = typeof response.data === 'object' && response.data !== null ? response.data : null;
      if (!data || Object.keys(data).length === 0) {
        toast.error('Empty product data received for SKU.');
        return;
      }

      const weightObj = data.product_product_sku_master_item_weight ?? data.product_weight ?? data.product_product_weight ?? null;

      // Safely extract fields with fallbacks to current values
      setFormData((prev) => {
        const items = [...prev.orderDetails.itemDetails];
        const currentItem = items[index] || {};

        const productName = (data.product_name ?? currentItem.name) || '';
        const unitPrice = (data.item_pricing_value?.unit_price ?? currentItem.unitPrice) || '';
        const quantity = "1"; // original logic sets to 1 on fetch
        const existingSkuId = currentItem.skuId || '';

        // Dimensions & weight (only update if present)
        const newPackageDetails = { ...prev.packageDetails };
        if (weightObj?.product_weight != null) {
          newPackageDetails.weight = weightObj.product_weight;
        }
        if (data.product_dimensions?.product_length != null) {
          newPackageDetails.length = data.product_dimensions.product_length;
        }
        if (data.product_dimensions?.product_width != null) {
          newPackageDetails.breadth = data.product_dimensions.product_width;
        }
        if (data.product_dimensions?.product_height != null) {
          newPackageDetails.height = data.product_dimensions.product_height;
        }

        const newUnits = { ...units };
        if (data.product_weight?.unit) newUnits.weight = data.product_weight.unit;
        if (data.product_dimensions?.unit) {
          newUnits.length = data.product_dimensions.unit;
          newUnits.breadth = data.product_dimensions.unit;
          newUnits.height = data.product_dimensions.unit;
        }

        items[index] = {
          ...currentItem,
          name: productName,
          unitPrice: unitPrice,
          quantity: quantity,
          skuId: existingSkuId,
        };

        return {
          ...prev,
          orderDetails: { ...prev.orderDetails, itemDetails: items },
          packageDetails: newPackageDetails,
          units: newUnits,
        };
      });
    } catch (err) {
      toast.error('Unable to fetch product details');
    }
  };

  // Conversion functions for payload only
  const convertLength = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'inch' ? num * 2.54 : num;
  };
  const convertWeight = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === 'kg' ? num * 1000 : num;
  };

  const handlePackageValueChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      packageDetails: { ...prev.packageDetails, [field]: value }
    }));
  };
  const handlePackageUnitChange = (field, unit) => {
    setUnits(prev => ({ ...prev, [field]: unit }));
  };

  const handlePincodeChange = async (e) => {
    const pin = e.target.value.trim();
    setFormData(prev => ({
      ...prev,
      customerDetails: { ...prev.customerDetails, pincode: pin, city: "", state: "" }
    }));
    setPincodeLookupFailed(false);

    if (!/^\d{6}$/.test(pin)) return;

    setLoadingPincode(true);
    const db = await loadPincodeDb();
    const [entry] = db ? db.search(pin) : [];
    if (entry) {
      setFormData(prev => ({
        ...prev,
        customerDetails: {
          ...prev.customerDetails,
          city: entry.city,
          state: entry.state
        }
      }));
      setLoadingPincode(false);
      return;
    }
    try {
      const response = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
      const data = response.data?.[0];
      if (data?.Status === 'Success' && data.PostOffice?.length) {
        const postOffice = data.PostOffice[0];
        setFormData(prev => ({
          ...prev,
          customerDetails: {
            ...prev.customerDetails,
            city: postOffice.District,
            state: postOffice.State
          }
        }));
      } else {
        throw new Error('No Postal data');
      }
    } catch (err) {
      setPincodeLookupFailed(true);
    } finally {
      setLoadingPincode(false);
    }
  };

  useEffect(() => {
    if (step === 1) {
      fetchAndStorePickupDetails();
    }
  }, [step]);

  // Keep the active step pill in view when the row scrolls horizontally (mobile),
  // so you always see where you are. No-op on desktop where every pill fits.
  useEffect(() => {
    const row = stepRowRef.current;
    if (!row) return;
    const active = row.querySelector(`[data-step="${step}"]`);
    if (!active) return;
    const rowRect = row.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    const delta = (aRect.left - rowRect.left) - (row.clientWidth - active.clientWidth) / 2;
    if (Math.abs(delta) > 4) row.scrollTo({ left: Math.max(0, row.scrollLeft + delta), behavior: "smooth" });
  }, [step]);

  // "More below" cue: on every step, show a hint while form content still extends past
  // the visible area (i.e. behind/under the sticky footer). Recomputes on scroll, resize
  // and content growth (courier rates loading, item rows added, groups expanding).
  useEffect(() => {
    const compute = () => {
      const body = stepBodyRef.current;
      if (!body) { setMoreBelow(false); return; }
      const footerH = footerRef.current ? footerRef.current.offsetHeight : 64;
      const foldY = window.innerHeight - footerH + 24;
      // (1) the step body itself runs past the fold, behind the sticky footer…
      let more = body.getBoundingClientRect().bottom > foldY;
      // (2) …or a scroller inside it (the saved-locations list caps at 52vh) still hides rows
      //     below its own edge — the page fits, but the merchant can't see everything.
      if (!more) {
        for (const el of body.querySelectorAll(".overflow-y-auto, .overflow-auto")) {
          if (el.scrollHeight - el.scrollTop - el.clientHeight > 4 && el.getBoundingClientRect().top < foldY) { more = true; break; }
        }
      }
      setMoreBelow(more);
    };
    moreBelowComputeRef.current = compute;
    compute();
    window.addEventListener("scroll", compute, true); // capture → catches window or inner scrollers
    window.addEventListener("resize", compute);
    let ro;
    if (stepBodyRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(compute);
      ro.observe(stepBodyRef.current);
    }
    const t = setTimeout(compute, 360); // after the step-enter animation settles
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, [step]);

  const scrollForMore = () => {
    const body = stepBodyRef.current;
    const footerH = footerRef.current ? footerRef.current.offsetHeight : 64;
    const foldY = window.innerHeight - footerH + 24;
    // Same priority as the cue: when the page already fits, the hidden content is inside a
    // scroller within the step (the saved-locations list) — scroll that, not the window.
    if (body && body.getBoundingClientRect().bottom <= foldY) {
      const inner = [...body.querySelectorAll(".overflow-y-auto, .overflow-auto")]
        .find((el) => el.scrollHeight - el.scrollTop - el.clientHeight > 4);
      if (inner) { inner.scrollBy({ top: Math.round(inner.clientHeight * 0.72), behavior: "smooth" }); return; }
    }
    let n = body ? body.parentElement : null, scroller = null;
    while (n) {
      const oy = getComputedStyle(n).overflowY;
      if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight + 4) { scroller = n; break; }
      n = n.parentElement;
    }
    const by = Math.round(window.innerHeight * 0.72);
    (scroller || window).scrollBy({ top: by, behavior: "smooth" });
  };

  // A focused <input type="number"> silently changes its value on a mouse-wheel scroll —
  // a real data-integrity hazard on price / quantity / weight / dimension fields. Blur any
  // focused number field the instant a wheel scroll starts, so the value can never change
  // by accident; the page keeps scrolling as normal. Covers every number field, every stage.
  useEffect(() => {
    const onWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number") el.blur();
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => { loadPincodeDb(); }, []);

  // Resolve how this merchant's shipment charge settles (SQL wallet type OR Mongo onboarding preference),
  // for the preview's settlement note. Fails open: on any error we keep null and fall back to the redux
  // wallet type below, so the note works before and after the OMS endpoint is deployed.
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return undefined;
    const merchantId = localStorage.getItem("merchantId");
    let cancelled = false;
    axios
      .get(API.merchantSettlementType(), { headers: { Authorization: authToken, "x-merchant-id": merchantId }, validateStatus: () => true })
      .then((res) => {
        if (!cancelled && res && res.status >= 200 && res.status < 300) {
          setResolvedSettlement(res.data?.settlement_type ?? null);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // After a failed Continue, put the cursor in the first field that needs attention.
  useEffect(() => {
    if (!Object.keys(errors).length) return;
    document.querySelector('[aria-invalid="true"]')?.focus();
  }, [errors]);

  // On a step change after first paint: bring the card back into view if the merchant had
  // scrolled, and put the cursor in the step's first field. Nothing moves on initial load.
  const flowRef = useRef(null);
  const settledRef = useRef(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const el = flowRef.current;
      if (settledRef.current && el) {
        const top = el.getBoundingClientRect().top;
        if (top < 0 || top > 200) el.scrollIntoView({ block: "start", behavior: "smooth" });
      }
      const first = el?.querySelector('input:not([type="radio"]):not([type="search"]):not([disabled]), textarea')
        || el?.querySelector('input[type="radio"]:checked, [role="switch"]');
      first?.focus({ preventScroll: true });
    });
    settledRef.current = true;
    return () => cancelAnimationFrame(id);
  }, [step]);

  const fetchAndStorePickupDetails = async ({ skipAutoSelect = false } = {}) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      return [];
    }

    const parentClientId = localStorage.getItem("parentClientId");
    const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}/v1/pocas/onboarding/${parentClientId}/getpickupdetails`;

    try {
      const response = await axios.get(apiUrl, { headers: { Authorization: authToken } });

      if (response.data && response.data.length > 0) {
        localStorage.setItem("pickupLocations", JSON.stringify(response.data));
        setPickupDetails(response.data);
        if (!skipAutoSelect) setSelectedPickup(response.data[0]);
        return response.data;
      } else {
        setPickupDetails([]);
        return [];
      }
    } catch (error) {
      setPickupDetails([]);
      return [];
    }
  };

  useEffect(() => {
    if (step === 2) {
      const tryMatchClonePickup = (addresses) => {
        // If we have cloneFrom pickup data, try to match against saved addresses
        if (!cloneFrom) return false;
        const clonedPostal = (cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "").trim();
        const clonedPhone = (cloneFrom.pickup_poc_contact_number || "").trim();
        const clonedAddress = (cloneFrom.pickup_address || "").trim().toLowerCase();

        if (!clonedPostal && !clonedPhone && !clonedAddress) return false;

        // Try to find a matching saved address (by pincode + phone, or pincode + address substring)
        const match = addresses.find((addr) => {
          const addrPostal = (addr.pickup_postal_code || "").trim();
          const addrPhone = (addr.pickup_poc_contact_number || "").trim();
          const addrAddress = (addr.pickup_address || "").trim().toLowerCase();

          if (clonedPostal && addrPostal === clonedPostal) {
            if (clonedPhone && addrPhone === clonedPhone) return true;
            if (clonedAddress && addrAddress.includes(clonedAddress.slice(0, 30))) return true;
          }
          return false;
        });

        if (match) {
          setSelectedPickup(match);
          return true;
        }

        // No match found - auto-open "Add New Address" modal pre-filled with cloned pickup data
        const mappedPickup = {
          pickup_address: cloneFrom.pickup_address || "",
          pickup_city: cloneFrom.pickup_city || "",
          pickup_postal_code: cloneFrom.pickup_pincode || cloneFrom.pickup_postal_code || "",
          pickup_poc: cloneFrom.pickup_name || cloneFrom.pickup_poc || "",
          pickup_organisation: cloneFrom.pickup_organisation || "",
          pickup_poc_contact_number: cloneFrom.pickup_poc_contact_number || "",
          pickup_poc_email: cloneFrom.pickup_poc_email || "",
          pickup_state: cloneFrom.pickup_state || "",
          pickup_district: cloneFrom.pickup_district || "",
          pickup_address_type: cloneFrom.pickup_address_type || "BUSINESS",
          pickup_country_code: cloneFrom.pickup_country_code || "IN",
          pickup_landmark: cloneFrom.pickup_landmark || "",
          gst_number: cloneFrom.pickup_gst_number || cloneFrom.gst_number || "",
        };
        // Only open modal if there's meaningful data to pre-fill
        const hasData = mappedPickup.pickup_postal_code || mappedPickup.pickup_address || mappedPickup.pickup_poc;
        if (hasData) {
          setAddrModalMode("add");
          setAddrModalLocation(mappedPickup);
          setAddrModalOpen(true);
        }
        return false;
      };

      const stored = JSON.parse(localStorage.getItem("pickupLocations") || "[]");
      if (Array.isArray(stored) && stored.length > 0) {
        setPickupDetails(stored);
        if (!tryMatchClonePickup(stored)) {
          setSelectedPickup(stored[0]);
        }
      } else {
        // Fetch with skipAutoSelect so we can try matching first
        fetchAndStorePickupDetails({ skipAutoSelect: true }).then((fetched) => {
          if (fetched && fetched.length > 0) {
            if (!tryMatchClonePickup(fetched)) {
              setSelectedPickup(fetched[0]);
            }
          } else if (cloneFrom) {
            // No saved addresses at all - open add modal with cloneFrom data
            tryMatchClonePickup([]);
          }
        });
      }
    }
  }, [step]);

  /* ── Address management handlers ── */
  const openAddAddress = () => { setAddrModalMode("add"); setAddrModalLocation(null); setAddrModalOpen(true); };
  const openEditAddress = (loc) => { setAddrModalMode("edit"); setAddrModalLocation(loc); setAddrModalOpen(true); };
  const handleAddressSaved = () => {
    fetchAndStorePickupDetails().then((fetched) => {
      // Auto-select the most recently added address (last in list)
      if (fetched && fetched.length > 0) {
        setSelectedPickup(fetched[fetched.length - 1]);
      }
    });
  };
  const openDeleteAddress = (id) => { setPickupToDelete(id); setDeleteConfirmOpen(true); };
  const confirmDeleteAddress = async () => {
    setDeletingPickup(true);
    await deletePickupAddress(pickupToDelete, () => {
      fetchAndStorePickupDetails();
      if (selectedPickup?.pickup_address_id === pickupToDelete) setSelectedPickup(null);
    });
    setDeletingPickup(false);
    setDeleteConfirmOpen(false);
    setPickupToDelete(null);
  };

  const steps = [
    { key: "customerDetails", label: "Recipient" },
    { key: "pickupDetails", label: "Pickup" },
    { key: "orderDetails", label: "Payment & items" },
    { key: "packageDetails", label: "Package" },
    { key: "courierDetails", label: "Courier" },
    { key: "summary", label: "Booked" },
  ];

  // The rules for one section, as an errors object. validateStep (the stepper's Continue) and
  // validateAll (Book in the stacked layout) both run exactly these.
  const validateSection = (currentSection) => {
    const newErrors = {};
    const addErr = (k, msg) => { newErrors[k] = msg; };

    if (currentSection === "pickupDetails" && !selectedPickup) {
      addErr("pickup", "Choose a pickup location to continue");
    }

    if (currentSection === "customerDetails") {
      const cd = formData.customerDetails || {};
      if (!String(cd.name || "").trim()) addErr("name", "Name is required");
      if (!/^[0-9]{10}$/.test(String(cd.phoneNumber || ""))) addErr("phoneNumber", "Phone Number must be 10 digits");
      const addr = String(cd.address || "").trim();
      if (!addr) addErr("address", "Address is required");
      else if (addr.length < 10) addErr("address", "Address looks too short");
      if (!String(cd.city || "").trim()) addErr("city", "City is required");
      if (!String(cd.state || "").trim()) addErr("state", "State is required");
      if (!/^[0-9]{6}$/.test(String(cd.pincode || ""))) addErr("pincode", "Pincode must be 6 digits");
      const email = String(cd.email || "").trim();
      if (email && !EMAIL_RE.test(email)) addErr("email", "Enter a valid email address");
    }

    if (currentSection === "orderDetails") {
      const od = formData.orderDetails || {};

      if (!cloneMode && !String(od.clientOrderId || "").trim()) {
        addErr("clientOrderId", "Client OrderID is required");
      } else if (!cloneMode && String(od.clientOrderId || "").trim() !== "" && orderRefCheck.ref === String(od.clientOrderId || "").trim() && orderRefCheck.outcome !== "NONE") {
        addErr("clientOrderId", "An order already exists for this reference");
      }

      // Invoice Value required and > 0
      const invoiceNum = parseFloat(od.invoiceValue || 0);
      if (!(Number.isFinite(invoiceNum) && invoiceNum > 0)) {
        addErr("invoiceValue", "Invoice Value must be greater than 0");
      }

      if (String(formData.paymentMode || "").toUpperCase() === "COD") {
        const codAmt = parseFloat(od.codAmount ?? "");
        if (!(Number.isFinite(codAmt) && codAmt >= 0)) {
          addErr("codAmount", "CodAmount is required for COD orders");
        }
      }

      // Item-level validations
      (od.itemDetails || []).forEach((item, index) => {
        if (!String(item.name || "").trim()) {
          addErr(`name-${index}`, "Name is required");
        }

        if (!cloneMode) {
          if (!String(item.skuId || "").trim()) {
            addErr(`skuId-${index}`, "SKU ID is required");
          }
        }

        if (!String(item.quantity ?? "").trim() || Number(item.quantity) <= 0 || !Number.isInteger(Number(item.quantity))) {
          addErr(`quantity-${index}`, "Quantity is required and must be > 0");
        }
        if (!String(item.unitPrice ?? "").trim() || Number(item.unitPrice) < 0) {
          addErr(`unitPrice-${index}`, "Unit Price is required");
        }
      });
    }

    if (currentSection === "packageDetails") {
      const pd = formData.packageDetails || {};

      // Weight validation
      const weightVal = String(pd.weight ?? "").trim();
      if (!weightVal) {
        addErr("weight", "Weight is required");
      } else if (isNaN(Number(weightVal)) || Number(weightVal) <= 0) {
        addErr("weight", "Weight must be a valid number greater than 0");
      } else if ((units.weight === "kg" ? Number(weightVal) : Number(weightVal) / 1000) > 200) {
        addErr("weight", "Weight looks too high - check the unit");
      }

      // Dimension validations
      const lengthVal = String(pd.length ?? "").trim();
      if (!lengthVal) {
        addErr("length", "Length is required");
      } else if (isNaN(Number(lengthVal)) || Number(lengthVal) <= 0) {
        addErr("length", "Length must be a valid number greater than 0");
      } else if ((units.length === "inch" ? Number(lengthVal) * 2.54 : Number(lengthVal)) > 500) {
        addErr("length", "Length looks too high - check the unit");
      }

      const breadthVal = String(pd.breadth ?? "").trim();
      if (!breadthVal) {
        addErr("breadth", "Breadth is required");
      } else if (isNaN(Number(breadthVal)) || Number(breadthVal) <= 0) {
        addErr("breadth", "Breadth must be a valid number greater than 0");
      } else if ((units.breadth === "inch" ? Number(breadthVal) * 2.54 : Number(breadthVal)) > 500) {
        addErr("breadth", "Breadth looks too high - check the unit");
      }

      const heightVal = String(pd.height ?? "").trim();
      if (!heightVal) {
        addErr("height", "Height is required");
      } else if (isNaN(Number(heightVal)) || Number(heightVal) <= 0) {
        addErr("height", "Height must be a valid number greater than 0");
      } else if ((units.height === "inch" ? Number(heightVal) * 2.54 : Number(heightVal)) > 500) {
        addErr("height", "Height looks too high - check the unit");
      }
    }

    return newErrors;
  };
  const validateStep = () => {
    const newErrors = validateSection(steps[step - 1].key);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Stacked layout: earlier sections stay editable, so Book re-checks every revealed section
  // and points at the first one with a problem.
  const validateAll = () => {
    const all = {};
    let firstBad = null;
    for (let n = 1; n <= 5; n++) {
      const errs = validateSection(steps[n - 1].key);
      if (Object.keys(errs).length && firstBad == null) firstBad = n;
      Object.assign(all, errs);
    }
    setErrors(all);
    return { ok: Object.keys(all).length === 0, firstBad };
  };

  const handleNext = () => {
    if (validateStep()) {
      if (step === 5) {
        handleSubmit();
      } else {
        const nextStep = step === 4 ? 5 : step + 1;
        setStep(nextStep);
        setHighestStepReached(prev => Math.max(prev, nextStep)); // Track highest step reached
        if (step === 4) {
          lastCourierKeyRef.current = courierInputsKey; // these rates belong to these inputs
          prefetchCouriersForStep5();
        }
      }
    } else {
    }
  };

  /* "↵ Enter to continue" must be true everywhere, not only where the native
     form submit reaches. This covers the gaps: steps with no inputs (pickup,
     courier), the address textarea (Shift+Enter keeps the newline), and blank
     focus. Inputs on steps 1-4 keep the native implicit submission; buttons,
     links and open dialogs keep their own Enter. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.isComposing || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (loading || savingDraft) return;
      if (addrModalOpen || deleteConfirmOpen || showCustomerModal || draftSuccessModal.open) return;
      const tag = document.activeElement?.tagName;
      if (tag === "BUTTON" || tag === "A") return;
      if (stacked) return; // stacked: Book is a deliberate click, never Enter
      if (step <= 4 && (tag === "INPUT" || tag === "SELECT")) return;
      e.preventDefault();
      handleNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const handleBack = () => {
    setStep(prev => (prev > 1 ? prev - 1 : prev));
  };

  /* ── Stacked layout ── */
  const scrollToSection = (n) => {
    const el = document.querySelector(`[data-section="${n}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleBookStacked = () => {
    const { ok, firstBad } = validateAll();
    if (!ok) { if (firstBad) scrollToSection(firstBad); return; }
    handleSubmit();
  };
  // Switching layouts keeps everything typed. Stacked shows every section, so the whole form
  // counts as reached (the ticket fills from completed groups either way).
  const chooseLayout = (mode) => {
    if (mode === layoutMode) return;
    setLayoutMode(mode);
    writeLayoutPreference(mode);
    if (mode === "vertical") { setStep(5); setHighestStepReached(5); return; }
    // Back to Steps: the stepper's position must come from the data, not from the "5" that
    // stacked mode pins — otherwise every pill shows as reached/ticked with nothing filled.
    // Land on the first section that doesn't validate yet (or Courier if all do).
    const firstIncomplete = steps.slice(0, 5).findIndex((s) => Object.keys(validateSection(s.key)).length > 0);
    const landing = firstIncomplete === -1 ? 5 : firstIncomplete + 1;
    setStep(landing);
    setHighestStepReached(landing);
    setErrors({});
  };
  // Stacked shows every section, so the form always sits at the last step — except once booked
  // (step 6 is the confirmation, which must stay put).
  useEffect(() => {
    if (stacked && step < 5) { setStep(5); setHighestStepReached(5); }
  }, [stacked, step]);
  // Stacked has no Continue on the Package step, so the courier rates (same two calls the stepper
  // makes on that Continue) fetch on their own once recipient, pickup and package are valid —
  // and again when any of those inputs change.
  const courierInputsKey = JSON.stringify([
    formData.customerDetails.pincode, selectedPickup?.pickup_address_id, formData.paymentMode,
    formData.orderDetails.invoiceValue, formData.orderDetails.codAmount,
    formData.packageDetails, units,
  ]);
  const courierInputsValid = stacked
    && Object.keys(validateSection("customerDetails")).length === 0
    && Object.keys(validateSection("pickupDetails")).length === 0
    && Object.keys(validateSection("packageDetails")).length === 0;
  // Stacked gating: a section unlocks only once every section above it validates (live, as the
  // merchant types). Section 1 is always open. Recomputed each render — validateSection is pure.
  const sectionValid = stacked ? steps.slice(0, 5).map((s) => Object.keys(validateSection(s.key)).length === 0) : [];
  const unlockedUpTo = stacked ? (sectionValid.findIndex((ok) => !ok) === -1 ? 5 : sectionValid.findIndex((ok) => !ok) + 1) : 5;
  const lastCourierKeyRef = useRef("");
  // The fetcher is a fresh closure every render; the latest one is kept in prefetchRef (assigned
  // right after its definition below) so this effect's deps can be exactly the inputs.
  const prefetchRef = useRef(null);
  useEffect(() => {
    if (!stacked || !courierInputsValid || courierInputsKey === lastCourierKeyRef.current) return undefined;
    const t = window.setTimeout(() => { lastCourierKeyRef.current = courierInputsKey; prefetchRef.current(); }, 500);
    return () => window.clearTimeout(t);
  }, [stacked, courierInputsValid, courierInputsKey]);

  // Step navigation via band headers / dock dots
  const handleStepClick = (targetStep) => {
    // Only allow navigation to steps that have been reached before
    if (targetStep <= highestStepReached && targetStep !== step) {
      setStep(targetStep);
    }
  };

  const handleCourierSelection = async (useRecommendation) => {
    setFormData((prev) => ({
      ...prev,
      courierDetails: {
        ...prev.courierDetails,
        useRecommendation,
        courierPartner: useRecommendation ? "" : prev.courierDetails.courierPartner,
      },
    }));
  };

  const prefetchCouriersForStep5 = async () => {
    try {
      setLoadingCouriers(true);
      await Promise.all([
        fetchCourierPricing(),
        fetchServiceability(),
      ]);
    } finally {
      setLoadingCouriers(false);
    }
  };
  prefetchRef.current = prefetchCouriersForStep5;
  // Where a freshly hydrated draft/clone should land (Steps only): the first section that
  // doesn't validate yet. Render-fresh values are read through refs so this effect depends on
  // exactly the one thing that should fire it.
  const landingRef = useRef(null);
  landingRef.current = () => {
    if (stacked) return; // stacked shows everything; its gate derives from the data anyway
    const firstIncomplete = steps.slice(0, 5).findIndex((s) => Object.keys(validateSection(s.key)).length > 0);
    const landing = firstIncomplete === -1 ? 5 : firstIncomplete + 1;
    setStep(landing);
    setHighestStepReached(landing);
    if (landing === 5) { lastCourierKeyRef.current = courierInputsKey; prefetchCouriersForStep5(); }
  };
  useEffect(() => {
    if (!pendingDraftLanding) return;
    setPendingDraftLanding(false);
    landingRef.current();
  }, [pendingDraftLanding]);

  const handlePhoneNumberChange = async (e) => {
    const phoneNumber = e.target.value.trim()
    setFormData((prev) => ({
      ...prev,
      customerDetails: { ...prev.customerDetails, phoneNumber },
    }))

    if (/^\d{10}$/.test(phoneNumber)) {
      try {
        const authToken = localStorage.getItem('authToken')
        const merchantId = localStorage.getItem('merchantId')
        const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_ADDRESS_LOOKUP_FROM_MOBILE_NUMBER}`
        const response = await axios.request({
          method: 'POST',
          url: apiUrl,
          headers: {
            Authorization: authToken,
            'Content-Type': 'application/json',
          },
          data: {
            phone_number: phoneNumber,
            merchant_id: merchantId,
          },
        })

        if (Array.isArray(response.data) && response.data.length > 0) {
          setCustomerLookupResults(response.data)
          setShowCustomerModal(true)
        }
      } catch (err) {
      }
    }
  }

  const selectCustomer = (cust) => {
    setFormData((prev) => ({
      ...prev,
      customerDetails: {
        ...prev.customerDetails,
        name: cust.drop_poc || '',
        phoneNumber: cust.drop_poc_contact_number || prev.customerDetails.phoneNumber,
        email: cust.drop_poc_email || prev.customerDetails.email,
        address: cust.drop_address || '',
        city: cust.drop_city || '',
        state: cust.drop_state || '',
        pincode: cust.drop_postal_code || '',
      },
    }))
    setShowCustomerModal(false)
  }

  const fetchCourierPricing = async () => {
    setLoadingCouriers(true);
    setCourierOptions([]);
    try {
      const allocatedCarriersRaw = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        toast.error("Missing authentication.");
        setLoadingCouriers(false);
        return;
      }

      const forwardCarriers = allocatedCarriersRaw
        .map((c) => (typeof c === "string" ? { carrier_partner_id: c } : c))
        .filter(
          (c) =>
            c &&
            typeof c.carrier_partner_id === "string" &&
            !reverseMatcher.test(c.carrier_partner_id)
        );

      if (!forwardCarriers.length) {
        toast.info("No forward-enabled carriers allocated.");
        setLoadingCouriers(false);
        return;
      }

      const requestPayload = forwardCarriers.map((carrier) => ({
        correlation_id: uuidv4(),
        carrier_partner_id: carrier.carrier_partner_id,
        shipment_type: "FORWARD",
        payment_mode: formData.paymentMode.toUpperCase(),
        invoice_value: parseFloat(formData.orderDetails.invoiceValue),
        rvp_with_quality_check: false,
        weight_in_grams: convertWeight(formData.packageDetails.weight, units.weight),
        length_in_cm: convertLength(formData.packageDetails.length, units.length),
        breadth_in_cm: convertLength(formData.packageDetails.breadth, units.breadth),
        height_in_cm: convertLength(formData.packageDetails.height, units.height),
        pickup_pincode: selectedPickup?.pickup_postal_code,
        drop_pincode: formData.customerDetails.pincode,
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_MERCHANT_DOMESTIC_PRICING_BATCH}`,
        requestPayload,
        {
          headers: {
            Authorization: authToken,
            "x-merchant-id": merchantId,
            "Content-Type": "application/json",
          },
        }
      );

      // Deduplicate by carrier_partner_public_name: keep first occurrence only
      const seenNames = new Set();
      const formattedOptions = (response.data || [])
        .filter(rate => !reverseMatcher.test(rate?.carrier_partner_id || ""))
        .filter((rate) => {
          const name = rate.carrier_partner_public_name;
          if (seenNames.has(name)) return false;
          seenNames.add(name);
          return true;
        })
        .map((rate) => ({
          carrier_partner_public_name: rate.carrier_partner_public_name,
          total_amount_charged_after_tax: rate.total_amount_charged_after_tax,
          max_tat_in_days: rate.max_tat_in_days,
        }));

      setCourierOptions(formattedOptions);
      // A manual pick belongs to the rate list it was made from. Once the route/package/payment
      // changes and the list is replaced, drop it (and say so) — the price it carried may no
      // longer apply. Smart allocation is unaffected.
      setFormData((prev) => {
        const picked = prev.courierDetails.courierPartner;
        if (!picked || prev.courierDetails.useRecommendation) return prev;
        toast.info("Shipment details changed — pick a courier again from the updated rates.");
        return { ...prev, courierDetails: { ...prev.courierDetails, courierPartner: "" } };
      });
    } catch (error) {
      toast.error("Failed to fetch courier rates.");
    } finally {
      setLoadingCouriers(false);
    }
  };

  const fetchServiceability = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        toast.error("Missing auth token.");
        return;
      }
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CHECK_MERCHANT_SERVICEABILITY}`;
      const payload = {
        pickup_pincode: selectedPickup?.pickup_postal_code,
        drop_pincode: formData.customerDetails.pincode,
      };
      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      });
      if (
        response.data &&
        response.data.merchant_domestic_serviceabilities &&
        response.data.merchant_domestic_serviceabilities.length > 0
      ) {
        const serviceData = response.data.merchant_domestic_serviceabilities
          .filter(p => !/REVERSE/i.test(p.carrier_partner_id))
          .map(p => ({
            id: p.carrier_partner_id,
            name: (p.carrier_partner_identifier_string || "N/A").trim(),
            prepaid: !!p.prepaid_serviceability_details?.is_serviceable,
            cod: !!p.cod_serviceability_details?.is_serviceable,
          }));
        setCourierServiceability(serviceData);
      } else {
        toast.error("No serviceability data available.");
      }
    } catch (error) {
      toast.error("Failed to fetch serviceability details.");
    }
  };

  const safe = (obj, path, fallback = "") => {
    try {
      return path.split('.').reduce((a, b) => (a && a[b] !== undefined ? a[b] : undefined), obj) ?? fallback;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    const cloneFrom = location?.state?.cloneFrom;
    if (!cloneFrom) return;

    const mappedItems = (cloneFrom.shipment_packages ?? [])
      .flatMap(pkg => (pkg.items ?? []).map(it => ({
        name: safe(it, 'item_general_details.item_name', ''),
        quantity: safe(it, 'quantity', 1),
        unitPrice: safe(it, 'item_payment_details.unit_price', '') || '',
        skuId: safe(it, 'item_general_details.sku', '') || '',
        hsnCode: safe(it, 'item_general_details.hsn_code', '') || '',
        isFragile: Boolean(safe(pkg, 'special_handling_details.is_fragile', false) || safe(pkg, 'special_handling_details.fragile', false)),
      })));

    const items = mappedItems.length ? mappedItems : [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "", isFragile: false }];

    const pm = (safe(cloneFrom, 'payment_details.payment_mode', '') || safe(cloneFrom, 'order_type', '')).toString().toUpperCase();
    const paymentMode = pm === 'COD' ? 'COD' : 'Prepaid';

    const firstPkg = (cloneFrom.shipment_packages && cloneFrom.shipment_packages[0]) || {};
    const packageDims = cloneFrom.shipment_package_dimensions || {};
    const weightInGrams = safe(cloneFrom, 'dead_weight_in_grams', '') || safe(firstPkg, 'weight.value', '');
    const lengthVal = safe(packageDims, 'length', '') || safe(firstPkg, 'dimensions.length', '') || '';
    const breadthVal = safe(packageDims, 'breadth', '') || safe(firstPkg, 'dimensions.breadth', '') || '';
    const heightVal = safe(packageDims, 'height', '') || safe(firstPkg, 'dimensions.height', '') || '';

    const newForm = {
      customerDetails: {
        name: safe(cloneFrom, 'drop_name', ''),
        phoneNumber: safe(cloneFrom, 'drop_poc_contact_number', ''),
        email: safe(cloneFrom, 'drop_poc_email', '') || '',
        address: safe(cloneFrom, 'drop_address', '') || '',
        city: safe(cloneFrom, 'drop_city', '') || '',
        state: safe(cloneFrom, 'drop_state', '') || '',
        pincode: safe(cloneFrom, 'drop_pincode', '') || safe(cloneFrom, 'drop_postal_code', '') || '',
      },
      orderDetails: {
        clientOrderId: uuidv4(),
        invoiceValue: String(safe(cloneFrom, 'payment_details.total_amount', '') || safe(cloneFrom, 'invoice_value', '') || ''),
        itemDetails: items,
        invoiceNumber: safe(cloneFrom, 'invoice_number', '') || '',
        codAmount: pm === 'COD' ? String(safe(cloneFrom, 'payment_details.total_amount', '') || '') : undefined,
      },
      packageDetails: {
        weight: weightInGrams ? String(weightInGrams) : "",
        length: lengthVal ? String(lengthVal) : "",
        breadth: breadthVal ? String(breadthVal) : "",
        height: heightVal ? String(heightVal) : "",
      },
      courierDetails: {
        courierPartner: safe(cloneFrom, 'allocated_carrier_partner_public_name', '') || "",
        useRecommendation: true,
      },
      paymentMode,
    };

    const inferredUnits = { weight: 'gm', length: 'cm', breadth: 'cm', height: 'cm' };
    // Prefer shipment_package_dimensions.unit, then firstPkg.dimensions.unit
    const dimensionUnit = safe(packageDims, 'unit', '') || safe(firstPkg, 'dimensions.unit', '');
    if (dimensionUnit) {
      inferredUnits.length = dimensionUnit;
      inferredUnits.breadth = dimensionUnit;
      inferredUnits.height = dimensionUnit;
    }
    const weightUnit = safe(firstPkg, 'weight.unit', '') || (cloneFrom.product_weight && cloneFrom.product_weight.unit) || '';
    if (weightUnit) inferredUnits.weight = weightUnit;

    // optionally set selectedPickup based on pickup fields in shipment
    const mappedPickup = {
      pickup_address: safe(cloneFrom, 'pickup_address', ''),
      pickup_city: safe(cloneFrom, 'pickup_city', ''),
      pickup_postal_code: safe(cloneFrom, 'pickup_pincode', '') || safe(cloneFrom, 'pickup_postal_code', ''),
      pickup_poc: safe(cloneFrom, 'pickup_name', '') || safe(cloneFrom, 'pickup_poc', ''),
      pickup_organisation: safe(cloneFrom, 'pickup_organisation', '') || '',
      pickup_poc_contact_number: safe(cloneFrom, 'pickup_poc_contact_number', ''),
      pickup_poc_email: safe(cloneFrom, 'pickup_poc_email', ''),
      pickup_state: safe(cloneFrom, 'pickup_state', ''),
      pickup_district: safe(cloneFrom, 'pickup_district', ''),
      pickup_address_type: safe(cloneFrom, 'pickup_address_type', '') || 'BUSINESS',
      pickup_country_code: safe(cloneFrom, 'pickup_country_code', '') || 'IN',
      pickup_landmark: safe(cloneFrom, 'pickup_landmark', ''),
      gst_number: safe(cloneFrom, 'pickup_gst_number', '') || safe(cloneFrom, 'gst_number', ''),
    };

    setFormData(prev => ({
      ...prev,
      ...newForm
    }));

    setUnits(inferredUnits);
    setSelectedPickup(mappedPickup);
    // A loaded draft/clone starts where its data stands, not at step 1: land on the first
    // section that doesn't validate yet, and mark the sections before it as reached — so the
    // stepper doesn't make the merchant re-walk sections the draft already filled.
    // (validateSection reads state, so this waits for the hydration above to commit.)
    setPendingDraftLanding(true);

  }, [location?.state]);

  const handleItemChange = (index, key, value) => {
    const updatedItems = [...formData.orderDetails.itemDetails];
    updatedItems[index] = { ...updatedItems[index], [key]: value };
    setFormData((prev) => ({
      ...prev,
      orderDetails: {
        ...prev.orderDetails,
        itemDetails: updatedItems,
      },
    }));
  };

  const removeItemDetail = (index) => {
    if (formData.orderDetails.itemDetails.length > 1) {
      const filteredItems = formData.orderDetails.itemDetails.filter((_, idx) => idx !== index);
      setFormData({
        ...formData,
        orderDetails: {
          ...formData.orderDetails,
          itemDetails: filteredItems
        }
      });
    } else {
      // The only item: clear it back to a blank row rather than leaving zero items.
      setFormData({
        ...formData,
        orderDetails: {
          ...formData.orderDetails,
          itemDetails: [{ name: "", quantity: 1, unitPrice: "", skuId: "", hsnCode: "", isFragile: false }]
        }
      });
      setExpandedHSNItems({});
    }
  };

  const addItemDetail = () => {
    const newItem = {
      name: "",
      quantity: 1,
      unitPrice: "",
      skuId: "",
      hsnCode: "",
      isFragile: false,
    };
    setFormData({
      ...formData,
      orderDetails: {
        ...formData.orderDetails,
        itemDetails: [...formData.orderDetails.itemDetails, newItem]
      }
    });
  };

  const buildApiPayload = (trimmedForm) => {
    // Any item flagged fragile marks the whole package for special handling.
    const anyFragile = (trimmedForm.orderDetails.itemDetails || []).some((item) => Boolean(item.isFragile));
    return {
      shipment_order_type: "FORWARD",
      process_shipment_order_in_async: false,
      pickup_details: {
        pickup_address: selectedPickup.pickup_address,
        pickup_city: selectedPickup.pickup_city,
        pickup_district: selectedPickup.pickup_district,
        pickup_state: selectedPickup.pickup_state,
        pickup_postal_code: selectedPickup.pickup_postal_code,
        pickup_poc: selectedPickup.pickup_poc,
        pickup_country_code: selectedPickup.pickup_country_code,
        pickup_poc_contact_number: selectedPickup.pickup_poc_contact_number,
        pickup_address_type: selectedPickup.pickup_address_type,
        pickup_poc_email: selectedPickup.pickup_poc_email,
        pickup_organisation: selectedPickup.pickup_organisation,
        pickup_landmark: selectedPickup.pickup_landmark,
        ...((selectedPickup.gst_number || selectedPickup.pickup_gst_number) && String(selectedPickup.gst_number || selectedPickup.pickup_gst_number).trim() && {
          gst_number: String(selectedPickup.gst_number || selectedPickup.pickup_gst_number).trim()
        }),
      },
      drop_details: {
        drop_poc: trimmedForm.customerDetails.name,
        drop_address: trimmedForm.customerDetails.address,
        drop_city: trimmedForm.customerDetails.city,
        drop_country_code: "IN",
        drop_poc_contact_number: trimmedForm.customerDetails.phoneNumber,
        drop_poc_email: trimmedForm.customerDetails.email || "abc@gmail.com",
        drop_state: trimmedForm.customerDetails.state,
        drop_postal_code: trimmedForm.customerDetails.pincode,
      },
      shipment_details: {
        ...(!trimmedForm.courierDetails.useRecommendation && trimmedForm.courierDetails.courierPartner && {
          shipment_carrier_details: {
            carrier_partner_public_name: trimmedForm.courierDetails.courierPartner,
          },
        }),
        order_details: {
          order_type: trimmedForm.paymentMode.toUpperCase(),
          order_reference_number: trimmedForm.orderDetails.clientOrderId,
          order_invoice_details: {
            invoice_value: parseFloat(trimmedForm.orderDetails.invoiceValue),
            invoice_number: trimmedForm.orderDetails.invoiceNumber || null,
            invoice_currency: "INR"
          },
          ...(trimmedForm.paymentMode.toUpperCase() === "COD" && {
            cod_amount_details: {
              value: parseFloat(trimmedForm.orderDetails.codAmount),
              currency: "INR"
            }
          })
        },
        shipment_packages: [
          {
            items: trimmedForm.orderDetails.itemDetails.map(item => {
              const itemGeneralDetails = {
                item_name: item.name,
                sku: item.skuId
              };
              if (item.hsnCode && String(item.hsnCode).trim()) {
                itemGeneralDetails.hsn_code = String(item.hsnCode).trim();
              }
              return {
                item_general_details: itemGeneralDetails,
                quantity: item.quantity,
                item_payment_details: {
                  unit_price: item.unitPrice
                }
              };
            }),
            dimensions: {
              length: convertLength(trimmedForm.packageDetails.length, units.length),
              breadth: convertLength(trimmedForm.packageDetails.breadth, units.breadth),
              height: convertLength(trimmedForm.packageDetails.height, units.height),
              unit: 'cm'
            },
            weight: {
              value: convertWeight(trimmedForm.packageDetails.weight, units.weight),
              unit: "gm"
            },
            special_handling_details: {
              fragile: anyFragile,
              is_fragile: anyFragile,
            },
          }
        ]
      }
    };
  };

  const handleSaveAsDraft = async () => {
    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const orderData = buildApiPayload(trimmedForm);

    setSavingDraft(true);
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const draftPayload = {
        order_data: orderData,
        order_source: "UI_PANEL",
        ...(trimmedForm.orderDetails.clientOrderId && {
          title: `Order ${trimmedForm.orderDetails.clientOrderId}`
        }),
      };

      const apiUrl = draftOrderId
        ? API.updateDraftOrder(draftOrderId)
        : API.createDraftOrder();

      const response = draftOrderId
        ? await axios.put(apiUrl, draftPayload, {
            headers: {
              'Authorization': `${authToken}`,
              'Content-Type': 'application/json',
              'merchantId': merchantId
            }
          })
        : await axios.post(apiUrl, draftPayload, {
            headers: {
              'Authorization': `${authToken}`,
              'Content-Type': 'application/json',
              'merchantId': merchantId
            }
          });

      if (response.data.metadata?.success !== false && response.data.draft_order_id) {
        const newDraftId = response.data.draft_order_id;
        // Store the draftOrderId so subsequent saves use update API (not create)
        if (!draftOrderId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else if (response.data.metadata?.success) {
        const newDraftId = draftOrderId || response.data.draft_order_id;
        if (!draftOrderId && newDraftId) setDraftOrderId(newDraftId);
        setDraftSuccessModal({ open: true, draftId: newDraftId, isUpdate: Boolean(draftOrderId) });
      } else {
        toast.error(response.data.metadata?.message || "Failed to save draft.");
      }
    } catch (error) {
      const errMsg = error?.response?.data?.metadata?.message || "Error saving draft. Please try again.";
      toast.error(errMsg);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    // Onboarding gate: order screens are fully usable, but placing an order requires an
    // activated account. If locked, this opens the guided onboarding overlay and preserves
    // everything the merchant entered here.
    if (!requireBusinessOps('create your order')) return;

    // Backstop for the order-reference precheck (the field-level gate already blocks Continue).
    const submitRef = String(formData.orderDetails.clientOrderId || "").trim();
    if (!cloneMode && submitRef !== "" && orderRefCheck.ref === submitRef && orderRefCheck.outcome !== "NONE") {
      toast.error("An order already exists for this reference. Use a different reference.");
      return;
    }

    const trimmedForm = deepTrimStrings(formData);
    setFormData(trimmedForm);

    const apiPayload = buildApiPayload(trimmedForm);

    setLoading(true);
    setResponseData(null);

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const merchantId = localStorage.getItem("merchantId");
      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_CREATE_ORDER_URL}`;

      const response = await axios.post(apiUrl, apiPayload, {
        headers: {
          'Authorization': `${authToken}`,
          'Content-Type': 'application/json',
          'x-client-id': merchantId
        }
      });

      const outcome = interpretOrderCreationResponse(response.data);
      if (outcome.kind === "success") {
        setResponseData(outcome.result);
        setBookedAt(new Date());
        dispatch(fetchWalletBalance());
        new BroadcastChannel('wallet').postMessage('refresh-balance');
        setStep(6);
      } else {
        setOrderIssue(outcome.issue);
      }
    } catch (error) {
      setOrderIssue(interpretOrderCreationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOrderIssueAction = (action) => {
    setOrderIssue(null);
    if (action === "recharge") navigate("/wallet?recharge=1");
    else if (action === "wallet") navigate("/wallet");
    else if (action === "orders") navigate("/orders");
    else if (action === "login") navigate("/login");
    else if (action === "retry") handleSubmit();
  };

  /* ─────────────────────────────────────────────────────────────────────────
     Render-only derivations. Everything below reads state; nothing writes it.
  ───────────────────────────────────────────────────────────────────────── */
  const cd = formData.customerDetails;
  const od = formData.orderDetails;
  const pd = formData.packageDetails;
  const isCod = String(formData.paymentMode || "").toUpperCase() === "COD";
  /* Partial COD: the buyer paid part online, the rest is collected at the door. */
  const codAmountNum = parseFloat(od.codAmount ?? "");
  const invoiceValueNum = parseFloat(od.invoiceValue || 0);
  const isPartialCod = isCod && Number.isFinite(codAmountNum) && codAmountNum > 0 &&
    invoiceValueNum > 0 && codAmountNum < invoiceValueNum - 0.009;

  /* Validation display: a field speaks once it has been visited with content (blur) or
     after a Continue attempt - and the moment the merchant fixes it, the message and the
     red border go away. Empty required fields stay quiet until Continue, so tabbing
     through a fresh form never scolds. Render-only; validateStep still owns the gate. */
  const nonEmpty = (v) => String(v ?? "").trim() !== "";

  // Order-reference block: an existing order for the typed reference (any state but NONE).
  const orderRefForBlock = String(od.clientOrderId || "").trim();
  const orderRefBlocked = !cloneMode && orderRefForBlock !== "" && orderRefCheck.ref === orderRefForBlock && orderRefCheck.outcome !== "NONE";
  const orderRefBlockInfo = (() => {
    if (!orderRefBlocked) return null;
    const detailsHref = `/shipment-details/${encodeURIComponent(orderRefForBlock)}`;
    const unprocessedHref = `/search-unprocessed-orders?ref=${encodeURIComponent(orderRefForBlock)}`;
    switch (orderRefCheck.outcome) {
      case "PLACED": return { msg: `An order already exists for this reference${orderRefCheck.awb ? ` (AWB ${orderRefCheck.awb})` : ""}. Use a different reference for a new order.`, linkLabel: "View order", href: detailsHref };
      case "FAILED": return { msg: "This reference already has an order that didn't get placed. Use a different reference for a new order, or retry the existing one.", linkLabel: "Retry in Unprocessed Orders", href: unprocessedHref };
      case "PROCESSING": return { msg: "An order for this reference is still being processed — check back in a moment.", linkLabel: "View order", href: detailsHref };
      case "CONFIRMATION_PENDING": return { msg: "An order for this reference is awaiting customer confirmation (WhatsApp/IVR).", linkLabel: "View order", href: detailsHref };
      default: return { msg: "An order already exists for this reference. Use a different reference for a new order.", linkLabel: "View order", href: detailsHref };
    }
  })();

  const stillBad = (key) => {
    const m = /^(name|skuId|quantity|unitPrice)-(\d+)$/.exec(key);
    if (m) {
      const it = (od.itemDetails || [])[Number(m[2])] || {};
      if (m[1] === "name") return !nonEmpty(it.name);
      if (m[1] === "skuId") return !nonEmpty(it.skuId);
      if (m[1] === "quantity") return !nonEmpty(it.quantity) || Number(it.quantity) <= 0 || !Number.isInteger(Number(it.quantity));
      return !nonEmpty(it.unitPrice) || Number(it.unitPrice) < 0;
    }
    switch (key) {
      case "phoneNumber": return !/^[0-9]{10}$/.test(String(cd.phoneNumber || ""));
      case "name": return !nonEmpty(cd.name);
      case "email": { const em = String(cd.email || "").trim(); return !!em && !EMAIL_RE.test(em); }
      case "pickup": return !selectedPickup;
      case "address": return String(cd.address || "").trim().length < 10;
      case "city": return !nonEmpty(cd.city);
      case "state": return !nonEmpty(cd.state);
      case "pincode": return !/^[0-9]{6}$/.test(String(cd.pincode || ""));
      case "clientOrderId": return !nonEmpty(od.clientOrderId) || orderRefBlocked;
      case "invoiceValue": { const n = parseFloat(od.invoiceValue || 0); return !(Number.isFinite(n) && n > 0); }
      case "codAmount": { const n = parseFloat(od.codAmount ?? ""); return !(Number.isFinite(n) && n >= 0); }
      case "weight": case "length": case "breadth": case "height": {
        const v = String(pd[key] ?? "").trim();
        if (!v || isNaN(Number(v)) || Number(v) <= 0) return true;
        if (key === "weight") return (units.weight === "kg" ? Number(v) : Number(v) / 1000) > 200;
        return (units[key] === "inch" ? Number(v) * 2.54 : Number(v)) > 500;
      }
      default: return true;
    }
  };
  const msgFor = (key) => {
    const m = /^(name|skuId|quantity|unitPrice)-\d+$/.exec(key);
    return FIELD_MSG[m ? "item-" + m[1] : key] || "Required";
  };
  // clientOrderId's block message is link-rich, so it renders as a custom band below the field
  // (not via FieldError). Suppress the generic message here to avoid a duplicate.
  const E = (key) => {
    if (key === "clientOrderId" && orderRefBlocked) return undefined;
    return (errors[key] || touched[key]) && stillBad(key) ? msgFor(key) : undefined;
  };
  const keyStep = (key) => (/^(name|skuId|quantity|unitPrice)-\d+$/.test(key) ? 3 : FIELD_STEP[key]);
  const liveErrCount = [...new Set([...Object.keys(errors), ...Object.keys(touched)])]
    .filter((k) => keyStep(k) === step && stillBad(k)).length;
  const handleFieldBlur = (e) => {
    const key = e.target && e.target.dataset ? e.target.dataset.vkey : undefined;
    if (!key) return;
    // A number field whose text the browser can't parse reports value "" but sets
    // validity.badInput — treat it as typed-and-wrong so its error shows.
    const badNumber = e.target.type === "number" && e.target.validity && e.target.validity.badInput;
    if (!badNumber && !String(e.target.value ?? "").trim()) return;
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  /* Group-level completeness for the journey spine - the node turns emerald the moment
     a group is actually complete. Render-only; validateStep still owns the gate. */
  const emailEntered = String(cd.email || "").trim();
  const contactDone = /^[0-9]{10}$/.test(String(cd.phoneNumber || "")) && nonEmpty(cd.name) &&
    (!emailEntered || EMAIL_RE.test(emailEntered));
  const addressDone = String(cd.address || "").trim().length >= 10 && /^[0-9]{6}$/.test(String(cd.pincode || "")) && nonEmpty(cd.city) && nonEmpty(cd.state);
  const paymentDone = (() => {
    const n = parseFloat(od.invoiceValue || 0);
    if (!(Number.isFinite(n) && n > 0)) return false;
    if (isCod) { const c = parseFloat(od.codAmount ?? ""); return Number.isFinite(c) && c >= 0; }
    return true;
  })();
  const orderDone = nonEmpty(od.clientOrderId) && !orderRefBlocked;
  const itemsDone = od.itemDetails.length > 0 && od.itemDetails.every(it =>
    nonEmpty(it.name) && nonEmpty(it.skuId) &&
    nonEmpty(it.quantity) && Number(it.quantity) > 0 && Number.isInteger(Number(it.quantity)) &&
    nonEmpty(it.unitPrice) && Number(it.unitPrice) >= 0);
  const weightDone = ["weight", "length", "breadth", "height"].every(k => {
    const v = String(pd[k] ?? "").trim();
    if (!v || isNaN(Number(v)) || Number(v) <= 0) return false;
    if (k === "weight") return (units.weight === "kg" ? Number(v) : Number(v) / 1000) <= 200;
    return (units[k] === "inch" ? Number(v) * 2.54 : Number(v)) <= 500;
  });
  const pickupDone = Boolean(selectedPickup);

  const deadKg = convertWeight(pd.weight, units.weight) / 1000;
  const volKg =
    pd.length && pd.breadth && pd.height
      ? (convertLength(pd.length, units.length) *
          convertLength(pd.breadth, units.breadth) *
          convertLength(pd.height, units.height)) / 5000
      : null;
  const billedKg = volKg != null && pd.weight ? Math.max(deadKg, volKg) : null;
  const billedBasis = billedKg == null ? null : billedKg === deadKg && deadKg >= (volKg ?? 0) ? "dead" : "vol";

  const itemsSubtotal = od.itemDetails.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
  const invoiceMismatch =
    itemsSubtotal > 0 && parseFloat(od.invoiceValue || 0) > 0 &&
    Math.abs(itemsSubtotal - parseFloat(od.invoiceValue)) > 0.009;

  const serviceableOf = (option) => {
    const serviceableEntries = courierServiceability.filter(
      s => s.name === (option.carrier_partner_public_name || "").trim()
    );
    return isCod ? serviceableEntries.some(s => s.cod) : serviceableEntries.some(s => s.prepaid);
  };
  const sortedOptions = [...courierOptions].sort((a, b) => {
    const sa = serviceableOf(a) ? 0 : 1;
    const sb = serviceableOf(b) ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return Number(a.total_amount_charged_after_tax) - Number(b.total_amount_charged_after_tax);
  });
  const serviceableOptions = sortedOptions.filter(serviceableOf);
  const unserviceableOptions = sortedOptions.filter(o => !serviceableOf(o));
  // Ties are real: every courier at the lowest price is "Cheapest" and every one at the shortest
  // TAT is "Fastest" — the tag describes the number, not a single row.
  const finiteOf = (list, pick) => list.map(pick).filter(Number.isFinite);
  const cheapestPrices = finiteOf(serviceableOptions, (o) => Number(o.total_amount_charged_after_tax));
  const fastestTats = finiteOf(serviceableOptions, (o) => Number(o.max_tat_in_days));
  const cheapestPrice = cheapestPrices.length ? Math.min(...cheapestPrices) : null;
  const fastestTat = fastestTats.length ? Math.min(...fastestTats) : null;
  const chosenOption = courierOptions.find(
    o => o.carrier_partner_public_name === formData.courierDetails.courierPartner);
  // Sort key ('cheapest'|'fastest') drives display order only.
  const serviceableDisplay = courierSort === "fastest"
    ? [...serviceableOptions].sort((a, b) =>
        (Number(a.max_tat_in_days) - Number(b.max_tat_in_days)) ||
        (Number(a.total_amount_charged_after_tax) - Number(b.total_amount_charged_after_tax)))
    : serviceableOptions;
  const titleCaseCity = (s) => String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const courierRoute = (selectedPickup?.pickup_city && formData.customerDetails?.city)
    ? `${titleCaseCity(selectedPickup.pickup_city)} → ${titleCaseCity(formData.customerDetails.city)}` : null;

  const filteredPickups = pickupQuery.trim()
    ? pickupDetails.filter(pk =>
        [pk.pickup_organisation, pk.pickup_poc, pk.pickup_city, pk.pickup_state, pk.pickup_postal_code, pk.pickup_address]
          .join(" ").toLowerCase().includes(pickupQuery.trim().toLowerCase()))
    : pickupDetails;

  // The truck advances one leg per completed section - it never moves on auto-selected
  // or default values, only on sections the merchant has actually finished.

  /* Callback ref: the step content mounts after AnimatePresence finishes the exit,
     later than any effect tied to the step change - so the observer attaches the
     moment the node exists and detaches when it goes. */
  const attachPickupList = (el) => {
    pickupListRef.current = el;
    if (pickupListRO.current) { pickupListRO.current.disconnect(); pickupListRO.current = null; }
    if (el) {
      const ro = new ResizeObserver(updatePickupEdges);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      pickupListRO.current = ro;
      updatePickupEdges();
    }
  };
  // Ticket progress = sections genuinely completed. Steps: the deepest step reached (Continue
  // validated everything before it). Stacked: sections that validate top-down — the same gate
  // that unlocks the next card — so an empty form is 0, never the pinned "5".
  const reached = stacked ? unlockedUpTo : highestStepReached;
  const pct = step === 6 ? 100 : Math.round(((reached - 1) / 5) * 100);
  const booked = step === 6 && responseData;
  // The form is tall and the merchant is at its bottom when they book; the confirmation is
  // short, so the browser clamps the old scroll offset to the new page's bottom and the ticket
  // opens scrolled past its stamp (phones especially). Start the confirmation from the top —
  // before paint, so there is no flash of the wrong position.
  useLayoutEffect(() => {
    if (!booked) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [booked]);

  /* ── Ticket data: strictly what the merchant has entered or reached ── */
  const dropKnown = /^[0-9]{6}$/.test(String(cd.pincode || ""));
  const originKnown = reached >= 2 && selectedPickup?.pickup_postal_code;
  const ticketRows = [
    {
      k: "Recipient",
      v: (cd.name || cd.phoneNumber) ? [cd.name, cd.phoneNumber].filter(Boolean).join(" · ") : null,
    },
    {
      k: "Pickup",
      v: originKnown ? (selectedPickup.pickup_organisation || selectedPickup.pickup_poc || selectedPickup.pickup_city) : null,
    },
    {
      k: "Courier",
      v: reached >= 5
        ? (formData.courierDetails.useRecommendation
            ? "Smart allocation"
            : chosenOption?.carrier_partner_public_name || null)
        : null,
    },
  ];
  const namedItems = od.itemDetails.filter(it => String(it.name || "").trim()).length;
  const ticketCells = [
    {
      k: reached >= 3 ? (isCod ? (isPartialCod ? "Partial COD" : "COD") : "Prepaid") : "Payment",
      v: reached >= 3
        ? (isCod && od.codAmount ? fmtInr(od.codAmount) : parseFloat(od.invoiceValue) > 0 ? fmtInr(od.invoiceValue) : null)
        : null,
    },
    { k: namedItems === 1 ? "Item" : "Items", v: namedItems ? String(namedItems) : null },
    {
      k: "Chargeable wt",
      v: billedKg != null ? `${billedKg.toFixed(2)} kg` : null,
      sub: billedKg != null && billedBasis ? (billedBasis === "vol" ? "volumetric" : "dead") : null,
    },
  ];
  const chargeValue = !formData.courierDetails.useRecommendation && chosenOption
    ? fmtInr(chosenOption.total_amount_charged_after_tax)
    : null;

  // How this shipment charge settles. Prefer the OMS-resolved type (SQL wallet OR Mongo onboarding);
  // fall back to the redux wallet type if that endpoint hasn't answered. Unknown → shown as nothing.
  const settlementLabel = (() => {
    const t = String(resolvedSettlement || walletType || "").toUpperCase();
    if (!t) return null;
    if (t.includes("COD") || t.includes("NETOFF") || t.includes("NET_OFF")) return "COD net-off";
    if (t.includes("POSTPAID") || t.includes("POST_PAID")) return "your postpaid wallet";
    if (t.includes("PREPAID") || t.includes("PRE_PAID")) return "your prepaid wallet";
    return null;
  })();

  // Booked summary: resolve the confirmed courier back to its fetched rate/TAT so the success
  // screen can show the charge and a real delivery estimate — for smart allocation (courier
  // chosen server-side) as well as manual. Degrades gracefully when a field isn't available.
  const bookedInfo = booked ? (() => {
    const name = responseData.courier_name || null;
    const opt = (name && courierOptions.find((o) => o.carrier_partner_public_name === name)) || chosenOption || null;
    const charge = opt && opt.total_amount_charged_after_tax != null ? Number(opt.total_amount_charged_after_tax) : null;
    const tat = opt && opt.max_tat_in_days != null ? Number(opt.max_tat_in_days) : null;
    const { carrier, mode } = formatCarrier(name || "");
    return { name, carrier, mode, accent: carrierAccent(carrier), charge, tat, edd: tat != null ? estDeliveryDate(tat) : null };
  })() : null;

  /* ── Step bodies (the desk side) ── */

  const renderRecipient = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={PhoneIcon} title="Contact" hint="Saved customers fill in from the phone number." done={contactDone}>
      <div className="grid gap-x-5 gap-y-3.5 md:grid-cols-2">
        <div>
          <label className={LABEL}>Phone number</label>
          <div className="relative">
            <PhoneIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              name="phoneNumber"
              placeholder="e.g. 9876543210"
              value={cd.phoneNumber}
              onChange={handlePhoneNumberChange}
              data-vkey="phoneNumber"
              aria-invalid={Boolean(E("phoneNumber"))}
              aria-describedby={E("phoneNumber") ? "err-phoneNumber" : undefined}
              className={inputClass(E("phoneNumber"), "pl-10 pr-10")}
            />
            {cd.phoneNumber && !E("phoneNumber") && cd.phoneNumber.length === 10 && (
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2">
                {/* positioning lives on the wrapper: framer-motion owns this element's transform */}
                <motion.span initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 500, damping: 24 }} className="block text-emerald-500">
                  <CheckIcon className="h-4 w-4" strokeWidth={3} />
                </motion.span>
              </span>
            )}
          </div>
          <FieldError id="err-phoneNumber" message={E("phoneNumber")} />
        </div>
        <div>
          <label className={LABEL}>Full name</label>
          <div className="relative">
            <UserIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="name"
              placeholder="e.g. Priya Sharma"
              value={cd.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  customerDetails: { ...prev.customerDetails, name: e.target.value },
                }))
              }
              onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, name: e.target.value.trim() } }))}
              data-vkey="name"
              aria-invalid={Boolean(E("name"))}
              aria-describedby={E("name") ? "err-name" : undefined}
              className={inputClass(E("name"), "pl-10")}
            />
          </div>
          <FieldError id="err-name" message={E("name")} />
        </div>
        <div>
          <label className={LABEL}>Email <span className="font-medium text-slate-400">· optional</span></label>
          <div className="relative">
            <MailIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              name="email"
              placeholder="e.g. priya@email.com"
              value={cd.email}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  customerDetails: { ...prev.customerDetails, email: e.target.value },
                }))
              }
              onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, email: e.target.value.trim() } }))}
              data-vkey="email"
              aria-invalid={Boolean(E("email"))}
              aria-describedby={E("email") ? "err-email" : undefined}
              className={inputClass(E("email"), "pl-10")}
            />
          </div>
          <FieldError id="err-email" message={E("email")} />
        </div>
      </div>

      </JourneyGroup>

      <JourneyGroup icon={MapPinIcon} title="Delivery address" hint="We’ll fill the city and state from the pincode — feel free to edit or add them yourself." done={addressDone} last>
      <div>
        <label className={LABEL}>Address</label>
        <textarea
          name="address"
          placeholder="e.g. 42 Green Park, Hauz Khas"
          value={cd.address}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              customerDetails: { ...prev.customerDetails, address: e.target.value },
            }))
          }
          onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, address: e.target.value.trim() } }))}
          rows="2"
          data-vkey="address"
          aria-invalid={Boolean(E("address"))}
              aria-describedby={E("address") ? "err-address" : undefined}
          className={`${inputClass(E("address"))} h-auto min-h-[68px] resize-none py-2.5 leading-relaxed`}
        />
        <FieldError id="err-address" message={E("address")} />
      </div>
      <div className="mt-4 grid gap-x-5 gap-y-3.5 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1.1fr)]">
        <div>
          <label className={LABEL}>Pincode</label>
          <div className="relative">
            <MapPinIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              name="pincode"
              placeholder="e.g. 400001"
              value={cd.pincode}
              onChange={handlePincodeChange}
              maxLength="6"
              data-vkey="pincode"
              aria-invalid={Boolean(E("pincode"))}
              aria-describedby={E("pincode") ? "err-pincode" : undefined}
              className={inputClass(E("pincode"), "pl-10 pr-10")}
            />
            {loadingPincode && (
              <span className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2">
                <span className="block h-full w-full animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
              </span>
            )}
          </div>
          {loadingPincode ? <p className="mt-1.5 text-[12px] font-medium text-[#3877FC]">Finding the city and state…</p> : null}
          {!loadingPincode && pincodeLookupFailed ? (
            <p className="mt-1.5 text-[12px] font-medium text-amber-700">We couldn’t find this pincode — please add the city and state below.</p>
          ) : null}
          <FieldError id="err-pincode" message={E("pincode")} />
        </div>
        <div>
          <label className={LABEL}>City</label>
          <input
            type="text"
            name="city"
            value={cd.city}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                customerDetails: { ...prev.customerDetails, city: e.target.value },
              }))
            }
            onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, city: e.target.value.trim() } }))}
            data-vkey="city"
            aria-invalid={Boolean(E("city"))}
              aria-describedby={E("city") ? "err-city" : undefined}
            className={inputClass(E("city"), cd.city ? "cfo-wash" : "")}
            placeholder="From pincode"
          />
          <FieldError id="err-city" message={E("city")} />
        </div>
        <div>
          <label className={LABEL}>State</label>
          <input
            type="text"
            name="state"
            value={cd.state}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                customerDetails: { ...prev.customerDetails, state: e.target.value },
              }))
            }
            onBlur={(e) => setFormData((prev) => ({ ...prev, customerDetails: { ...prev.customerDetails, state: e.target.value.trim() } }))}
            data-vkey="state"
            aria-invalid={Boolean(E("state"))}
              aria-describedby={E("state") ? "err-state" : undefined}
            className={inputClass(E("state"), cd.state ? "cfo-wash" : "")}
            placeholder="From pincode"
          />
          <FieldError id="err-state" message={E("state")} />
        </div>
      </div>
      </JourneyGroup>
    </div>
  );

  // Refresh the saved locations, spinning the glyph for at least ~a full turn so the click is
  // visibly acknowledged even when the list comes back unchanged.
  const refreshPickupLocations = async () => {
    if (refreshingPickups) return;
    setRefreshingPickups(true);
    const t0 = Date.now();
    try { await Promise.resolve(fetchAndStorePickupDetails()); } catch { /* the fetch reports its own errors */ }
    window.setTimeout(() => setRefreshingPickups(false), Math.max(0, 900 - (Date.now() - t0)));
  };

  const renderPickup = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup
        icon={StoreIcon}
        title="Saved locations"
        done={pickupDone}
        last
        hint={pickupDetails.length ? `${pickupDetails.length} saved` : null}
        right={(
          <>
        {pickupDetails.length > 6 && (
          <div className="relative min-w-0 flex-1 sm:flex-none">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={pickupQuery}
              onChange={(e) => setPickupQuery(e.target.value)}
              placeholder="Search name, city or pincode"
              className="h-9 w-full rounded-xl border border-[#D6DEE9] bg-white pl-9 pr-3 text-[13px] sm:w-60 font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-all hover:border-[#AFBDD1] focus:border-[#3877FC] focus:ring-4 focus:ring-[#3877FC]/10"
            />
          </div>
        )}
          <button
            type="button"
            onClick={refreshPickupLocations}
            title="Refresh locations"
            aria-busy={refreshingPickups}
            className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border bg-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${refreshingPickups ? "border-[#3877FC]/40 text-[#3877FC]" : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"}`}
          >
            <RefreshIcon className={`h-4 w-4 ${refreshingPickups ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={openAddAddress}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3877FC] px-3.5 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_1px_2px_rgba(15,23,42,0.10),0_6px_16px_-8px_rgba(56,119,252,0.55)] transition-all hover:bg-[#2A59BD] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 focus-visible:ring-offset-2"
          >
            <PlusIcon className="h-4 w-4" strokeWidth={2.5} /> Add pickup location
          </button>
          </>
        )}
      >
      {E("pickup") ? (
        <p className="mb-4 rounded-lg border border-rose-200/70 bg-rose-50 px-3.5 py-2.5 text-[12.5px] font-semibold text-rose-700">
          Choose a pickup location to continue.
        </p>
      ) : null}
      {pickupDetails.length > 0 ? (
        filteredPickups.length > 0 ? (
          <div className="relative">
          <div ref={attachPickupList} onScroll={updatePickupEdges} className="custom-scrollbar max-h-[min(52vh,560px)] overflow-y-auto pr-1.5">
          <div className="columns-1 gap-3 md:columns-2">
            {filteredPickups.map((pickup) => {
              const isSelected = selectedPickup?.pickup_address_id === pickup.pickup_address_id;
              const locName = displayLocName(pickup.pickup_organisation || pickup.pickup_poc);
              return (
                <div key={pickup.pickup_address_id} className="group relative mb-3 break-inside-avoid">
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-all duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#3877FC]/30 ${
                      isSelected
                        ? "border-[#3877FC] bg-[#3877FC]/[0.04] shadow-[0_2px_10px_-4px_rgba(56,119,252,0.35)]"
                        : "border-slate-200 bg-white hover:border-[#AFBDD1] hover:shadow-sm"
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupLocation"
                      className="sr-only"
                      checked={isSelected}
                      onChange={() => setSelectedPickup(pickup)}
                    />
                    <motion.span
                      layout
                      transition={{ type: "spring", stiffness: 500, damping: 26 }}
                      className={`mt-0.5 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-[12px] font-bold tracking-wide transition-colors duration-150 ${
                        isSelected ? "bg-[#3877FC] text-white shadow-[0_2px_8px_-2px_rgba(56,119,252,0.5)]" : "bg-[#3877FC]/[0.08] text-[#2A59BD]"
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected ? <CheckIcon className="h-4 w-4" strokeWidth={3} /> : locInitials(locName)}
                    </motion.span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="min-w-0 text-[13.5px] font-semibold leading-snug text-slate-900">{locName}</span>
                        <span className="flex flex-shrink-0 items-center gap-1">
                          <span className="font-mono text-[12px] font-semibold tabular-nums text-slate-700">{pickup.pickup_postal_code}</span>
                          <button
                            type="button"
                            aria-label={`Options for ${locName}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPickupMenuFor(pickupMenuFor === pickup.pickup_address_id ? null : pickup.pickup_address_id); }}
                            className="grid h-6 w-6 -translate-y-0.5 cursor-pointer place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                          >
                            <MoreVerticalIcon className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-snug text-slate-500">
                        {titleCaseLoc(pickup.pickup_address)}
                      </span>
                      <span className="mt-1 block text-[11.5px] leading-snug text-slate-400">
                        <span className="font-medium text-slate-500">{[titleCaseLoc(pickup.pickup_city), titleCaseLoc(pickup.pickup_state)].filter(Boolean).join(", ")}</span>
                        {pickup.pickup_poc ? ` · ${pickup.pickup_poc}` : ""}{pickup.pickup_poc_contact_number ? ` · ${pickup.pickup_poc_contact_number}` : ""}
                      </span>
                    </span>
                  </label>
                  {pickupMenuFor === pickup.pickup_address_id && (
                    <>
                      <button type="button" aria-label="Close menu" className="fixed inset-0 z-10 cursor-default" onClick={() => setPickupMenuFor(null)} />
                      <div className="absolute right-2 top-9 z-20 w-32 overflow-hidden rounded-lg border border-[#D6DEE9] bg-white p-1 shadow-lg">
                        <button
                          type="button"
                          onClick={() => { setPickupMenuFor(null); openEditAddress(pickup); }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-[#2A59BD]"
                        >
                          <PencilIcon className="h-3.5 w-3.5" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => { setPickupMenuFor(null); openDeleteAddress(pickup.pickup_address_id); }}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                        >
                          <TrashIcon className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          </div>
          <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-[#FAFBFD] via-[#FAFBFD]/70 to-transparent transition-opacity duration-200 ${pickupEdges.top ? "opacity-0" : "opacity-100"}`} />
          <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#FAFBFD] via-[#FAFBFD]/70 to-transparent transition-opacity duration-200 ${pickupEdges.bottom ? "opacity-0" : "opacity-100"}`} />
          </div>
        ) : (
          <p className="text-[13.5px] text-slate-500">No saved location matches “{pickupQuery.trim()}”.</p>
        )
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-slate-100"><StoreIcon className="h-6 w-6 text-slate-400" /></span>
          <p className="mt-1 text-[14.5px] font-semibold text-slate-800">No pickup locations yet</p>
          <p className="text-[13px] text-slate-500">Add the address the courier should collect from.</p>
          <button type="button" onClick={openAddAddress} className={`${BTN_PRIMARY} mt-3`}>
            + Add pickup location
          </button>
        </div>
      )}
      </JourneyGroup>
    </div>
  );

  const renderPayment = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={RupeeIcon} title="Payment" done={paymentDone}>
      <div className="flex flex-wrap items-start gap-x-5 gap-y-4">
        <div>
          <label className={LABEL}>Payment mode</label>
          <div className="flex h-11 items-stretch rounded-xl bg-[#EEF2F8] p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-slate-900/[0.05]">
            {["Prepaid", "COD"].map((mode) => {
              const active = formData.paymentMode === mode;
              return (
                <label key={mode} className="relative cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMode"
                    value={mode}
                    checked={active}
                    onChange={(e) => setFormData((prev) => ({ ...prev, paymentMode: e.target.value }))}
                    className="sr-only"
                  />
                  {active ? (
                    <motion.span
                      layoutId="cfoPayThumb"
                      transition={{ type: "spring", stiffness: 500, damping: 38 }}
                      className="absolute inset-0 rounded-lg bg-[#3877FC] shadow-[0_1px_2px_rgba(15,23,42,0.25)]"
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className={`relative z-10 inline-flex h-full items-center px-5 text-[13px] font-semibold transition-[color,transform] duration-150 active:scale-[0.97] ${
                    active ? "text-white" : "text-slate-500 hover:text-slate-800"
                  }`}>
                    {mode === "COD" ? "COD" : "Prepaid"}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="w-44">
          <label className={LABEL}>Invoice value <span className="font-medium text-slate-400">· incl. GST</span></label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-medium text-slate-400">₹</span>
            <input
              type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
              value={od.invoiceValue === 0 ? "" : od.invoiceValue}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  orderDetails: {
                    ...prev.orderDetails,
                    invoiceValue: e.target.value,
                  },
                }))
              }
              data-vkey="invoiceValue"
              aria-invalid={Boolean(E("invoiceValue"))}
              aria-describedby={E("invoiceValue") ? "err-invoiceValue" : undefined}
              className={inputClass(E("invoiceValue"), "pl-8 text-right tabular-nums")}
              placeholder="e.g. 1500"
            />
          </div>
          <FieldError id="err-invoiceValue" message={E("invoiceValue")} />
        </div>
        <AnimatePresence initial={false}>
          {isCod && (
            <motion.div
              key="cod"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.16 }}
              className="w-64"
            >
              <label className={LABEL}>Amount to collect</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[14px] font-medium text-slate-400">₹</span>
                <input
                  type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                  value={od.codAmount || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      orderDetails: { ...prev.orderDetails, codAmount: e.target.value },
                    }))
                  }
                  data-vkey="codAmount"
                  aria-invalid={Boolean(E("codAmount"))}
              aria-describedby={E("codAmount") ? "err-codAmount" : undefined}
                  className={inputClass(E("codAmount"), "pl-8 text-right tabular-nums")}
                  placeholder="Usually the invoice value"
                />
              </div>
              <FieldError id="err-codAmount" message={E("codAmount")} />
              {isPartialCod && !E("codAmount") ? (
                <p className="mt-1.5 flex items-start gap-1.5 text-[11.5px] font-medium text-slate-500">
                  <span className="flex-shrink-0 whitespace-nowrap rounded-md bg-[#3877FC]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2A59BD]">Partial COD</span>
                  <span className="leading-snug">{fmtInr(invoiceValueNum - codAmountNum)} paid beforehand to merchant</span>
                </p>
              ) : null}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      </JourneyGroup>

      <JourneyGroup icon={FileTextIcon} title="Order" done={orderDone}>
      <div className="grid gap-x-5 gap-y-3.5 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div>
          <label className={LABEL}>Order reference</label>
          <div className="relative">
            <input
              type="text"
              value={od.clientOrderId}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value },
                }))
              }
              onBlur={(e) => setFormData((prev) => ({ ...prev, orderDetails: { ...prev.orderDetails, clientOrderId: e.target.value.trim() } }))}
              data-vkey="clientOrderId"
              aria-invalid={Boolean(E("clientOrderId")) || orderRefBlocked}
              aria-describedby={E("clientOrderId") ? "err-clientOrderId" : undefined}
              className={inputClass(Boolean(E("clientOrderId")) || orderRefBlocked, "pr-11 font-mono text-[12.5px]")}
              placeholder="e.g. SHOP-1042"
            />
            <button
              type="button"
              onClick={handleGenerateOrderId}
              title="Generate a reference"
              aria-label="Generate a reference"
              className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 cursor-pointer place-items-center rounded-lg text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
            >
              <SparklesIcon className="h-4 w-4" />
            </button>
          </div>
          <FieldError id="err-clientOrderId" message={E("clientOrderId")} />
          {orderRefBlockInfo ? (
            <div className="mt-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
              <p className="text-[12px] font-medium leading-snug text-rose-700">{orderRefBlockInfo.msg}</p>
              <a href={orderRefBlockInfo.href} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[11.5px] font-semibold text-[#2A59BD] transition-colors hover:text-[#1E40AF] hover:underline">
                {orderRefBlockInfo.linkLabel} <ExternalLinkIcon className="h-3 w-3" />
              </a>
            </div>
          ) : (
            <p className="mt-1.5 text-[11.5px] text-slate-500">Your own ID for this order. Shows on the label and in reports.</p>
          )}
        </div>
        <div>
          {showShippingDetails ? (
            <div>
              <label className={LABEL}>Invoice number <span className="font-medium text-slate-400">· optional</span></label>
              <input
                type="text"
                value={od.invoiceNumber || ""}
                onChange={(e) =>
                  setFormData((prev2) => ({
                    ...prev2,
                    orderDetails: { ...prev2.orderDetails, invoiceNumber: e.target.value },
                  }))
                }
                onBlur={(e) => setFormData((prev2) => ({ ...prev2, orderDetails: { ...prev2.orderDetails, invoiceNumber: e.target.value.trim() } }))}
                className={inputClass(false)}
                placeholder="e.g. INV-2043"
              />
            </div>
          ) : (
            <div className="md:pt-[24px]">
              <button
                type="button"
                onClick={() => setShowShippingDetails((v) => !v)}
                className="inline-flex h-11 cursor-pointer items-center rounded-xl border border-dashed border-[#3877FC]/35 bg-[#3877FC]/[0.03] px-4 text-[13px] font-semibold text-[#2A59BD] transition-all hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.07] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
              >
                + Add invoice number&nbsp;<span className="font-medium text-[#2A59BD]/55">· optional</span>
              </button>
            </div>
          )}
        </div>
      </div>

      </JourneyGroup>

      <JourneyGroup icon={TagIcon} title="Items" done={itemsDone} last>
      {orderRefBlocked && (
        <p className="mb-2.5 text-[12px] font-medium text-slate-500">Sort out the order reference above before adding items.</p>
      )}
      <div className={`cfo-thin-scroll overflow-x-auto ${orderRefBlocked ? "pointer-events-none select-none opacity-50" : ""}`} aria-disabled={orderRefBlocked}>
        <div className="min-w-[560px]">
          <div className="overflow-hidden rounded-xl border border-[#D6DEE9] bg-white shadow-[0_1px_2px_rgba(42,89,189,0.05)]">
          {/* caps band - same grid as the rows, captions on each column's text axis */}
          <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_84px_120px_32px] gap-3 border-b border-[#E7ECF3] bg-[#3877FC]/[0.06] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            <span className="pl-3.5">Item</span>
            <span className="pl-3.5">SKU{cloneMode ? <span className="text-slate-500"> · optional</span> : null}</span>
            <span className="pr-3.5 text-right">Qty</span>
            <span className="pr-3.5 text-right">Unit price</span>
            <span aria-hidden="true" />
          </div>
          <div className="divide-y divide-[#E7ECF3]">
            {od.itemDetails.map((item, index) => (
              <div key={index} className="bg-[#F6F9FF] px-3 py-2.5 transition-colors hover:bg-[#EEF4FE] focus-within:bg-[#E7EFFE]">
                <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_84px_120px_32px] items-start gap-3">
                  <div>
                    {/* Long names must stay fully visible: an auto-growing one-line textarea
                        in the input's material. Newlines never enter the value (Enter still
                        advances the step via the global handler; pasted breaks become spaces). */}
                    <textarea
                      rows={1}
                      value={item.name}
                      onChange={(e) => handleItemChange(index, "name", e.target.value.replace(/\s*\n\s*/g, " "))}
                      onBlur={(e) => handleItemChange(index, "name", e.target.value.trim())}
                      onKeyDown={(e) => { if (e.key === "Enter" && e.shiftKey) e.preventDefault(); }}
                      ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; } }}
                      aria-label="Item name"
                      data-vkey={`name-${index}`}
                      aria-invalid={Boolean(E(`name-${index}`))}
                      aria-describedby={E(`name-${index}`) ? `err-name-${index}` : undefined}
                      className={`w-full resize-none overflow-hidden rounded-xl border bg-white px-3.5 py-[11px] text-sm font-medium leading-[20px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ${E(`name-${index}`) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"}`}
                      placeholder="e.g. Cotton kurta set"
                    />
                    <FieldError id={`err-name-${index}`} message={E(`name-${index}`)} />
                  </div>
                  <div>
                    <div className="relative">
                      <textarea
                        rows={1}
                        value={item.skuId}
                        onChange={(e) => { const v = e.target.value.replace(/\s*\n\s*/g, ""); handleItemChange(index, "skuId", v); queueSkuSearch(index, v); }}
                        onFocus={(e) => {
                          const typed = e.target.value.trim();
                          const itemName = String(item.name || "").trim();
                          if (!typed && itemName.length >= 2) queueSkuSearch(index, itemName, true);
                        }}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v && orderHasSku(v, index)) { toast.error(DUPLICATE_SKU_MSG); handleItemChange(index, "skuId", ""); window.setTimeout(closeSkuSearch, 150); return; } handleItemChange(index, "skuId", v); window.setTimeout(closeSkuSearch, 150); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); return; }
                          if (!(skuSearch.open && skuSearch.index === index)) return;
                          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                            e.preventDefault();
                            const n = skuSearch.results.length;
                            if (!n) return;
                            setSkuSearch((prev) => ({ ...prev, hi: ((prev.hi + (e.key === "ArrowDown" ? 1 : -1)) + n) % n }));
                          } else if (e.key === "Enter") {
                            e.preventDefault(); e.stopPropagation();
                            if (skuSearch.hi >= 0 && skuSearch.results[skuSearch.hi]) chooseSkuResult(index, skuSearch.results[skuSearch.hi]);
                            else closeSkuSearch();
                          } else if (e.key === "Escape") {
                            e.stopPropagation();
                            closeSkuSearch();
                          }
                        }}
                        ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; } }}
                        aria-label="SKU"
                        data-vkey={`skuId-${index}`}
                        aria-invalid={Boolean(E(`skuId-${index}`))}
                        aria-describedby={E(`skuId-${index}`) ? `err-skuId-${index}` : undefined}
                        className={`w-full resize-none overflow-hidden rounded-xl border bg-white px-3.5 py-[12px] font-mono text-[12.5px] font-medium leading-[18px] text-slate-900 outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] pr-11 ${E(`skuId-${index}`) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"}`}
                        placeholder="e.g. SKU-1125"
                      />
                      <button
                        type="button"
                        onClick={() => generateSkuForItem(index)}
                        title="Generate a random SKU"
                        aria-label="Generate a random SKU"
                        className="absolute right-1.5 top-1.5 grid h-8 w-8 cursor-pointer place-items-center rounded-md text-[#2A59BD] transition-all hover:scale-110 active:scale-[0.92] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                      >
                        <SparklesIcon className="h-3.5 w-3.5" />
                      </button>
                      {skuSearch.open && skuSearch.index === index && (
                        <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl border border-[#D6DEE9] bg-white shadow-[0_12px_32px_-12px_rgba(15,23,42,0.28)]">
                          {skuSearch.loading ? (
                            <div className="px-3 py-2.5 text-[12px] font-medium text-slate-500">Searching your catalog…</div>
                          ) : (
                            <>
                              {skuSearch.results.map((result, ri) => (
                                <button
                                  key={result.product_sku_id || ri}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => chooseSkuResult(index, result)}
                                  className={`flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${ri === skuSearch.hi ? "bg-[#EEF4FE]" : "hover:bg-[#F6F9FF]"}`}
                                >
                                  <span className="min-w-0">
                                    <span className="block truncate text-[12.5px] font-semibold text-slate-800">{result.product_name || "—"}</span>
                                    <span className="block font-mono text-[11px] text-slate-500">{result.product_sku_id}</span>
                                  </span>
                                  {result.item_pricing_value?.unit_price != null ? (
                                    <span className="flex-shrink-0 text-[12px] font-semibold tabular-nums text-slate-700">{fmtInr(result.item_pricing_value.unit_price)}</span>
                                  ) : null}
                                </button>
                              ))}
                              {!skuSearch.results.length && (
                                <div className="px-3 py-2.5 text-[12px] font-medium text-slate-500">No matching SKU in your catalog.</div>
                              )}
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openNewSkuDialog(index)}
                                className="flex w-full cursor-pointer items-center gap-1.5 border-t border-[#EDF1F7] bg-[#F6F9FF] px-3 py-2 text-left text-[12px] font-semibold text-[#2A59BD] transition-colors hover:bg-[#EEF4FE]"
                              >
                                {skuSearch.byName ? <>+ Add “{skuSearch.q}” to your catalog</> : <>+ Add “{skuSearch.q}” as a new SKU</>}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => openCatalog(index)}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2A59BD] transition-colors hover:text-[#1E44A8] focus-visible:outline-none focus-visible:underline"
                    >
                      <GridIcon className="h-3 w-3" /> Add from catalogue
                    </button>
                    <FieldError id={`err-skuId-${index}`} message={E(`skuId-${index}`)} />
                  </div>
                  <div>
                    <input
                      type="number" onKeyDown={numericKeyGuard(true)} onPaste={numericPasteGuard(true)}
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                      onFocus={(e) => e.target.select()}
                      aria-label="Quantity"
                      data-vkey={`quantity-${index}`}
                      aria-invalid={Boolean(E(`quantity-${index}`))}
                        aria-describedby={E(`quantity-${index}`) ? `err-quantity-${index}` : undefined}
                      className={inputClass(E(`quantity-${index}`), "text-right tabular-nums")}
                      placeholder="e.g. 1"
                      min="1"
                    />
                    <FieldError id={`err-quantity-${index}`} message={E(`quantity-${index}`)} />
                  </div>
                  <div>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] font-medium text-slate-400">₹</span>
                      <input
                        type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, "unitPrice", e.target.value)}
                        aria-label="Unit price"
                        data-vkey={`unitPrice-${index}`}
                        aria-invalid={Boolean(E(`unitPrice-${index}`))}
                        aria-describedby={E(`unitPrice-${index}`) ? `err-unitPrice-${index}` : undefined}
                        className={inputClass(E(`unitPrice-${index}`), "pl-7 text-right tabular-nums")}
                        placeholder="e.g. 100"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <FieldError id={`err-unitPrice-${index}`} message={E(`unitPrice-${index}`)} />
                  </div>
                  {/* row-level delete on its own column, clear of the HSN chip.
                      On the only remaining item it clears the row instead of removing it. */}
                  <div>
                    <button
                      type="button"
                      tabIndex={-1}
                      title={od.itemDetails.length > 1 ? "Remove this item" : "Clear this item"}
                      aria-label={od.itemDetails.length > 1 ? `Remove item ${index + 1}` : "Clear this item"}
                      onClick={() => removeItemDetail(index)}
                      className="mt-1.5 grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-slate-400 transition-all hover:bg-rose-50 hover:text-rose-600 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* meta band - one level floor: HSN + fragile left, line total on the money axis */}
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="flex flex-wrap items-center gap-2">
                    {expandedHSNItems[index] ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">HSN</span>
                        <input
                          type="text"
                          value={item.hsnCode || ""}
                          onChange={(e) => handleItemChange(index, "hsnCode", e.target.value)}
                          onBlur={(e) => handleItemChange(index, "hsnCode", e.target.value.trim())}
                          aria-label="HSN code"
                          className="h-7 w-44 rounded-lg border border-[#D6DEE9] bg-white px-2.5 font-mono text-[11.5px] font-medium text-slate-900 outline-none transition-all placeholder:text-slate-400 hover:border-[#AFBDD1] focus:border-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/10"
                          placeholder="e.g. 610910"
                        />
                        <button
                          type="button"
                          aria-label="Hide HSN code"
                          title="Hide HSN code"
                          onClick={() => setExpandedHSNItems(prev2 => ({ ...prev2, [index]: false }))}
                          className="grid h-6 w-6 cursor-pointer place-items-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setExpandedHSNItems(prev2 => ({ ...prev2, [index]: !prev2[index] }))}
                        className={`inline-flex h-6 cursor-pointer items-center rounded-md border px-2 text-[10.5px] font-semibold transition-colors ${item.hsnCode ? "border-solid border-[#D6DEE9] bg-white text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:border-[#AFBDD1] hover:text-[#2A59BD]" : "border-dashed border-[#3877FC]/35 bg-[#3877FC]/[0.04] text-[#2A59BD] hover:border-[#3877FC]/60 hover:bg-[#3877FC]/[0.08]"}`}
                      >
                        {item.hsnCode ? `HSN ${item.hsnCode}` : (<>+ HSN&nbsp;<span className="font-medium text-[#2A59BD]/55">· optional</span></>)}
                      </button>
                    )}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(item.isFragile)}
                      aria-label={`Mark item ${index + 1} as fragile`}
                      title="Mark as fragile — special handling"
                      onClick={() => handleItemChange(index, "isFragile", !item.isFragile)}
                      className={`inline-flex h-6 cursor-pointer items-center gap-2 rounded-md border pl-2 pr-1.5 text-[10.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${item.isFragile ? "border-solid border-amber-300/80 bg-amber-50 text-amber-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)] hover:border-amber-400" : "border-dashed border-[#B9C6DA] bg-white text-slate-600 hover:border-[#AFBDD1] hover:text-slate-800"}`}
                    >
                      Fragile
                      <span
                        aria-hidden="true"
                        className={`relative h-3.5 w-6 shrink-0 rounded-full transition-colors ${item.isFragile ? "bg-amber-500" : "bg-slate-300"}`}
                      >
                        <span
                          className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow transition-[left] duration-150 ${item.isFragile ? "left-[12px]" : "left-0.5"}`}
                        />
                      </span>
                    </button>
                  </span>
                  {(Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) > 0 ? (
                    <span className="flex items-center gap-1.5 pr-3.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Line total</span>
                      <span className="flex flex-col items-end leading-tight">
                        <span className="text-[12.5px] font-semibold tabular-nums text-slate-700">{fmtInr((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}</span>
                        <span className="text-[9px] font-medium text-slate-400">(incl. GST)</span>
                      </span>
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {/* ghost row - the affordance shaped like what it creates */}
          <div className="border-t border-[#E7ECF3] bg-[#F6F9FF] px-3 py-2">
            <button
              type="button"
              onClick={() => {
                const nextIndex = od.itemDetails.length;
                addItemDetail();
                requestAnimationFrame(() => document.querySelector(`[data-vkey="name-${nextIndex}"]`)?.focus());
              }}
              className="flex h-9 w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-[#B9C6DA] bg-white text-[12.5px] font-semibold text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:border-[#3877FC]/50 hover:text-[#2A59BD] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
            >
              + Add another item
            </button>
          </div>
          {/* subtotal foot - always on; whispers at zero, speaks once real.
              GST note rides the total itself so it can't be missed. */}
          <div className="flex items-center justify-end gap-2 border-t border-[#E7ECF3] bg-[#3877FC]/[0.04] px-3 py-2.5 pr-[26px]">
            <span className="text-[13px] text-slate-500">Items subtotal</span>
            <span className="flex flex-col items-end leading-tight">
              <b className={`tabular-nums ${itemsSubtotal > 0 ? "text-[15.5px] font-semibold text-slate-900" : "text-[13px] font-medium text-slate-600"}`}>{fmtInr(itemsSubtotal)}</b>
              {itemsSubtotal > 0 ? <span className="text-[10px] font-medium text-slate-400">(incl. GST)</span> : null}
            </span>
          </div>
          </div>
        </div>
      </div>
      {invoiceMismatch && (
        <p className="rounded-lg border border-amber-200/70 bg-amber-50 px-3.5 py-2.5 text-[12.5px] font-medium leading-relaxed text-amber-800">
          Items add up to {fmtInr(itemsSubtotal)}, but the invoice value is {fmtInr(od.invoiceValue)}. Both are sent as entered.
        </p>
      )}
      </JourneyGroup>
    </div>
  );

  const dimFields = [
    { key: "weight", label: "Dead weight", placeholder: "e.g. 500", options: ["gm", "kg"] },
    { key: "length", label: "Length", placeholder: "e.g. 30", options: ["cm", "inch"] },
    { key: "breadth", label: "Breadth", placeholder: "e.g. 20", options: ["cm", "inch"] },
    { key: "height", label: "Height", placeholder: "e.g. 10", options: ["cm", "inch"] },
  ];

  const renderPackage = () => (
    <div className="cfo-stagger space-y-4">
      <JourneyGroup icon={BoxIcon} title="Weight and size" hint="Packed weight on the scale, outer dimensions of the box." done={weightDone} last>
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 lg:grid-cols-4">
        {dimFields.map(({ key, label, placeholder, options }) => (
          <div key={key}>
            <label className={LABEL}>{label}</label>
            <div className={`flex h-11 items-stretch overflow-hidden rounded-xl border bg-white transition-[border-color,box-shadow] duration-150 focus-within:border-[#3877FC] focus-within:shadow-[0_0_0_4px_rgba(56,119,252,0.12)] ${
              E(key) ? "border-rose-300 bg-rose-50/40" : "border-[#D6DEE9] hover:border-[#AFBDD1]"
            }`}>
              <input
                type="number" onKeyDown={numericKeyGuard()} onPaste={numericPasteGuard()}
                placeholder={placeholder}
                value={pd[key]}
                onChange={(e) => handlePackageValueChange(key, e.target.value)}
                aria-label={label}
                data-vkey={key}
                aria-invalid={Boolean(E(key))}
                  aria-describedby={E(key) ? `err-${key}` : undefined}
                className="min-w-0 flex-1 bg-transparent px-3.5 text-[14px] font-semibold tabular-nums text-slate-900 placeholder:font-medium placeholder:text-slate-500 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <select
                value={units[key]}
                onChange={(e) => handlePackageUnitChange(key, e.target.value)}
                aria-label={`${label} unit`}
                className="cursor-pointer border-l border-slate-200/80 bg-transparent px-2.5 text-[12px] font-medium text-slate-500 focus:outline-none"
              >
                {options.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <FieldError id={`err-${key}`} message={E(key)} />
          </div>
        ))}
      </div>

      {/* The studio unfolds: its height eases open (and closed) so the card grows smoothly
          instead of jumping to size — the same in the stepper and the stacked layout. */}
      <AnimatePresence initial={false}>
      {(pd.weight || pd.length || pd.breadth || pd.height) && (
        <motion.div
          key="package-studio"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ height: { type: "spring", stiffness: 150, damping: 26, mass: 1 }, opacity: { duration: 0.34, ease: "easeOut" } }}
          className="overflow-clip"
        >
          <PackageStudio
            lCm={convertLength(pd.length, units.length)}
            bCm={convertLength(pd.breadth, units.breadth)}
            hCm={convertLength(pd.height, units.height)}
            l={Number(pd.length) || 0} b={Number(pd.breadth) || 0} h={Number(pd.height) || 0}
            unitL={units.length} unitB={units.breadth} unitH={units.height}
            weightDisp={Number(pd.weight) || 0} unitW={units.weight}
            deadKg={deadKg} volKg={volKg} billedBasis={billedBasis} billedKg={billedKg}
          />
        </motion.div>
      )}
      </AnimatePresence>
      </JourneyGroup>
    </div>
  );

  const renderCourier = () => (
    <div className="cfo-stagger space-y-4">
      <SmartAllocationEngine
        on={formData.courierDetails.useRecommendation}
        onToggle={() => handleCourierSelection(!formData.courierDetails.useRecommendation)}
      />

      <AnimatePresence initial={false}>
        {!formData.courierDetails.useRecommendation && (
          <motion.div
            key="chooser"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ height: SPRING, opacity: { duration: 0.16 } }}
            className="overflow-clip"
          >
            <div className="pt-4">
              <JourneyGroup
                icon={TruckIcon}
                title="Choose a courier"
                done={Boolean(formData.courierDetails.courierPartner)}
                last
              >
              {loadingCouriers ? (
                <div className="space-y-2.5" role="status" aria-label="Getting courier rates">
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-3.5 rounded-2xl border border-[#EDF1F7] p-3.5">
                      <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-xl bg-slate-100" />
                      <div className="flex-1 space-y-2"><div className="h-3.5 w-1/3 animate-pulse rounded bg-slate-100" /><div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" /></div>
                      <div className="h-4 w-16 animate-pulse rounded bg-slate-100" />
                    </div>
                  ))}
                  <p className="pt-0.5 text-[13px] font-medium text-slate-500">Fetching live rates for this route…</p>
                </div>
              ) : sortedOptions.length > 0 ? (
                <>
                  {/* Toolbar: how many, where to, and how to sort */}
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <p className="text-[12.5px] text-slate-500">
                      <b className="font-semibold text-slate-800">{serviceableOptions.length}</b> {serviceableOptions.length === 1 ? "courier" : "couriers"} available
                      {courierRoute && <> · <span className="font-medium text-slate-600 tabular-nums">{courierRoute}</span></>}
                    </p>
                    {serviceableOptions.length > 1 && (
                      <div className="inline-flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 text-[12px] font-semibold" role="tablist" aria-label="Sort couriers">
                        {[["cheapest", "Cheapest"], ["fastest", "Fastest"]].map(([k, label]) => (
                          <button key={k} type="button" role="tab" aria-selected={courierSort === k} onClick={() => setCourierSort(k)}
                            className={`rounded-md px-2.5 py-1 transition-colors ${courierSort === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2.5" role="radiogroup" aria-label="Courier">
                    <AnimatePresence initial={false} mode="popLayout">
                    {(serviceableDisplay.length ? [...serviceableDisplay, ...(showUnserviceable ? unserviceableOptions : [])] : sortedOptions).map((option) => {
                      const isServiceable = serviceableOf(option);
                      const isSelected = formData.courierDetails.courierPartner === option.carrier_partner_public_name;
                      const name = option.carrier_partner_public_name;
                      const { carrier, mode } = formatCarrier(name);
                      const ModeIcon = mode ? MODE_ICON[mode] : null;
                      const accent = carrierAccent(carrier); // per-carrier, so Delhivery Air & Surface share a colour
                      const isCheapest = isServiceable && cheapestPrice != null && Math.abs(Number(option.total_amount_charged_after_tax) - cheapestPrice) < 0.005;
                      const isFastest = isServiceable && fastestTat != null && Number(option.max_tat_in_days) === fastestTat;
                      const price = Number(option.total_amount_charged_after_tax);
                      const date = estDeliveryDate(option.max_tat_in_days);
                      return (
                        <motion.button
                          key={name}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ type: "spring", stiffness: 380, damping: 32 }}
                          whileTap={isServiceable ? { scale: 0.99 } : undefined}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          aria-disabled={!isServiceable}
                          onClick={() => { if (isServiceable) setFormData((prev) => ({ ...prev, courierDetails: { ...prev.courierDetails, courierPartner: name } })); }}
                          className={`relative flex w-full items-center gap-3.5 rounded-2xl border p-3.5 text-left transition-[border-color,box-shadow,background-color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${
                            isSelected
                              ? "border-[#3877FC] bg-white shadow-[0_4px_14px_-8px_rgba(15,23,42,0.25)]"
                              : isServiceable
                                ? "border-[#E7ECF3] bg-white hover:border-[#C4D2E8] hover:shadow-[0_8px_20px_-12px_rgba(15,23,42,0.28)]"
                                : "cursor-default border-[#EDF1F7] bg-slate-50/40"
                          }`}
                        >
                          {/* Selected: a springy accent spine anchors the choice */}
                          <AnimatePresence>{isSelected && (
                            <motion.span
                              key="spine"
                              initial={{ opacity: 0, scaleY: 0.35 }}
                              animate={{ opacity: 1, scaleY: 1 }}
                              exit={{ opacity: 0, scaleY: 0.35 }}
                              transition={{ type: "spring", stiffness: 420, damping: 30 }}
                              className="absolute left-0 inset-y-0 my-auto h-7 w-[3px] rounded-r-full bg-[#3877FC]"
                            />
                          )}</AnimatePresence>

                          <span className={`grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl text-[14px] font-extrabold ${!isServiceable ? "opacity-45 grayscale" : ""}`}
                            style={{ backgroundImage: `radial-gradient(circle at 30% 22%, ${accent}33, transparent 62%), linear-gradient(150deg, ${accent}1F, ${accent}0A)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}2E, 0 1px 2px rgba(15,23,42,0.06)` }}>
                            {initialsOf(carrier)}
                          </span>

                          {/* Name + mode, then ranking + ETA — two tight lines */}
                          <span className={`min-w-0 flex-1 ${!isServiceable ? "opacity-60" : ""}`}>
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="whitespace-nowrap text-[14.5px] font-bold text-slate-900">{carrier}</span>
                              {mode && (
                                <span className={`inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${MODE_STYLE[mode] || "bg-slate-100 text-slate-500 ring-slate-200/70"}`}>
                                  {ModeIcon && <ModeIcon className="h-3 w-3" strokeWidth={2.25} />}
                                  {mode}
                                </span>
                              )}
                              {isCheapest && (
                                <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-gradient-to-b from-emerald-500 to-emerald-600 px-2 py-[3px] text-[10px] font-bold uppercase tracking-wide text-white shadow-[0_1px_2px_rgba(5,150,105,0.45)]"><TagIcon className="h-3 w-3" strokeWidth={2.5} /> Cheapest</span>
                              )}
                              {isFastest && (
                                <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-gradient-to-b from-[#3877FC] to-[#2A59BD] px-2 py-[3px] text-[10px] font-bold uppercase tracking-wide text-white shadow-[0_1px_2px_rgba(56,119,252,0.45)]"><ZapIcon className="h-3 w-3" fill="currentColor" strokeWidth={0} /> Fastest</span>
                              )}
                            </span>
                            <span className="mt-0.5 block text-[12.5px]">
                              {isServiceable ? (
                                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-500">
                                  <ClockIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                                  {date
                                    ? <span>By <b className="font-semibold text-slate-700">{date}</b><span className="hidden sm:inline"> · {option.max_tat_in_days} day{option.max_tat_in_days === 1 ? "" : "s"}</span></span>
                                    : <span>Up to {option.max_tat_in_days} day{option.max_tat_in_days === 1 ? "" : "s"} from pickup</span>}
                                </span>
                              ) : (
                                <span className="font-medium text-rose-500">Not serviceable for {isCod ? "COD" : "prepaid"} on this route</span>
                              )}
                            </span>
                          </span>

                          {/* Price + selector, vertically centred against the block */}
                          <span className="flex flex-shrink-0 items-center gap-3">
                            <span className="text-right">
                              <span className={`block text-[17px] font-bold leading-tight tabular-nums transition-colors ${isServiceable ? (isSelected ? "text-[#2A59BD]" : "text-slate-900") : "text-slate-400"}`}>{fmtInr(price)}</span>
                              <span className="mt-0.5 block whitespace-nowrap text-[10.5px] font-medium text-slate-400">incl. GST</span>
                            </span>
                            <span className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-full border-2 transition-colors ${isSelected ? "border-[#3877FC] bg-[#3877FC]" : isServiceable ? "border-slate-300" : "border-transparent"}`}>
                              <AnimatePresence>{isSelected && (
                                <motion.span key="ck" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}>
                                  <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3.5} />
                                </motion.span>
                              )}</AnimatePresence>
                            </span>
                          </span>
                        </motion.button>
                      );
                    })}
                    </AnimatePresence>
                  </div>

                  {serviceableOptions.length > 0 && unserviceableOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowUnserviceable(v => !v)}
                      className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-slate-400 transition-colors hover:text-slate-600"
                    >
                      {showUnserviceable ? "Hide" : "Show"} {unserviceableOptions.length} not serviceable on this route
                      <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${showUnserviceable ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center gap-1.5 rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-100 text-slate-400"><TruckIcon className="h-6 w-6" /></span>
                  <p className="mt-2 text-[14.5px] font-semibold text-slate-800">No courier rates for this route</p>
                  <p className="text-[13px] text-slate-500">Check both pincodes and the package, or switch to smart allocation.</p>
                </div>
              )}
              </JourneyGroup>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const STEP_META = [
    { key: "customerDetails", title: "Recipient", sub: "Who is this going to?", body: renderRecipient },
    { key: "pickupDetails", title: "Pickup", sub: "Where does it leave from?", body: renderPickup },
    { key: "orderDetails", title: "Payment & items", sub: "How is it paid and what's inside?", body: renderPayment },
    { key: "packageDetails", title: "Package", sub: "How big and heavy is the package?", body: renderPackage },
    { key: "courierDetails", title: "Courier", sub: "Who should carry it?", body: renderCourier },
  ];
  // Forward reads as travel, Back as return - the slide direction follows the journey.
  const prevStepRef = useRef(step);
  const stepDir = step >= prevStepRef.current ? 1 : -1;
  useEffect(() => { prevStepRef.current = step; }, [step]);
  const courierDone = formData.courierDetails.useRecommendation || Boolean(formData.courierDetails.courierPartner);
  const STEP_GROUPS = [
    [contactDone, addressDone],
    [pickupDone],
    [paymentDone, orderDone, itemsDone],
    [weightDone],
    [courierDone],
  ];
  const groupsOfStep = STEP_GROUPS[step - 1] || [];
  const groupsDone = groupsOfStep.filter(Boolean).length;
  const stepReady = groupsOfStep.length > 0 && groupsDone === groupsOfStep.length;
  const RING_C = 2 * Math.PI * 13;
  const FormBody = step <= 4 ? "form" : "div";
  // One header for a section, used by both layouts: icon, title, subtitle, and the live ring.
  const SECTION_ICON = { customerDetails: UserIcon, pickupDetails: StoreIcon, orderDetails: RupeeIcon, packageDetails: BoxIcon, courierDetails: TruckIcon };
  const renderSectionHeader = (n) => {
    const sm = STEP_META[n - 1];
    const Icon = SECTION_ICON[sm.key] || UserIcon;
    const groups = STEP_GROUPS[n - 1] || [];
    const done = groups.filter(Boolean).length;
    const complete = groups.length > 0 && done === groups.length;
    const title = !stacked && n < highestStepReached ? `Editing · ${sm.title}` : sm.title;
    const showRing = !(stacked && n > unlockedUpTo);
    return (
      <div className="mb-4 flex items-center gap-3">
        <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-2xl bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_15px_-3px_rgba(56,119,252,0.25),0_4px_6px_-4px_rgba(56,119,252,0.25)]">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-900">{title}</h2>
          <p className="mt-0.5 text-[13px] text-slate-500">{sm.sub}</p>
        </div>
        {/* the section's own pulse: groups completed, live */}
        {showRing && <span className="relative ml-auto grid h-[38px] w-[38px] flex-shrink-0 place-items-center" title={`${done} of ${groups.length} complete`}>
          <svg width="38" height="38" viewBox="0 0 38 38" className="-rotate-90">
            <circle cx="19" cy="19" r="13" fill="none" stroke="#E2E8F0" strokeWidth="3.5" />
            <motion.circle
              cx="19" cy="19" r="13" fill="none"
              stroke={complete ? "#10B981" : "#3877FC"}
              strokeWidth="3.5" strokeLinecap="round"
              strokeDasharray={RING_C}
              initial={false}
              animate={{ strokeDashoffset: RING_C * (1 - (groups.length ? done / groups.length : 0)) }}
              transition={{ type: "spring", stiffness: 90, damping: 20, delay: 0.14 }}
            />
          </svg>
          <span className={`absolute flex items-baseline overflow-hidden text-[10px] font-semibold tabular-nums ${complete ? "text-emerald-600" : "text-slate-500"}`}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={done} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -8, opacity: 0 }} transition={{ duration: 0.2, ease: "easeOut" }}>
                {done}
              </motion.span>
            </AnimatePresence>
            /{groups.length}
          </span>
        </span>}
      </div>
    );
  };

  /* ── The page ── */
  return (
    <MotionConfig reducedMotion="user">
      <div
        className="relative min-h-full bg-[#F6F8FB]"
        style={{
          backgroundImage: "radial-gradient(1100px 440px at 55% -160px, rgba(56,119,252,0.06), transparent 70%), radial-gradient(900px 420px at 6% 110%, rgba(56,119,252,0.035), transparent 70%)",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      >
        {/* cartographic dot grid, top band only - designed air, never inside a card */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(rgba(30,58,109,0.05) 1px, transparent 1.4px)",
            backgroundSize: "22px 22px",
            maskImage: "linear-gradient(to bottom, black, transparent 400px)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent 400px)",
          }}
        />
        <div className="relative mx-auto w-full max-w-[1440px] px-5 pb-4 pt-3 lg:px-8 antialiased">
          {booked ? (
            /* ───────────── Booked: the celebration takes the desk ───────────── */
            <BookedSuccess
              bookedAt={bookedAt}
              origin={{ city: selectedPickup?.pickup_city, pin: selectedPickup?.pickup_postal_code }}
              destination={{ city: cd.city, pin: cd.pincode }}
              courier={bookedInfo}
              charge={bookedInfo?.charge ?? null}
              settlement={settlementLabel}
              billedKg={billedKg}
              billedBasis={billedBasis}
              order={{
                recipientName: cd.name,
                recipientPhone: cd.phoneNumber,
                paymentLabel: isCod ? (isPartialCod ? "Partial COD" : "COD") : "Prepaid",
                paymentValue: isCod && od.codAmount ? fmtInr(od.codAmount) : (parseFloat(od.invoiceValue) > 0 ? fmtInr(od.invoiceValue) : null),
                itemsCount: namedItems,
                orderRef: responseData.order_reference_number,
                waybill: responseData.waybill,
              }}
              labelUrl={responseData.label_url}
              invoiceUrl={responseData.commercial_invoice_url}
              awbCopied={awbCopied}
              onCopyAwb={() => {
                navigator.clipboard?.writeText(String(responseData.waybill));
                setAwbCopied(true);
                window.setTimeout(() => setAwbCopied(false), 1500);
              }}
              onViewDetails={() => responseData.order_reference_number && navigate(`/shipment-details/${encodeURIComponent(responseData.order_reference_number)}`)}
              onAllShipments={() => navigate("/all-shipments")}
              onBookAnother={resetForm}
            />
          ) : (
            <div className="gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
              {/* ── The desk: quiet, fast form ── */}
              {/* Keyed by layout mode: switching Steps ⇄ Stacked remounts the desk, so the
                  layout animation never tweens between the two very different heights
                  (that read as the whole form stretching). Within a mode, `layout` still
                  eases step-to-step height changes. */}
              <motion.div
                key={layoutMode}
                ref={flowRef}
                layout
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`${stacked ? "" : CARD} scroll-mt-[76px]`}
                onBlurCapture={handleFieldBlur}
              >
                {/* Slim section pills — every section named upfront (stepper only) */}
                {!stacked && (
                <div ref={stepRowRef} className="cfo-thin-scroll sticky top-16 z-30 flex w-full items-center justify-between gap-1 overflow-x-auto rounded-t-2xl border-b border-slate-100 bg-white/95 px-5 py-2 backdrop-blur lg:px-7" aria-label="Sections">
                  {STEP_META.map((sm, index) => {
                    const n = index + 1;
                    const isActive = n === step;
                    const done = !isActive && n <= highestStepReached;
                    const reachable = n <= highestStepReached;
                    return (
                      <React.Fragment key={sm.key}>
                      <button
                        type="button"
                        data-step={n}
                        onClick={() => handleStepClick(n)}
                        aria-disabled={!reachable}
                        aria-current={isActive ? "step" : undefined}
                        className={`flex flex-shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40 ${
                          isActive
                            ? "bg-[#3877FC] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_2px_8px_-2px_rgba(56,119,252,0.45)]"
                            : done
                              ? "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                              : "text-slate-500"
                        } ${reachable && !isActive ? "" : "cursor-default"}`}
                      >
                        {done ? (
                          <motion.span
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30, delay: 0.09 }}
                            className="grid h-4 w-4 place-items-center rounded-full bg-emerald-500 text-white"
                          >
                            <CheckIcon className="h-2.5 w-2.5" strokeWidth={4} />
                          </motion.span>
                        ) : (
                          <span className={`grid h-4 w-4 place-items-center rounded-full text-[10px] font-semibold ${
                            isActive ? "bg-white/20 text-white" : "border border-slate-400/70 text-slate-600"
                          }`}>
                            {n}
                          </span>
                        )}
                        {sm.title}
                      </button>
                      {n < STEP_META.length && (
                        <span
                          aria-hidden="true"
                          className={`h-px min-w-[10px] flex-1 rounded-full transition-colors duration-300 ${
                            n < highestStepReached ? "bg-emerald-300" : "bg-slate-200"
                          }`}
                        />
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
                )}

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center" role="status" aria-busy="true">
                    <div className="relative h-12 w-12">
                      <div className="absolute inset-0 animate-spin rounded-full border-2 border-slate-200 border-t-[#3877FC]" />
                      <div className="absolute inset-0 flex items-center justify-center text-[#3877FC]">
                        <PackageIcon className="h-4 w-4" />
                      </div>
                    </div>
                    <p className="mt-4 text-[15.5px] font-semibold text-slate-900">Booking your shipment…</p>
                    <p className="mt-1 text-[13px] text-slate-500">Allocating the courier and generating the AWB.</p>
                  </div>
                ) : (
                  <FormBody
                    noValidate={step <= 4 ? true : undefined}
                    onSubmit={step <= 4 ? (e) => { e.preventDefault(); handleNext(); } : undefined}
                  >
                    <div ref={stepBodyRef} className={stacked ? "" : "px-5 pb-5 pt-3 lg:px-7"}>
                      {stacked ? (
                        /* Stacked layout: every section is its own card, threaded on a numbered rail:
                           (01) — card — | — (02) — card — … */
                        <div>
                          {STEP_META.map((sm, i) => {
                            const n = i + 1;
                            const groups = STEP_GROUPS[i] || [];
                            const complete = groups.length > 0 && groups.every(Boolean);
                            const unlocked = n <= unlockedUpTo;
                            const current = n === unlockedUpTo;
                            return (
                              <React.Fragment key={sm.key}>
                                {/* rail: connector from the previous card, then the section number */}
                                <div className="flex flex-col items-center" aria-hidden="true">
                                  {n > 1 && <span className={`h-7 w-px ${n <= unlockedUpTo ? "bg-emerald-300" : "bg-[#D8DFEA]"}`} />}
                                  <span className={`my-2 grid h-8 min-w-[2rem] place-items-center rounded-full px-2 font-mono text-[12px] font-bold tabular-nums ring-4 ring-[#F6F8FB] ${
                                    complete && unlocked ? "bg-emerald-500 text-white"
                                      : current ? "bg-[#3877FC] text-white shadow-[0_2px_8px_-2px_rgba(56,119,252,0.5)]"
                                      : unlocked ? "bg-white text-slate-600 shadow-[inset_0_0_0_1.5px_#C9D3E0]"
                                      : "bg-white text-slate-400 shadow-[inset_0_0_0_1.5px_#E2E8F0]"
                                  }`}>
                                    {complete && unlocked ? <CheckIcon className="h-4 w-4" strokeWidth={3.5} /> : String(n).padStart(2, "0")}
                                  </span>
                                </div>
                                <section
                                  data-section={n}
                                  data-locked={unlocked ? undefined : "true"}
                                  ref={(el) => { if (el) { if (unlocked) el.removeAttribute("inert"); else el.setAttribute("inert", ""); } }}
                                  className={`${CARD} relative scroll-mt-[88px] px-5 pb-5 pt-4 transition-opacity duration-300 lg:px-7 ${unlocked ? "" : "pointer-events-none select-none opacity-55"}`}
                                >
                                  {!unlocked && (
                                    <span className="absolute right-5 top-4 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#E4E9F1] bg-[#F6F8FB] px-2.5 py-1 text-[11px] font-semibold text-slate-500 lg:right-7">
                                      <LockIcon className="h-3 w-3" /> Complete {STEP_META[unlockedUpTo - 1].title} first
                                    </span>
                                  )}
                                  {renderSectionHeader(n)}
                                  {sm.body()}
                                </section>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      ) : (
                        <AnimatePresence mode="wait" initial={false}>
                          <motion.div
                            key={step}
                            initial={{ opacity: 0, x: stepDir * 32 }}
                            animate={{ opacity: 1, x: 0, transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1], delay: 0.07 } }}
                            exit={{ opacity: 0, x: stepDir * -24, transition: { duration: 0.16, ease: [0.4, 0, 1, 1] } }}
                          >
                            {renderSectionHeader(step)}
                            {STEP_META[step - 1].body()}
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </div>

                    <div ref={footerRef} className={stacked
                      ? `${CARD} mt-7 px-5 py-3 lg:px-7`
                      : "sticky bottom-0 rounded-b-[inherit] border-t border-[#EDF1F7] bg-[#FAFBFD]/95 px-5 py-3 backdrop-blur lg:px-7"}>
                      <AnimatePresence>
                        {moreBelow && !stacked && (
                          <motion.button
                            type="button"
                            onClick={scrollForMore}
                            aria-label="Scroll down for more"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.2 }}
                            className="absolute -top-[42px] left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-[#E2EAF8] bg-white/95 py-1.5 pl-3 pr-2.5 text-[11px] font-semibold text-[#3877FC] shadow-[0_6px_18px_-6px_rgba(15,23,42,0.28)] backdrop-blur transition-colors hover:bg-white"
                          >
                            More below
                            <motion.span animate={{ y: [0, 2.5, 0] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}>
                              <ChevronDownIcon className="h-3.5 w-3.5" strokeWidth={2.75} />
                            </motion.span>
                          </motion.button>
                        )}
                      </AnimatePresence>
                      <div className="flex flex-wrap items-center gap-3">
                        {step > 1 && !stacked && (
                          <button type="button" onClick={handleBack} className={BTN_GHOST}>
                            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" /> Back
                          </button>
                        )}
                        {liveErrCount > 0 ? (
                          <span className="text-[12.5px] font-semibold text-rose-600" role="alert">
                            {liveErrCount} field{liveErrCount === 1 ? "" : "s"} need{liveErrCount === 1 ? "s" : ""} attention
                          </span>
                        ) : stacked ? (
                          <span className="hidden text-[12px] text-slate-500 md:inline">{unlockedUpTo < 5 ? `Complete ${STEP_META[unlockedUpTo - 1].title} to unlock the next section` : "Every section is open — book when ready"}</span>
                        ) : step <= 4 ? (
                          <span className="hidden items-center gap-1.5 text-[12px] text-slate-500 md:flex">
                            <kbd className="rounded-md border-b-2 border-[#C9D3E0] bg-white px-1.5 py-0.5 font-sans text-[11px] font-semibold text-slate-500 ring-1 ring-[#D8DFEA]">↵ Enter</kbd>
                            to continue
                          </span>
                        ) : null}
                        {/* Phones: this group takes the full width and Book/Continue stretches; desktop: right-aligned */}
                        <div className="order-first flex w-full flex-shrink-0 items-center gap-2.5 sm:order-none sm:ml-auto sm:w-auto">
                          {/* Saving a draft doesn't depend on where you are: always on the last step,
                              and on every step while editing an existing draft (Steps mode lands a
                              loaded draft on its first incomplete section). */}
                          {(step === 5 || Boolean(draftOrderId)) && (
                            <button
                              type="button"
                              onClick={handleSaveAsDraft}
                              disabled={loading || savingDraft}
                              className={BTN_OUTLINE}
                            >
                              {savingDraft && <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />}
                              {savingDraft ? "Saving…" : draftOrderId ? "Update draft" : "Save as draft"}
                            </button>
                          )}
                          {step === 5 ? (
                            <button type="button" onClick={stacked ? handleBookStacked : handleSubmit} disabled={loading || savingDraft || (stacked && unlockedUpTo < 5)} title={stacked && unlockedUpTo < 5 ? `Complete ${STEP_META[unlockedUpTo - 1].title} first` : undefined} className={`${BTN_PRIMARY} flex-1 justify-center sm:flex-none ${stepReady ? "cfo-cta-ready" : ""}`}>
                              Book shipment <CheckIcon className="h-4 w-4" strokeWidth={3} />
                            </button>
                          ) : (
                            <button type={step <= 4 ? "submit" : "button"} onClick={step <= 4 ? undefined : handleNext} className={`${BTN_PRIMARY} flex-1 justify-center sm:flex-none ${stepReady ? "cfo-cta-ready" : ""}`}>
                              Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </FormBody>
                )}
              </motion.div>

              {/* ── The ticket: the shipment, becoming real ── */}
              <motion.aside
                initial={{ x: 16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
                className="mt-6 lg:sticky lg:top-20 lg:mt-0"
              >
                <ShipmentTicket
                  pct={pct}
                  step={step}
                  stageLabel={stacked ? `${Math.min(reached - 1, 5)} of 5 done` : undefined}
                  origin={originKnown ? { city: selectedPickup.pickup_city, pin: selectedPickup.pickup_postal_code } : null}
                  destination={dropKnown ? { city: cd.city, pin: cd.pincode } : null}
                  rows={ticketRows}
                  cells={ticketCells}
                  orderRef={String(od.clientOrderId || "").trim() || null}
                  charge={chargeValue}
                  settlement={settlementLabel}
                />
                {/* Form layout preference — how the merchant likes to fill the form; remembered for a year */}
                <div className={`${CARD} relative z-10 mt-7 p-4`}>
                  <div className="text-[13px] font-semibold text-slate-900">Form layout</div>
                  <div className="mt-0.5 text-[12px] text-slate-500">How you like to fill the form. Saved for next time.</div>
                  <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Form layout">
                    {[
                      { id: "horizontal", label: "Steps", hint: "One section at a time", Glyph: LayoutStepsGlyph },
                      { id: "vertical", label: "Stacked", hint: "Sections build up on one page", Glyph: LayoutStackGlyph },
                    ].map((o) => {
                      const on = layoutMode === o.id;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => chooseLayout(o.id)}
                          className={`flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${on ? "border-[#3877FC] bg-[#3877FC]/[0.05]" : "border-[#E4E9F1] bg-white hover:border-[#C4D2E8]"}`}
                        >
                          <o.Glyph active={on} />
                          <span className={`text-[12.5px] font-semibold ${on ? "text-[#2A59BD]" : "text-slate-800"}`}>{o.label}</span>
                          <span className="text-[11px] leading-snug text-slate-500">{o.hint}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.aside>
            </div>
          )}

          {/* SKU catalogue browser: browse/search every SKU, multi-select, and add as items */}
          {catalog.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" onClick={closeCatalog}>
              <div
                className="relative flex h-[88vh] max-h-[880px] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#E4E9F1] bg-white shadow-2xl animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Escape") { if (catalogArchive) { if (!archivingSku) setCatalogArchive(null); } else { closeCatalog(); } } e.stopPropagation(); }}
              >
                {catalog.view === "create" ? (
                  <>
                    {/* Create view — add a new SKU without leaving the catalogue */}
                    <div className="flex items-center gap-3 border-b border-slate-100 px-6 pb-4 pt-5">
                      <button type="button" onClick={cancelCatalogCreate} aria-label="Back to catalogue" className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      <div className="flex-1">
                        <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-900">{newSkuDialog?.editing ? "Edit SKU" : "Add a new SKU"}</h3>
                        <p className="mt-0.5 text-[12.5px] text-slate-500">{newSkuDialog?.editing ? "Changes save to your catalogue for future orders." : "It saves to your catalogue and gets selected here."}</p>
                      </div>
                      <button type="button" onClick={closeCatalog} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="custom-scrollbar flex-1 space-y-3.5 overflow-y-auto px-6 py-5">
                      {renderSkuFormFields()}
                    </div>
                    <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-[#F6F9FF] px-6 py-3.5">
                      <button type="button" onClick={cancelCatalogCreate} disabled={savingNewSku} className="inline-flex h-9 cursor-pointer items-center rounded-lg bg-white px-4 text-[13px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9] disabled:opacity-60">Back</button>
                      <button type="button" onClick={saveNewSku} disabled={savingNewSku} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#3877FC] px-4 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:opacity-60">
                        {savingNewSku && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                        {savingNewSku ? (newSkuDialog?.editing ? "Saving…" : "Adding…") : (newSkuDialog?.editing ? "Save changes" : "Add SKU")}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 pb-4 pt-5">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#3877FC] text-white shadow-[0_6px_16px_-6px_rgba(56,119,252,0.6)]">
                      <GridIcon className="h-[18px] w-[18px]" />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-900">Your product catalogue</h3>
                      <p className="mt-0.5 text-[12.5px] text-slate-500">Tick the products you want, then add them to your order.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(catalog.activeTotal + catalog.archivedTotal) > 0 && (
                      <span className="rounded-full bg-[#3877FC]/[0.08] px-2.5 py-1 text-[11px] font-bold tabular-nums text-[#2A59BD]">
                        {catalog.activeTotal} active · {catalog.archivedTotal} archived
                      </span>
                    )}
                    <button type="button" onClick={closeCatalog} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Search band */}
                <div className="border-b border-slate-100 bg-[#F6F9FF] px-6 py-4">
                  <div className="relative">
                    <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      autoFocus
                      value={catalog.q}
                      onChange={(e) => onCatalogSearchChange(e.target.value)}
                      placeholder="Search by product name or SKU…"
                      className="w-full rounded-xl border border-[#D6DEE9] bg-white py-3 pl-11 pr-3 text-[13.5px] font-medium text-slate-900 shadow-sm outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-slate-400 hover:border-[#AFBDD1] focus:border-[#3877FC] focus:shadow-[0_0_0_4px_rgba(56,119,252,0.12)]"
                    />
                  </div>
                  <div className="mt-2.5 flex items-center justify-end">
                    <label className="inline-flex cursor-pointer select-none items-center gap-2 text-[12px] font-medium text-slate-500 transition-colors hover:text-slate-700">
                      <input type="checkbox" checked={catalog.showArchived} onChange={toggleShowArchived} className="h-[15px] w-[15px] cursor-pointer rounded-[4px] border-[#C7D2E4] text-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/30 focus:ring-offset-0" />
                      Show archived
                    </label>
                  </div>
                </div>

                {/* List */}
                <div
                  className="custom-scrollbar min-h-[240px] flex-1 overflow-y-auto px-4 py-3"
                  onScroll={(e) => { const el = e.currentTarget; if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) loadMoreCatalog(); }}
                >
                  {catalog.loading ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400">
                      <RefreshIcon className="h-6 w-6 animate-spin text-[#3877FC]" />
                      <span className="text-[13px] font-medium">Loading your catalogue…</span>
                    </div>
                  ) : catalog.error ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                      <div className="grid h-12 w-12 place-items-center rounded-full bg-rose-50 text-rose-400"><PackageOpenIcon className="h-6 w-6" /></div>
                      <p className="text-[13px] font-medium text-slate-600">Couldn't load your catalogue.</p>
                      <button type="button" onClick={() => runCatalogQuery(catalog.q, catalog.showArchived)} className="rounded-lg bg-[#3877FC] px-4 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2A59BD]">Retry</button>
                    </div>
                  ) : catalog.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F6F9FF] text-slate-300 ring-1 ring-inset ring-[#E2EAF8]"><PackageOpenIcon className="h-7 w-7" /></div>
                      <p className="text-[13.5px] font-semibold text-slate-700">{String(catalog.q || "").trim() ? "No products match your search" : "Your catalogue is empty"}</p>
                      <p className="max-w-xs text-[12px] text-slate-500">{String(catalog.q || "").trim() ? "Try a different name or SKU, or add it as a new product." : "Add your first product to reuse it across orders."}</p>
                      <button type="button" onClick={addSkuFromCatalog} className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-[#3877FC] px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2A59BD] active:scale-[0.98]"><PlusIcon className="h-3.5 w-3.5" /> Add new SKU</button>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {catalog.items.map((it, i) => {
                        const archived = String(it.status || "").toUpperCase() === STATUS_ARCHIVED;
                        const added = !archived && orderHasSku(it.product_sku_id);
                        const selected = !archived && catalogSelected.has(it.product_sku_id);
                        const restoring = restoringSku === it.product_sku_id;
                        // Active rows are a <label> so a click anywhere toggles the checkbox.
                        // Archived rows have no checkbox — a <label> would forward the click to
                        // its first control (the Edit button), so use a plain <div> instead.
                        const RowTag = archived ? "div" : "label";
                        return (
                          <li key={(it.product_sku_id || "") + i}>
                            <RowTag title={archived ? "Archived — restore it first to add it to an order" : undefined} className={`group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 transition-all ${archived ? "cursor-default border-transparent bg-slate-50/60" : added ? "cursor-default border-transparent opacity-60" : selected ? "cursor-pointer border-[#3877FC] bg-[#EEF4FE]" : "cursor-pointer border-transparent hover:border-[#E2EAF8] hover:bg-[#F6F9FF]"}`}>
                              {archived ? (
                                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center text-slate-300"><ArchiveIcon className="h-[15px] w-[15px]" /></span>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={added || selected}
                                  disabled={added}
                                  onChange={() => toggleCatalogSelect(it.product_sku_id)}
                                  className="h-[18px] w-[18px] shrink-0 cursor-pointer rounded-[5px] border-[#C7D2E4] text-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/30 focus:ring-offset-0 disabled:cursor-default"
                                />
                              )}
                              <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg transition-colors ${archived ? "bg-slate-100 text-slate-400" : selected && !added ? "bg-[#3877FC]/[0.14] text-[#2A59BD]" : "bg-[#3877FC]/[0.08] text-[#2A59BD]"}`}>
                                <BoxIcon className="h-[18px] w-[18px]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className={`block truncate text-[13.5px] font-semibold ${archived ? "text-slate-500" : "text-slate-800"}`}>{it.product_name || "—"}</span>
                                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-slate-600">{it.product_sku_id}</span>
                                  {it.product_hsn_code ? <span className="text-[10.5px] font-medium text-slate-400">HSN {it.product_hsn_code}</span> : null}
                                </span>
                              </div>
                              {archived ? (
                                <span className="flex-shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold text-slate-500 ring-1 ring-inset ring-slate-200">Archived</span>
                              ) : added ? (
                                <span className="flex-shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold text-emerald-600 ring-1 ring-inset ring-emerald-200">Added</span>
                              ) : it.item_pricing_value?.unit_price != null ? (
                                <span className="flex flex-shrink-0 flex-col items-end leading-tight">
                                  <span className="text-[14px] font-bold tabular-nums text-slate-800">{fmtInr(it.item_pricing_value.unit_price)}</span>
                                  <span className="text-[9.5px] font-medium text-slate-400">(incl. GST)</span>
                                </span>
                              ) : null}
                              <span className="flex flex-shrink-0 items-center gap-0.5">
                                <button
                                  type="button"
                                  title="Edit SKU"
                                  aria-label={`Edit ${it.product_sku_id}`}
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); editSkuFromCatalog(it); }}
                                  className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-slate-500 transition-colors hover:bg-[#3877FC]/[0.08] hover:text-[#2A59BD] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
                                >
                                  <PencilIcon className="h-3.5 w-3.5" />
                                </button>
                                {archived ? (
                                  <button
                                    type="button"
                                    title="Restore SKU"
                                    aria-label={`Restore ${it.product_sku_id}`}
                                    disabled={restoring}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); restoreSku(it); }}
                                    className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-slate-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40 disabled:opacity-50"
                                  >
                                    {restoring ? <RefreshIcon className="h-3.5 w-3.5 animate-spin" /> : <RestoreIcon className="h-3.5 w-3.5" />}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    title="Archive SKU"
                                    aria-label={`Archive ${it.product_sku_id}`}
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCatalogArchive(it); }}
                                    className="grid h-7 w-7 cursor-pointer place-items-center rounded-md text-slate-500 transition-colors hover:bg-amber-50 hover:text-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
                                  >
                                    <ArchiveIcon className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </span>
                            </RowTag>
                          </li>
                        );
                      })}
                      {catalog.loadingMore && <li className="flex items-center justify-center gap-2 py-4 text-[12px] text-slate-400"><RefreshIcon className="h-3.5 w-3.5 animate-spin" /> Loading more…</li>}
                    </ul>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-[#F6F9FF] px-6 py-3.5">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={addSkuFromCatalog} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-white px-3 text-[12.5px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9]">
                      <PlusIcon className="h-3.5 w-3.5" /> New SKU
                    </button>
                    <button type="button" onClick={openBulkSkuPage} className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg bg-white px-3 text-[12.5px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9]">
                      <ExternalLinkIcon className="h-3.5 w-3.5" /> Bulk upload
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    {catalogSelected.size > 0 && <span className="text-[12px] font-medium text-slate-500">{catalogSelected.size} selected</span>}
                    <button type="button" onClick={closeCatalog} className="inline-flex h-9 cursor-pointer items-center rounded-lg bg-white px-4 text-[13px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9]">
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={addSelectedFromCatalog}
                      disabled={catalogSelected.size === 0}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#3877FC] px-4 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                    >
                      {catalogSelected.size > 0 ? `Add ${catalogSelected.size} item${catalogSelected.size === 1 ? "" : "s"}` : "Add items"}
                    </button>
                  </div>
                </div>
                  </>
                )}
                {catalogArchive && (
                  <div className="absolute inset-0 z-20 grid place-items-center bg-slate-900/25 p-6 backdrop-blur-[2px] animate-fade-in" onClick={() => !archivingSku && setCatalogArchive(null)}>
                    <div className="w-full max-w-sm rounded-2xl border border-[#E4E9F1] bg-white p-5 shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-start gap-3">
                        <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-amber-50 text-amber-600"><ArchiveIcon className="h-[18px] w-[18px]" /></div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-[14.5px] font-semibold text-slate-900">Archive this SKU?</h4>
                          <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[12.5px]">
                            <span className="min-w-0 truncate font-semibold text-slate-700">{catalogArchive.product_name || "—"}</span>
                            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] font-medium text-slate-600">{catalogArchive.product_sku_id}</span>
                          </p>
                          <p className="mt-2 text-[12px] leading-relaxed text-slate-500">It leaves your active catalogue but stays on record — restore it anytime from “Show archived”. Items already in this order aren't affected.</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-end gap-2.5">
                        <button type="button" disabled={archivingSku} onClick={() => setCatalogArchive(null)} className="inline-flex h-9 cursor-pointer items-center rounded-lg bg-white px-4 text-[13px] font-medium text-slate-600 ring-1 ring-inset ring-[#D8DFEA] transition-all hover:text-slate-900 hover:ring-[#C0CAD9] disabled:opacity-60">Cancel</button>
                        <button type="button" disabled={archivingSku} onClick={confirmArchiveSku} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-amber-500 px-4 text-[13px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(245,158,11,0.5)] transition-all hover:bg-amber-600 active:translate-y-px disabled:opacity-60">
                          {archivingSku && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                          {archivingSku ? "Archiving…" : "Archive SKU"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}

          {/* Address management dialogs (shared components, unchanged) */}
          {newSkuDialog && !catalog.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" onClick={() => !savingNewSku && setNewSkuDialog(null)}>
              <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-[#E4E9F1] bg-white shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Escape") setNewSkuDialog(null); e.stopPropagation(); }}>
                <div className="border-b border-slate-100 px-5 pb-4 pt-5">
                  <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-slate-900">Add SKU to your catalog</h3>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">Next time it fills in from a search.</p>
                </div>
                <div className="space-y-3.5 px-5 py-4">
                  {renderSkuFormFields()}
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-[#F6F9FF] px-5 py-3.5">
                  <button type="button" onClick={() => setNewSkuDialog(null)} disabled={savingNewSku} className="inline-flex h-10 cursor-pointer items-center rounded-xl bg-white px-4 text-sm font-medium text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-[#D8DFEA] transition-all hover:bg-[#F8FAFD] hover:text-slate-900 disabled:opacity-60">Cancel</button>
                  <button type="button" onClick={saveNewSku} disabled={savingNewSku} className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-xl bg-[#3877FC] px-4 text-sm font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_8px_20px_-8px_rgba(56,119,252,0.5)] transition-all hover:bg-[#2A59BD] active:translate-y-px disabled:opacity-60">
                    {savingNewSku && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                    {savingNewSku ? "Adding…" : "Add SKU"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
          <PickupAddressModal open={addrModalOpen} mode={addrModalMode} location={addrModalLocation} onClose={() => setAddrModalOpen(false)} onSaved={handleAddressSaved} />
          <DeleteConfirmModal open={deleteConfirmOpen} onConfirm={confirmDeleteAddress} onCancel={() => { setDeleteConfirmOpen(false); setPickupToDelete(null); }} deleting={deletingPickup} />

          {/* Saved-customer dialog */}
          {showCustomerModal && ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cfo-saved-customer-title"
              tabIndex={-1}
              onKeyDown={(e) => { if (e.key === "Escape") setShowCustomerModal(false); }}
            >
              <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
                <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 pb-4 pt-5">
                  <div>
                    <h3 id="cfo-saved-customer-title" className="text-[16px] font-semibold tracking-[-0.01em] text-slate-900">Saved customer</h3>
                    <p className="mt-0.5 text-[13px] text-slate-500">Choose an address to use it, or close to enter a new one.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomerModal(false)}
                    aria-label="Close"
                    className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/50"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="max-h-[60vh] space-y-2.5 overflow-y-auto p-5">
                  {customerLookupResults.map((cust, i) => (
                    <button
                      key={i}
                      type="button"
                      autoFocus={i === 0}
                      onClick={() => selectCustomer(cust)}
                      className="group flex w-full items-start gap-3.5 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-[#3877FC] hover:bg-[#3877FC]/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      <span className={`mt-0.5 grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-slate-100 text-[13px] font-semibold text-slate-500 transition-colors group-hover:bg-[#3877FC]/10 group-hover:text-[#2A59BD]`}>
                        {initialsOf(cust.drop_poc)}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold text-slate-900">{cust.drop_poc}</span>
                        <span className="mt-0.5 block text-[12.5px] leading-relaxed text-slate-500 line-clamp-2">{cust.drop_address}</span>
                        <span className="mt-0.5 block text-[12.5px] font-semibold text-slate-700">
                          {[cust.drop_city, cust.drop_state].filter(Boolean).join(", ")} {cust.drop_postal_code}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Draft saved dialog */}
          {draftSuccessModal.open && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in" role="dialog" aria-modal="true">
              <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl animate-scale-in">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
                  <CheckIcon className="h-7 w-7" strokeWidth={2.75} />
                </div>
                <h3 className="mt-4 text-[17px] font-semibold tracking-[-0.01em] text-slate-900">
                  {draftSuccessModal.isUpdate ? "Draft updated" : "Draft saved"}
                </h3>
                {!draftSuccessModal.isUpdate && (
                  <p className="mt-1 text-[13px] text-slate-500">Find it under Draft Orders whenever you're ready.</p>
                )}
                {draftSuccessModal.draftId && (
                  <p className="mx-auto mt-2.5 w-fit rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-[11px] text-slate-500">{draftSuccessModal.draftId}</p>
                )}
                <div className="mt-5 flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={() => navigate("/draft-orders")}
                    className={`${BTN_PRIMARY} w-full justify-center`}
                  >
                    Open Draft Orders
                  </button>
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={() => navigate("/orders")}
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      Orders
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDraftSuccessModal({ open: false, draftId: null, isUpdate: false });
                        navigate("/create-forward-order", { replace: true, state: {} });
                        window.location.reload();
                      }}
                      className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
                    >
                      New order
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          <OrderCreationIssueModal
            issue={orderIssue}
            onClose={() => setOrderIssue(null)}
            onAction={handleOrderIssueAction}
          />
        </div>
      </div>
    </MotionConfig>
  );
}

export default CreateForwardOrder;

function FieldError({ message, id }) {
  if (!message) return null;
  return <p id={id} className="mt-1.5 text-[12px] font-medium text-rose-600">{message}</p>;
}

/* ─────────────────────────────────────────────────────────────────────────────
   JourneyGroup — a folder-tab card: the docket, digitised.
   Each group is a file in the shipment's paperwork. Its tab carries the icon,
   the title and the one live signal (blue while typing via :focus-within,
   emerald pop when the group is complete); the body joins seamlessly beneath.
   Silhouette does the hierarchy - no per-card shadows, no offsets.
   Module-scoped: an inline component identity would remount fields per keystroke.
──────────────────────────────────────────────────────────────────────────── */
function JourneyGroup({ icon: GIcon, title, hint, right, done, children }) {
  return (
    <section className="group/jg">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className={`relative z-10 -mb-px inline-flex h-10 items-center gap-2.5 rounded-t-xl border border-b-0 px-4 transition-colors duration-200 ${
          done
            ? "border-emerald-200 bg-emerald-50/70"
            : "border-[#E2EAF8] bg-[#F6F9FF] group-focus-within/jg:border-[#3877FC]/35 group-focus-within/jg:bg-[#3877FC]/[0.05]"
        }`}>
          <span
            className={`relative grid h-6 w-6 flex-shrink-0 place-items-center rounded-full transition-all duration-200 ${
              done
                ? ""
                : "bg-[#3877FC]/[0.08] text-[#2A59BD] group-focus-within/jg:bg-[#3877FC]/[0.14] group-focus-within/jg:ring-4 group-focus-within/jg:ring-[#3877FC]/10"
            }`}
          >
            <AnimatePresence initial={false}>
              {done ? (
                <motion.span
                  key="done"
                  initial={{ scale: 0.3, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.3, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 22 }}
                  className="absolute inset-0 grid place-items-center rounded-full bg-emerald-500 text-white ring-[3px] ring-emerald-500/15"
                >
                  <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                </motion.span>
              ) : null}
            </AnimatePresence>
            <GIcon className={`h-3.5 w-3.5 ${done ? "opacity-0" : ""}`} aria-hidden="true" />
          </span>
          <h3 className={`whitespace-nowrap text-[13.5px] font-semibold tracking-[-0.01em] transition-colors duration-200 ${
            done ? "text-emerald-900" : "text-slate-900 group-focus-within/jg:text-[#2A59BD]"
          }`}>{title}</h3>
        </div>
        {/* Phones: the actions take a full row (a fixed-width search beside the tab clipped);
            desktop: beside the tab as before. */}
        {right ? <div className="mb-1.5 flex w-full items-center gap-2 sm:w-auto">{right}</div> : null}
      </div>
      <div className="rounded-xl rounded-tl-none border border-[#E2EAF8] bg-[#F6F9FF] px-4 pb-3.5 pt-3 lg:px-5">
        {hint ? <p className="-mt-0.5 mb-3.5 text-[12.5px] text-slate-500">{hint}</p> : null}
        {children}
      </div>
    </section>
  );
}

/* TicketEndpoint — one end of the route: the pincode as the code, the city beneath. */
/* Deterministic barcode: bar and gap widths derived from the value's bytes, so
   every reference draws its own pattern. Decorative (not a scannable symbology) -
   the same visual language as a printed label, generated live. */
function CodeBars({ value, height = 36, className = "" }) {
  const str = String(value);
  const widths = [2, 1, 1];
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    widths.push(1 + (c % 3), 1 + ((c >> 2) % 2), 1 + ((c >> 4) % 3), 1);
  }
  widths.push(1, 1, 2);
  const total = widths.reduce((a, b) => a + b, 0);
  let x = 0;
  const bars = [];
  widths.forEach((w, i) => {
    if (i % 2 === 0) bars.push(<rect key={i} x={x} y="0" width={w} height="10" />);
    x += w;
  });
  return (
    <svg viewBox={`0 0 ${total} 10`} preserveAspectRatio="none" className={className} style={{ height }} aria-hidden="true">
      <g fill="#0F172A">{bars}</g>
    </svg>
  );
}

function TicketEndpoint({ label, data, align = "left", size = "md" }) {
  // "lg" is the booked-confirmation ticket: same anatomy, larger pin and a readable city name
  const lg = size === "lg";
  return (
    <div className={`min-w-0 ${align === "right" ? "text-right" : "text-left"}`}>
      <div className={`${lg ? "text-[10.5px]" : "text-[9.5px]"} font-bold uppercase tracking-wider text-slate-600`}>{label}</div>
      {data ? (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
          <div className={`font-mono ${lg ? "text-[22px]" : "text-[19px]"} font-semibold leading-tight tracking-[0.04em] tabular-nums text-slate-900`}>{data.pin}</div>
          {data.city ? <div className={`truncate ${lg ? "mt-0.5 text-[15px] font-semibold text-slate-800" : "text-[11.5px] font-medium text-slate-600"}`}>{data.city}</div> : null}
        </motion.div>
      ) : (
        <div className={align === "right" ? "flex flex-col items-end" : ""} aria-hidden="true">
          <div className="cfo-skeleton mt-1.5 h-[17px] w-20 rounded-md" />
          <div className="cfo-skeleton mt-1 h-2.5 w-12 rounded" />
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   ShipmentTicket — the living artifact.
   A straight route line the shipment marker travels as the flow completes;
   clean label/value rows that fill as facts become real; a stamp, a barcode
   strip and the AWB once booked. Never shows anything not entered.
──────────────────────────────────────────────────────────────────────────── */
/* The ticket's true silhouette: rounded top, a semicircular die-cut notch on each
   edge at every perforation, and a sawtooth tear along the bottom - one measured
   path, so the drop-shadow and the backdrop flow through every cut for real. */
function buildTicketOutline(w, h, ys) {
  const R = 12, N = 7, T = 8, TEETH = 20;
  let d = `M ${R} 0 H ${w - R} A ${R} ${R} 0 0 1 ${w} ${R}`;
  ys.forEach((y) => { d += ` V ${y - N} A ${N} ${N} 0 0 0 ${w} ${y + N}`; });
  d += ` V ${h - T}`;
  const step = w / TEETH;
  for (let i = TEETH - 1; i >= 0; i--) {
    d += ` L ${((i + 0.5) * step).toFixed(2)} ${h} L ${(i * step).toFixed(2)} ${h - T}`;
  }
  [...ys].reverse().forEach((y) => { d += ` V ${y + N} A ${N} ${N} 0 0 0 0 ${y - N}`; });
  d += ` V ${R} A ${R} ${R} 0 0 1 ${R} 0 Z`;
  return d;
}

/* ═══════════════════ Booked — one wide boarding-pass ticket ═══════════════════
   A single elegant object, in the app's own ticket language: a refined ink stamp pressed
   onto the top edge, the route drawn full-width with a marker-truck gliding origin→
   destination, then a perforated stub carrying the four facts that matter (courier,
   arrival, charge, AWB) and the order meta as a quiet line. Brand blue leads; emerald
   only where "booked" is stated. Every motion respects prefers-reduced-motion. */
function fmtStampDate(d) {
  try {
    const day = String(d.getDate()).padStart(2, "0");
    const mon = MONTH_ABBR[d.getMonth()];
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${day} ${mon} ${d.getFullYear()} · ${h}:${m} ${ap}`;
  } catch { return null; }
}

const riseIn = (reduce, delay) => (reduce
  ? { initial: false }
  : { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.42, ease: [0.22, 1, 0.36, 1] } });

/* Confetti that bursts from the stamp as it lands — brand palette, small pieces, clipped to
   the card so it can never widen the page. Skipped entirely under reduced motion. */
const CONFETTI_COLOURS = ["#3877FC", "#2A59BD", "#10B981", "#34D399", "#F59E0B", "#93C5FD"];
function StampConfetti() {
  const parts = useRef(null);
  const rain = useRef(null);
  const layerRef = useRef(null);
  const [fallH, setFallH] = useState(720);
  const [phase, setPhase] = useState("live"); // live → fading → gone (after ~30s)
  if (!parts.current) {
    // Three opening bursts from the stamp.
    const WAVES = [0.28, 1.25, 2.3];
    parts.current = WAVES.flatMap((base, w) => Array.from({ length: 20 }).map((_, i) => ({
      dx: (Math.random() * 2 - 1) * (300 + w * 40),
      rise: 24 + Math.random() * 60,
      fall: 190 + Math.random() * 110,
      rot: (Math.random() * 2 - 1) * 440,
      delay: base + Math.random() * 0.22,
      dur: 1.6 + Math.random() * 0.6,
      w: 5 + Math.random() * 4,
      round: (i + w) % 4 === 0,
      color: CONFETTI_COLOURS[(i + w) % CONFETTI_COLOURS.length],
    })));
    // Then a slow, sparse confetti fall across the card, looping for half a minute.
    rain.current = Array.from({ length: 34 }).map((_, i) => ({
      left: Math.random() * 100,
      sway: (Math.random() * 2 - 1) * 46,
      rot: (Math.random() * 2 - 1) * 720,
      delay: 1.2 + Math.random() * 6,
      dur: 5.5 + Math.random() * 4,
      gap: Math.random() * 2.5,
      w: 5 + Math.random() * 4,
      round: i % 3 === 0,
      color: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
    }));
  }
  useLayoutEffect(() => {
    const el = layerRef.current;
    if (!el) return undefined;
    const measure = () => setFallH(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const fade = window.setTimeout(() => setPhase("fading"), 30000);
    const gone = window.setTimeout(() => setPhase("gone"), 31600);
    return () => { window.clearTimeout(fade); window.clearTimeout(gone); };
  }, []);
  if (phase === "gone") return null;
  return (
    <motion.div ref={layerRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-[28px]"
      animate={{ opacity: phase === "fading" ? 0 : 1 }} transition={{ duration: 1.4 }}>
      <div className="absolute left-1/2 top-3 h-0 w-0">
        {parts.current.map((p, i) => (
          <motion.span
            key={i}
            className={`absolute block ${p.round ? "rounded-full" : "rounded-[1.5px]"}`}
            style={{ width: p.w, height: p.round ? p.w : p.w * 0.6, background: p.color }}
            initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
            animate={{ x: p.dx, y: [0, -p.rise, p.fall], opacity: [1, 1, 0], rotate: p.rot }}
            transition={{ duration: p.dur, delay: p.delay, ease: "easeOut" }}
          />
        ))}
      </div>
      {rain.current.map((p, i) => (
        <motion.span
          key={`r${i}`}
          className={`absolute top-0 block ${p.round ? "rounded-full" : "rounded-[1.5px]"}`}
          style={{ left: `${p.left}%`, width: p.w, height: p.round ? p.w : p.w * 0.6, background: p.color }}
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: [-24, fallH + 24], x: [0, p.sway, 0], rotate: [0, p.rot], opacity: [0, 1, 1, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, repeatDelay: p.gap, ease: "linear" }}
        />
      ))}
    </motion.div>
  );
}

/* The route — the same treatment as the shipment-preview ticket: 2px base line, four leg
   ticks that turn blue as the truck passes, the rainbow trail behind it, blue endpoint dots,
   and the brand truck riding the line. Here the truck makes the whole journey slowly (8s);
   the destination stays a hollow dot, so nothing claims delivery. Reduced motion parks it. */
const relativeDays = (n) => (n <= 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`);
function RouteDrive({ originCity, originPin, destCity, destPin, reduce }) {
  const p = useMotionValue(reduce ? 1 : 0);          // journey progress 0 → 1
  const [legs, setLegs] = useState(reduce ? 4 : 0);   // ticks passed, for colouring
  useMotionValueEvent(p, "change", (v) => setLegs(Math.min(4, Math.floor(v * 5))));
  useEffect(() => {
    if (reduce) { p.set(1); return undefined; }
    const controls = animate(p, 1, { duration: 8, ease: [0.45, 0, 0.25, 1], delay: 0.6 });
    return () => controls.stop();
  }, [p, reduce]);
  const truckLeft = useTransform(p, (v) => `calc(5px + ${v} * (100% - 10px))`);
  const trailWidth = useTransform(p, (v) => `calc(${v} * (100% - 10px))`);
  return (
    <div className="mt-3 grid grid-cols-2 items-center gap-x-4 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-6">
      {/* the preview ticket's endpoints, reused (lg): mono pincode leads, city as the caption.
          On sm+ they flank the line like a boarding-pass route strip; on mobile they sit above it. */}
      <TicketEndpoint label="From" data={{ pin: originPin, city: originCity }} size="lg" />
      <div className="order-2 min-w-0 justify-self-end sm:order-3">
        <TicketEndpoint label="To" data={{ pin: destPin, city: destCity }} align="right" size="lg" />
      </div>
      {/* the track is tall enough that the 60px truck's roof stays inside it; mx-2 keeps the
          truck clear of the endpoint blocks / stub edges at both ends */}
      <div className="relative order-3 col-span-2 mx-2 h-[80px] sm:order-2 sm:col-span-1">
        <div className="absolute left-[5px] right-[5px] top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-slate-200" />
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={`absolute top-1/2 h-[7px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-300 ${legs >= i ? "bg-[#3877FC]" : "bg-slate-300"}`}
            style={{ left: `calc(5px + ${i / 5} * (100% - 10px))` }}
            aria-hidden="true"
          />
        ))}
        <motion.div className="cfo-rainbow absolute left-[5px] top-1/2 h-[2px] -translate-y-1/2 rounded-full" style={{ width: trailWidth }} aria-hidden="true" />
        <span className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[#3877FC]" />
        <span className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-[#3877FC] bg-white" />
        <motion.span className="absolute top-1/2 z-10" style={{ left: truckLeft, transform: "translate(-50%, -50%)" }} aria-hidden="true">
          {/* the preview's sprite, a third larger for the confirmation page; wheels still ride the line */}
          <span className="wv-ticket-truck block h-[40px] w-[60px] -translate-y-[17px] [filter:drop-shadow(0_2px_3px_rgba(15,23,42,0.3))]" />
          <span className="wv-ticket-truck-fallback h-9 w-9 place-items-center rounded-full border-2 border-[#3877FC] bg-white text-[#2A59BD] shadow-sm">
            <TruckIcon className="h-4 w-4" />
          </span>
        </motion.span>
      </div>
    </div>
  );
}

/* Code 39 barcode of the AWB — a real, scannable encoding of the tracking number (A–Z, 0–9,
   "-", ".", space, $, /, +, %; lower-case is upper-cased, as scanners read it), so the ticket
   carries its AWB the way a boarding pass does. The table is built from the Code 39 structure
   (bar pattern by position × wide-space group per character set) rather than typed by hand. */
const CODE39 = (() => {
  const bars = ["10001", "01001", "11000", "00101", "10100", "01100", "00011", "10010", "01010", "00110"];
  const groups = [["1234567890", "0100"], ["ABCDEFGHIJ", "0010"], ["KLMNOPQRST", "0001"], ["UVWXYZ-. *", "1000"]];
  const weave = (b, s) => Array.from({ length: 9 }, (_, i) => ((i % 2 === 0 ? b[i / 2] : s[(i - 1) / 2]) === "1" ? "w" : "n")).join("");
  const table = {};
  groups.forEach(([chars, spaces]) => chars.split("").forEach((c, i) => { table[c] = weave(bars[i], spaces); }));
  Object.entries({ $: "1110", "/": "1101", "+": "1011", "%": "0111" }).forEach(([c, spaces]) => { table[c] = weave("00000", spaces); });
  return table;
})();
function AwbBarcode({ value, className = "" }) {
  const text = String(value || "").trim().toUpperCase();
  if (text.length < 4 || [...text].some((c) => !CODE39[c])) return null;
  const rects = [];
  let x = 0;
  `*${text}*`.split("").forEach((c) => {
    const pattern = CODE39[c];
    for (let i = 0; i < 9; i++) {
      const width = pattern[i] === "w" ? 3 : 1;
      if (i % 2 === 0) rects.push([x, width]); // even indexes are bars, odd are spaces
      x += width;
    }
    x += 1; // inter-character gap
  });
  const total = x - 1;
  return (
    <svg className={className} viewBox={`0 0 ${total} 40`} preserveAspectRatio="none" shapeRendering="crispEdges" role="img" aria-label={`AWB ${text} barcode`}>
      {rects.map(([bx, bw], i) => <rect key={i} x={bx} y="0" width={bw} height="40" fill="#0F172A" />)}
    </svg>
  );
}

function Fact({ label, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Detail({ k, v, mono = false, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">{k}</div>
      <div className={`mt-1 text-[13.5px] font-semibold leading-snug text-slate-800 ${mono ? "break-all tabular-nums" : "break-words"}`}>{v}</div>
    </div>
  );
}

function BookedSuccess({ bookedAt, origin, destination, courier, charge, settlement, billedKg, billedBasis, order, labelUrl, invoiceUrl, awbCopied, onCopyAwb, onViewDetails, onAllShipments, onBookAnother }) {
  const reduce = useReducedMotion();
  const titleCase = (s) => String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const originCity = titleCase(origin?.city) || "Pickup";
  const destCity = titleCase(destination?.city) || "Destination";
  const stampDate = bookedAt ? fmtStampDate(bookedAt) : null;
  const ModeIcon = courier?.mode ? MODE_ICON[courier.mode] : null;
  const accent = courier?.accent || "#3877FC";
  // Wallets are debited at booking (present tense); COD net-off settles later, at remittance (future).
  const settlementLine = settlement
    ? (settlement === "COD net-off" ? "Will be settled from your COD net-off" : `Settled from ${settlement}`)
    : null;

  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={SPRING} className="mx-auto w-full max-w-[1180px] pb-5 pt-9 sm:pt-10">
      {/* ── The ticket ── */}
      <div className="relative">
        {/* Shadow lives on a halo layer, off the paper, so the notches stay true cut-outs */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[28px] shadow-[0_30px_70px_-42px_rgba(15,23,42,0.4)]" />
        {!reduce && <StampConfetti />}

        {/* Ink stamp — pressed onto the top edge, centred */}
        <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
          <motion.div
            initial={reduce ? false : { scale: 1.45, opacity: 0, rotate: -13 }}
            // slam down, press into the paper, rock twice as it seats, settle — a stamp impression
            animate={reduce ? { scale: 1, opacity: 1, rotate: -5 } : { scale: [1.45, 0.96, 1.03, 0.99, 1], opacity: [0, 1, 1, 1, 1], rotate: [-13, -3.5, -7.5, -4.2, -5] }}
            transition={reduce ? { duration: 0 } : { duration: 1.15, times: [0, 0.28, 0.5, 0.72, 1], ease: "easeOut", delay: 0.12 }}
            className="relative"
          >
            {!reduce && (
              <motion.span aria-hidden="true" className="absolute inset-0 rounded-[14px] border border-emerald-500/50"
                initial={{ scale: 0.85, opacity: 0.5 }} animate={{ scale: 1.5, opacity: 0 }} transition={{ duration: 0.7, delay: 0.18, ease: "easeOut" }} />
            )}
            <div className="rounded-[14px] border-2 border-emerald-600/80 bg-white p-[3px] shadow-[0_8px_20px_-12px_rgba(16,185,129,0.5)]">
              <div className="rounded-[10px] border border-emerald-600/60 px-6 py-2 text-center">
                <div className="text-[22px] font-black uppercase leading-none tracking-[0.24em] text-emerald-700">Booked</div>
                {stampDate && <div className="mt-1 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.08em] tabular-nums text-emerald-700/75">{stampDate}</div>}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Upper stub: headline + route, on a softly tinted, lightly mapped ground */}
        <div className="relative overflow-hidden rounded-t-[28px] rounded-b-[12px] border border-b-0 border-[#E3E9F2] bg-white px-6 pb-4 pt-10 sm:px-10 sm:pt-11">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-40" style={{ background: "linear-gradient(to bottom, rgba(56,119,252,0.075), rgba(56,119,252,0.025) 50%, transparent)" }} />
          <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-32 w-[420px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.13), transparent)" }} />
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-36" style={{ backgroundImage: "radial-gradient(rgba(30,58,109,0.09) 1px, transparent 1.4px)", backgroundSize: "18px 18px", maskImage: "linear-gradient(to bottom, transparent, black 35%, black 70%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, transparent, black 35%, black 70%, transparent)" }} />
          {/* Headline */}
          <motion.div {...riseIn(reduce, 0.3)} className="text-center">
            <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900 sm:text-[27px]">Order placed. <span className="whitespace-nowrap">Pickup is next.</span></h2>
            <p className="mt-1.5 text-[15px] text-slate-500">
              {courier?.carrier
                ? <><b className="font-semibold text-slate-700">{courier.carrier}</b> will collect it from <b className="font-semibold text-slate-700">{originCity}</b>{courier?.edd && courier?.tat != null ? <> · delivery expected <b className="whitespace-nowrap font-semibold text-slate-700">{relativeDays(courier.tat)}</b> <span className="whitespace-nowrap text-slate-400">({courier.edd})</span></> : null}</>
                : <>Your courier will collect it from <b className="font-semibold text-slate-700">{originCity}</b></>}
            </p>
          </motion.div>

          {/* Route */}
          <RouteDrive originCity={originCity} originPin={origin?.pin} destCity={destCity} destPin={destination?.pin} reduce={reduce} />

        </div>

        {/* Perforation: the two stubs meet here — their 12px seam corners form the notches as
            real cut-outs, so the page's own background shows through them. */}
        <div className="relative z-10 h-0" aria-hidden="true">
          <div className="absolute inset-x-[14px] top-0 -translate-y-1/2 border-t-2 border-dotted border-slate-300" />
        </div>

        {/* Lower stub: the facts + order meta */}
        <div className="relative rounded-b-[28px] rounded-t-[12px] border border-t-0 border-[#E3E9F2] bg-white px-6 pb-5 pt-4 sm:px-10">

          {/* The AWB — the king of this ticket: big, copyable, and a real Code 39 barcode */}
          <motion.div {...riseIn(reduce, 0.4)} className="flex flex-wrap items-center justify-between gap-x-10 gap-y-4">
            <div className="min-w-0">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">AWB / Tracking number</div>
              {order?.waybill ? (
                <button type="button" onClick={onCopyAwb} className="group mt-1.5 inline-flex max-w-full items-center gap-2.5 text-left">
                  {/* never truncate the AWB — wrap a rare over-long one instead of hiding characters */}
                  <span className="break-all text-[22px] font-black leading-tight tracking-[0.04em] tabular-nums text-slate-900 transition-colors group-hover:text-[#2A59BD] sm:text-[30px] sm:leading-none">{order.waybill}</span>
                  {awbCopied ? <CheckIcon className="h-5 w-5 flex-shrink-0 text-emerald-600" strokeWidth={3} /> : <CopyIcon className="h-5 w-5 flex-shrink-0 text-slate-400 transition-colors group-hover:text-[#3877FC]" />}
                </button>
              ) : (
                <div className="mt-1.5 text-[17px] font-semibold text-slate-600">Assigned shortly</div>
              )}
            </div>
            {order?.waybill && <AwbBarcode value={order.waybill} className="h-12 w-full max-w-[460px] flex-shrink-0 sm:w-[460px]" />}
          </motion.div>
          <div className="my-3 border-t border-slate-100" aria-hidden="true" />

          {/* Three facts */}
          <motion.div {...riseIn(reduce, 0.45)} className="grid gap-x-8 gap-y-5 sm:grid-cols-3">
            <Fact label="Courier">
              <div className="flex items-center gap-2.5">
                <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl text-[14px] font-extrabold"
                  style={{ backgroundImage: `radial-gradient(circle at 30% 22%, ${accent}33, transparent 62%), linear-gradient(150deg, ${accent}1F, ${accent}0A)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}2E` }}>
                  {initialsOf(courier?.carrier || "?")}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[16px] font-bold leading-tight text-slate-900">{courier?.carrier || "Allocating"}</div>
                  {courier?.mode && (
                    <span className={`mt-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide ring-1 ring-inset ${MODE_STYLE[courier.mode] || "bg-slate-100 text-slate-500 ring-slate-200/70"}`}>
                      {ModeIcon && <ModeIcon className="h-2.5 w-2.5" strokeWidth={2.25} />}{courier.mode}
                    </span>
                  )}
                </div>
              </div>
            </Fact>

            <Fact label="Expected delivery">
              {courier?.edd
                ? <><div className="text-[20px] font-bold leading-tight text-slate-900">{courier.edd}</div>{courier?.tat != null && <div className="mt-0.5 text-[13px] text-slate-400">up to {courier.tat} day{courier.tat === 1 ? "" : "s"} from pickup</div>}</>
                : <div className="text-[15px] font-semibold text-slate-600">Shared once the carrier scans it</div>}
            </Fact>

            <Fact label="Shipment charge">
              {charge != null
                ? <><div className="flex items-baseline gap-1.5"><span className="text-[26px] font-black leading-none tabular-nums text-slate-900">{fmtInr(charge)}</span><span className="text-[12px] font-medium text-slate-400">incl. GST</span></div>{settlementLine && <div className="mt-1.5 text-[13px] text-slate-400">{settlementLine}</div>}</>
                : <div className="text-[15px] font-semibold text-slate-600">Confirmed at billing</div>}
            </Fact>

          </motion.div>

          {/* Order details — a calm labelled grid; long ids get their own cell */}
          <motion.div {...riseIn(reduce, 0.55)} className="mt-3 grid gap-x-6 gap-y-4 border-t border-slate-100 pt-3 sm:grid-cols-2 lg:grid-cols-8">
            <Detail k="Recipient" v={[order?.recipientName, order?.recipientPhone].filter(Boolean).join(" · ") || "—"} className="lg:col-span-2" />
            <Detail k={order?.paymentLabel || "Payment"} v={order?.paymentValue || "—"} />
            <Detail k={order?.itemsCount === 1 ? "Item" : "Items"} v={order?.itemsCount ? String(order.itemsCount) : "—"} />
            <Detail k="Chargeable wt" v={billedKg != null ? `${billedKg.toFixed(2)} kg${billedBasis ? ` (${billedBasis === "vol" ? "volumetric" : "dead"})` : ""}` : "—"} />
            <Detail k="Order reference" v={order?.orderRef || "—"} mono className="sm:col-span-2 lg:col-span-3" />
          </motion.div>

          {/* ── Card footer: the actions live inside the ticket, flush with its bottom — never in its shadow ── */}
          {/* Phones: one full-width primary, then the secondaries two-up (a wrapped cluster reads
              as a jumble at 390px). Desktop: the same buttons in a centred row. */}
          <motion.div {...riseIn(reduce, 0.65)} className="-mx-6 -mb-5 mt-4 grid gap-2.5 rounded-b-[28px] border-t border-slate-100 bg-[#FAFBFD] px-6 py-4 sm:-mx-10 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:px-10">
            <button type="button" onClick={onViewDetails} className={`${BTN_PRIMARY} cfo-cta-ready h-12 w-full justify-center sm:h-11 sm:w-auto`}>
              <ExternalLinkIcon className="h-4 w-4" /> View order details
            </button>
            <div className="grid grid-cols-2 gap-2.5 sm:contents">
              {labelUrl && <a href={labelUrl} target="_blank" rel="noopener noreferrer" className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><PrinterIcon className="h-4 w-4" /> Print label</a>}
              {invoiceUrl && <a href={invoiceUrl} target="_blank" rel="noopener noreferrer" className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><FileTextIcon className="h-4 w-4" /> Invoice</a>}
              <button type="button" onClick={onAllShipments} className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><PackageIcon className="h-4 w-4" /> All shipments</button>
              <button type="button" onClick={onBookAnother} className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><PlusIcon className="h-4 w-4" /> Book another</button>
            </div>
          </motion.div>
        </div>
      </div>

    </motion.div>
  );
}

function ShipmentTicket({ pct = 0, step, stageLabel, origin, destination, rows, cells, charge, settlement, orderRef, booked = false, awb, awbCopied, onCopyAwb }) {
  const t = Math.max(0, Math.min(1, pct / 100));
  // the teaching line stays for as long as anything on the pass is still waiting
  const awaitingAnything = !booked && (
    !origin || !destination || !orderRef ||
    (rows || []).some((r) => !r.v) || (cells || []).some((c) => !c.v)
  );
  const clipRef = useRef(null);
  const perfARef = useRef(null);
  const perfBRef = useRef(null);
  const [outline, setOutline] = useState("");
  useLayoutEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const compute = () => {
      const w = el.offsetWidth, h = el.offsetHeight;
      const ys = [perfARef.current, perfBRef.current]
        .filter(Boolean)
        .map((n) => n.offsetTop + n.offsetHeight / 2);
      if (w && h && ys.length === 2) setOutline(buildTicketOutline(w, h, ys));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cells, charge, booked, awb]);
  const VEHICLE_SPRING = { type: "spring", stiffness: 90, damping: 16, mass: 1.2, delay: 0.26 };
  return (
    <div
      className="relative"
      style={{ filter: "drop-shadow(0 1px 2px rgba(15,23,42,0.06)) drop-shadow(0 14px 24px rgba(15,23,42,0.09)) drop-shadow(0 22px 34px rgba(30,58,138,0.07))" }}
    >
    <div
      className="relative overflow-hidden rounded-xl border border-[#DEE5EF] bg-white"
      ref={clipRef}
      style={{ clipPath: outline ? `path("${outline}")` : "polygon(0% 0%, 100% 0%, 100% calc(100% - 8px), 97.5% 100%, 95% calc(100% - 8px), 92.5% 100%, 90% calc(100% - 8px), 87.5% 100%, 85% calc(100% - 8px), 82.5% 100%, 80% calc(100% - 8px), 77.5% 100%, 75% calc(100% - 8px), 72.5% 100%, 70% calc(100% - 8px), 67.5% 100%, 65% calc(100% - 8px), 62.5% 100%, 60% calc(100% - 8px), 57.5% 100%, 55% calc(100% - 8px), 52.5% 100%, 50% calc(100% - 8px), 47.5% 100%, 45% calc(100% - 8px), 42.5% 100%, 40% calc(100% - 8px), 37.5% 100%, 35% calc(100% - 8px), 32.5% 100%, 30% calc(100% - 8px), 27.5% 100%, 25% calc(100% - 8px), 22.5% 100%, 20% calc(100% - 8px), 17.5% 100%, 15% calc(100% - 8px), 12.5% 100%, 10% calc(100% - 8px), 7.5% 100%, 5% calc(100% - 8px), 2.5% 100%, 0% calc(100% - 8px))" }}
    >
      <div className="flex items-center justify-between rounded-t-[inherit] border-b border-[#D9E3F4] bg-[rgba(56,119,252,0.07)] px-5 py-2.5">
        <span className="inline-flex items-center gap-2">
          <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3877FC] opacity-50 [animation-duration:2.6s] motion-reduce:hidden" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#3877FC]" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#2A59BD]">Your shipment preview</span>
        </span>
        {booked ? (
          <span className="rounded-full bg-emerald-500 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-white">Booked</span>
        ) : (
          <span className="text-[11px] font-semibold tabular-nums text-slate-500">{stageLabel || `Step ${step} of 5`}</span>
        )}
      </div>

      {awaitingAnything && (
        <p className="px-5 pt-3 text-center text-[10.5px] font-medium text-slate-500">
          Fills in live as you enter the shipment details
        </p>
      )}

      {/* route */}
      <div className="px-5 pt-3.5">
        <div className="flex items-start justify-between gap-4">
          <TicketEndpoint label="From" data={origin} />
          <TicketEndpoint label="To" data={destination} align="right" />
        </div>
        <div className="relative mb-1 mt-3 h-9">
          {/* base line between the endpoint dots - draws itself on arrival */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.45, ease: "easeOut", delay: 0.24 }}
            className="absolute left-[5px] right-[5px] top-1/2 h-[2px] origin-left -translate-y-1/2 rounded-full bg-slate-200"
          />
          {/* one tick per leg of the journey */}
          {[1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={`absolute top-1/2 h-[7px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-300 ${
                t >= i / 5 ? (booked ? "bg-emerald-400" : "bg-[#3877FC]") : "bg-slate-300"
              }`}
              style={{ left: `calc(5px + ${i / 5} * (100% - 10px))` }}
              aria-hidden="true"
            />
          ))}
          {/* progress, with the cargo flowing along it */}
          <motion.div
            className={`absolute left-[5px] top-1/2 h-[2px] -translate-y-1/2 rounded-full ${booked ? "bg-emerald-500" : "cfo-rainbow"}`}
            initial={false}
            animate={{ width: `calc(${t} * (100% - 10px))` }}
            transition={VEHICLE_SPRING}
          />
          {/* endpoint dots */}
          <span className={`absolute left-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${origin ? "bg-[#3877FC]" : "border-2 border-slate-300 bg-white"}`} />
          <span className={`absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${booked ? "bg-emerald-500" : destination ? "border-2 border-[#3877FC] bg-white" : "border-2 border-slate-300 bg-white"}`} />
          {booked ? (
            <motion.span
              className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-emerald-400"
              initial={{ scale: 1, opacity: 0.7 }}
              animate={{ scale: 3.2, opacity: 0 }}
              transition={{ duration: 1.1, ease: "easeOut", delay: 0.4 }}
              aria-hidden="true"
            />
          ) : null}
          {/* the shipment, on its way */}
          <motion.span
            className="absolute top-1/2 z-10"
            initial={false}
            animate={{ left: `calc(5px + ${t} * (100% - 10px))` }}
            transition={VEHICLE_SPRING}
            style={{ transform: "translate(-50%, -50%)" }}
          >
            {booked ? (
              <span className="grid h-7 w-7 place-items-center rounded-full border-2 border-emerald-500 bg-white text-emerald-600 shadow-sm">
                <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
            ) : (
              <>
                <span className="wv-ticket-truck block h-[24px] w-[36px] -translate-y-[10px] [filter:drop-shadow(0_1.5px_2px_rgba(15,23,42,0.3))]" aria-hidden="true" />
                <span className="wv-ticket-truck-fallback h-7 w-7 place-items-center rounded-full border-2 border-[#3877FC] bg-white text-[#2A59BD] shadow-sm">
                  <TruckIcon className="h-3.5 w-3.5" />
                </span>
              </>
            )}
          </motion.span>
        </div>
      </div>

      <div ref={perfARef} className="relative my-2" aria-hidden="true">
        <div className="mx-4 border-t border-dashed border-[#CBD5E4]" />
      </div>

      {/* facts */}
      <div className="space-y-[9px] px-5 pb-3 pt-1.5">
        {rows.map(({ k, v, mono }, rowIndex) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <span className="flex-shrink-0 text-[12.5px] font-medium text-slate-600">{k}</span>
            {v ? (
              <motion.span
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut", delay: 0.2 }}
                className={`cfo-print min-w-0 max-w-[62%] truncate text-right text-[13px] font-semibold tabular-nums text-slate-900 ${mono ? "font-mono text-[12px] font-medium" : ""}`}
                title={typeof v === "string" ? v : undefined}
              >
                {v}
              </motion.span>
            ) : (
              <span className={`cfo-skeleton h-3.5 rounded-md ${["w-24", "w-16", "w-20", "w-12", "w-14", "w-16"][rowIndex % 6]}`} aria-hidden="true" />
            )}
          </div>
        ))}
        {cells && (
          <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 pt-2.5">
            {cells.map(({ k, v, sub }) => (
              <div key={k} className="min-w-0 px-1 text-center">
                <div className="truncate text-[9.5px] font-bold uppercase tracking-wider text-slate-600">{k}</div>
                {v ? (
                  <motion.div
                    initial={{ opacity: 0, y: 3 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut", delay: 0.2 }}
                    className="cfo-print mt-0.5 truncate text-[13.5px] font-semibold tabular-nums text-slate-900"
                    title={sub ? `${v} (${sub})` : v}
                  >
                    {v}
                  </motion.div>
                ) : (
                  <div className="cfo-skeleton mx-auto mt-2 h-3.5 w-10 rounded-md" aria-hidden="true" />
                )}
                {v && sub && <div className="truncate text-[9.5px] font-medium text-slate-400">({sub})</div>}
              </div>
            ))}
          </div>
        )}
        {charge && !booked && (
          <div className="mt-1 border-t border-slate-100 pt-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12.5px] font-semibold text-slate-700">Shipment charge <span className="font-medium text-slate-400">incl. GST</span></span>
              <motion.span initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} className="text-[15px] font-semibold tabular-nums text-slate-900">
                {charge}
              </motion.span>
            </div>
            {settlement && (
              <p className="mt-1 text-[11px] font-medium leading-relaxed text-slate-400">
                {settlement === "COD net-off" ? <>Will be adjusted against your <span className="text-slate-500">COD net-off</span></> : <>Will be settled from <span className="text-slate-500">{settlement}</span></>}<span className="text-slate-300"> *</span>
              </p>
            )}
            <p className="mt-1.5 flex items-start gap-1 text-[10px] leading-relaxed text-slate-400/90">
              <span aria-hidden="true" className="mt-px text-slate-300">*</span>
              <span>Reflects the latest rates and your current wallet setup. The exact charge and how it settles are confirmed when you book.</span>
            </p>
          </div>
        )}
      </div>

      {/* stub - the tear-off half: the AWB lives here, ghosted until booking prints it */}
      <div ref={perfBRef} className="relative my-1.5" aria-hidden="true">
        <div className="mx-4 border-t border-dashed border-[#CBD5E4]" />
      </div>
      {!booked && (
        <div className="px-5 pb-7 pt-2.5">
          {orderRef ? (
            <motion.div key={orderRef} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: "easeOut" }}>
              <CodeBars value={orderRef} height={36} className="mx-auto block w-[84%]" />
              <p className="mt-2 truncate text-center font-mono text-[11px] font-medium tracking-[0.08em] text-slate-600" title={orderRef}>{orderRef}</p>
            </motion.div>
          ) : (
            <div>
              <div
                className="mx-auto h-9 w-[84%] rounded-[2px] opacity-60"
                aria-hidden="true"
                style={{ background: "repeating-linear-gradient(90deg, #D9E3F2 0 2px, transparent 2px 4px, #D9E3F2 4px 5px, transparent 5px 9px, #D9E3F2 9px 12px, transparent 12px 14px)" }}
              />
              <div className="cfo-skeleton mx-auto mt-2 h-2.5 w-32 rounded" aria-hidden="true" />
            </div>
          )}
        </div>
      )}
      {booked && awb && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.75 }}
          className="px-5 pb-7 pt-2.5"
        >
          <CodeBars value={awb} height={36} className="mx-auto block w-[84%]" />
          <div className="mt-2.5 flex items-center justify-center gap-2">
            <span className="font-mono text-[16px] font-semibold tracking-[0.06em] text-slate-900">{awb}</span>
            <button
              type="button"
              onClick={onCopyAwb}
              className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
              title="Copy AWB"
            >
              {awbCopied ? <CheckIcon className="h-3.5 w-3.5 text-emerald-600" /> : <CopyIcon className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="mt-1 text-center text-[9px] font-semibold uppercase tracking-wider text-slate-500">AWB · for your reference</p>
        </motion.div>
      )}

      {/* stamp */}
      {booked && (
        <motion.div
          initial={{ opacity: 0, scale: 1.6, rotate: -18 }}
          animate={{ opacity: 1, scale: 1, rotate: -12 }}
          transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.25 }}
          className="pointer-events-none absolute right-4 top-[74px] select-none rounded-md border-2 border-emerald-500/60 px-2 py-0.5 text-[13px] font-bold uppercase tracking-[0.18em] text-emerald-600/80"
          aria-hidden="true"
        >
          Booked
        </motion.div>
      )}
    </div>
    </div>
  );
}

/* A tiny isometric box that follows the entered dimensions. Decoration with a job:
   it makes wrong numbers (a 100 × 2 × 2 “box”) look wrong at a glance. */
function MiniBox({ l, b, h }) {
  const max = Math.max(l, b, h, 1);
  const L = 18 + (l / max) * 26;
  const B = 9 + (b / max) * 14;
  const H = 12 + (h / max) * 24;
  const x0 = 7, y0 = 14 + B;
  const top = `${x0},${y0} ${x0 + B},${y0 - B} ${x0 + B + L},${y0 - B} ${x0 + L},${y0}`;
  const front = `${x0},${y0} ${x0 + L},${y0} ${x0 + L},${y0 + H} ${x0},${y0 + H}`;
  const side = `${x0 + L},${y0} ${x0 + B + L},${y0 - B} ${x0 + B + L},${y0 - B + H} ${x0 + L},${y0 + H}`;
  return (
    <svg width="74" height="62" viewBox="0 0 74 62" aria-hidden="true" className="flex-shrink-0">
      <polygon points={top} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={front} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={side} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/* A number that springs to its value and ticks along the way. */
function AnimatedNumber({ value, decimals = 0, className }) {
  const mv = useSpring(0, { stiffness: 90, damping: 18 });
  const [display, setDisplay] = useState((Number(value) || 0).toFixed(decimals));
  useEffect(() => { mv.set(Number(value) || 0); }, [value, mv]);
  useEffect(() => {
    const unsub = mv.on("change", (v) => setDisplay((Number(v) || 0).toFixed(decimals)));
    return () => unsub();
  }, [mv, decimals]);
  return <span className={className}>{display}</span>;
}

/* Smart allocation, sold as an engine: a live AI core, three benefit cards (why it's
   worth turning on), an ambient blue aurora, and a payoff line. When off it stays
   inviting — never punishes choosing the courier by hand. Fully mobile-safe (stacks,
   no horizontal overflow). Behaviour is unchanged; this is presentation only. */
const ALLOC_BENEFITS = [
  { Icon: TrendingUpIcon,  title: "Higher delivery rates", desc: "Every order routes to the courier that performs best on its lane." },
  { Icon: ShieldCheckIcon, title: "Lower RTO losses",      desc: "Fewer failed deliveries, and less revenue lost to returns." },
  { Icon: ZapIcon,         title: "Effortless at scale",   desc: "Optimised the moment you book — at ten orders or ten thousand." },
];

function AllocAurora() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.span className="absolute -left-16 -top-16 h-44 w-44 rounded-full bg-[#3877FC]/12 blur-3xl"
        animate={{ x: [0, 20, 0], y: [0, 12, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }} />
      <motion.span className="absolute -bottom-20 right-2 h-40 w-40 rounded-full bg-[#38BDF8]/10 blur-3xl"
        animate={{ x: [0, -14, 0], y: [0, -10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
    </div>
  );
}

function AllocModeCard({ active, onSelect, Icon, title, desc, isSmart }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      className={`group relative flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${
        active
          ? "border-[#3877FC] bg-[#3877FC]/[0.045] shadow-[0_2px_12px_-5px_rgba(56,119,252,0.45)]"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60"
      }`}
    >
      <span className={`mt-0.5 grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-full border-2 transition-colors ${active ? "border-[#3877FC]" : "border-slate-300 group-hover:border-slate-400"}`}>
        <AnimatePresence>{active && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 500, damping: 30 }} className="h-2 w-2 rounded-full bg-[#3877FC]" />
        )}</AnimatePresence>
      </span>
      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg transition-all"
        style={active ? { background: "linear-gradient(135deg,#3877FC,#4F6BF6)", boxShadow: "0 5px 14px -4px rgba(56,119,252,0.5)" } : { background: "#EEF2F8" }}>
        <Icon className={`h-[18px] w-[18px] ${active ? "text-white" : "text-slate-400"}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={`text-[14px] font-bold ${active ? "text-slate-900" : "text-slate-700"}`}>{title}</span>
          {isSmart && <span className="cfo-ai-badge inline-flex items-center px-[7px] py-[3px] text-[9px] font-extrabold uppercase tracking-[0.09em]"><span className="cfo-ai-badge-text">AI</span></span>}
        </span>
        <span className="mt-0.5 block text-[12px] leading-relaxed text-slate-500">{desc}</span>
      </span>
    </button>
  );
}

function SmartAllocationEngine({ on, onToggle }) {
  const selectSmart = () => { if (!on) onToggle(); };
  const selectManual = () => { if (on) onToggle(); };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#E2EAF8] bg-gradient-to-b from-white to-[#FAFBFF]">
      <AnimatePresence>{on && (
        <motion.div key="aurora" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
          <AllocAurora />
        </motion.div>
      )}</AnimatePresence>

      <div className="relative p-4 sm:p-5">
        {/* Selection mode — two explicit, discoverable choices */}
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Selection mode</p>
        <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Courier selection mode">
          <AllocModeCard active={on} onSelect={selectSmart} Icon={SparklesIcon} isSmart title="Smart allocation" desc="Best courier chosen automatically at booking." />
          <AllocModeCard active={!on} onSelect={selectManual} Icon={TruckIcon} title="Manual selection" desc="Choose a specific courier from the live rates." />
        </div>

        {/* Why smart is worth it — benefit cards + payoff (only when smart is selected) */}
        <AnimatePresence initial={false}>
          {on && (
            <motion.div
              key="benefits"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ height: { type: "spring", stiffness: 260, damping: 30 }, opacity: { duration: 0.2 } }}
              className="overflow-hidden"
            >
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#3877FC]">What you get</p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {ALLOC_BENEFITS.map((b, i) => (
                    <motion.div
                      key={b.title}
                      className="relative overflow-hidden rounded-xl p-[1.25px]"
                      style={{ background: "linear-gradient(135deg,#3877FC,#7CA0FA)" }}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.09, type: "spring", stiffness: 300, damping: 26 }}
                    >
                      <div className="h-full rounded-[10.75px] bg-white p-3.5">
                        <span className="grid h-9 w-9 place-items-center rounded-lg text-white"
                          style={{ background: "linear-gradient(135deg,#3877FC,#4F6BF6)", boxShadow: "0 5px 14px -4px rgba(56,119,252,0.55)" }}>
                          <b.Icon className="h-[18px] w-[18px]" />
                        </span>
                        <p className="mt-2.5 text-[13px] font-bold text-slate-900">{b.title}</p>
                        <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{b.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  className="mt-3 flex items-center gap-2.5 rounded-xl bg-[#3877FC]/[0.06] px-4 py-2.5 ring-1 ring-inset ring-[#3877FC]/10"
                >
                  <SparklesIcon className="h-4 w-4 flex-shrink-0 text-[#3877FC]" />
                  <p className="text-[12.5px] leading-relaxed text-slate-600">More delivered, fewer returned, nothing to manage — on <b className="font-semibold text-slate-800">every order</b> you ship.</p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* The Package step's hero: an isometric box that springs to the live L×B×H, a dial that
   sweeps to the dead weight, and the charged-weight read-out — the wow centrepiece. */
function PackageStudio({ lCm, bCm, hCm, l, b, h, unitL, unitB, unitH, weightDisp, unitW, deadKg, volKg, billedBasis, billedKg }) {
  const lS = useSpring(0, { stiffness: 130, damping: 20 });
  const bS = useSpring(0, { stiffness: 130, damping: 20 });
  const hS = useSpring(0, { stiffness: 130, damping: 20 });
  useEffect(() => { lS.set(Number(lCm) || 0); }, [lCm, lS]);
  useEffect(() => { bS.set(Number(bCm) || 0); }, [bCm, bS]);
  useEffect(() => { hS.set(Number(hCm) || 0); }, [hCm, hS]);

  // Isometric geometry, anchored with generous margins so the dimension callouts sit OUTSIDE the box.
  const G = useTransform([lS, bS, hS], ([lc, bc, hc]) => {
    const max = Math.max(lc, bc, hc, 1);
    const L = 60 + (lc / max) * 130;
    const B = 24 + (bc / max) * 44;
    const H = 44 + (hc / max) * 118;
    return { L, B, H, x0: 100, y0: 96 };
  });
  const topPts = useTransform(G, ({ L, B, x0, y0 }) => `${x0},${y0} ${x0 + B},${y0 - B} ${x0 + B + L},${y0 - B} ${x0 + L},${y0}`);
  const frontPts = useTransform(G, ({ L, H, x0, y0 }) => `${x0},${y0} ${x0 + L},${y0} ${x0 + L},${y0 + H} ${x0},${y0 + H}`);
  const sidePts = useTransform(G, ({ L, B, H, x0, y0 }) => `${x0 + L},${y0} ${x0 + B + L},${y0 - B} ${x0 + B + L},${y0 - B + H} ${x0 + L},${y0 + H}`);
  const logoX = useTransform(G, ({ L, H, x0 }) => x0 + L / 2 - (Math.min(L, H) * 0.7) / 2);
  const logoY = useTransform(G, ({ L, H, y0 }) => y0 + H / 2 - (Math.min(L, H) * 0.7 / 1.25) / 2);
  const logoW = useTransform(G, ({ L, H }) => Math.min(L, H) * 0.7);
  const logoH = useTransform(G, ({ L, H }) => Math.min(L, H) * 0.7 / 1.25);

  // L — dimension line below the box (extension lines + end serifs + centred label).
  const Lx1 = useTransform(G, g => g.x0);
  const Lx2 = useTransform(G, g => g.x0 + g.L);
  const Ly = useTransform(G, g => g.y0 + g.H + 20);
  const Ley = useTransform(G, g => g.y0 + g.H + 5);
  const LyT = useTransform(Ly, v => v - 4);
  const LyB = useTransform(Ly, v => v + 4);
  const Lmx = useTransform(G, g => g.x0 + g.L / 2);
  const Lly = useTransform(G, g => g.y0 + g.H + 37);
  // H — dimension line to the left.
  const Hx = useTransform(G, g => g.x0 - 20);
  const Hy1 = useTransform(G, g => g.y0);
  const Hy2 = useTransform(G, g => g.y0 + g.H);
  const Hex = useTransform(G, g => g.x0 - 5);
  const HxL = useTransform(Hx, v => v - 4);
  const HxR = useTransform(Hx, v => v + 4);
  const Hmy = useTransform(G, g => g.y0 + g.H / 2);
  const Hlx = useTransform(G, g => g.x0 - 28);
  // B — depth: a real dimension line on the top-LEFT depth edge, offset up-left (the one direction
  // that is always clear of the box in this isometric view). Extension lines + serifs, like L and H.
  const BO = 13;
  const Bx1 = useTransform(G, g => g.x0 - BO);
  const By1 = useTransform(G, g => g.y0 - BO);
  const Bx2 = useTransform(G, g => g.x0 + g.B - BO);
  const By2 = useTransform(G, g => g.y0 - g.B - BO);
  const Bex0 = useTransform(G, g => g.x0);
  const Bey0 = useTransform(G, g => g.y0);
  const Bex1 = useTransform(G, g => g.x0 + g.B);
  const Bey1 = useTransform(G, g => g.y0 - g.B);
  const Bx1a = useTransform(Bx1, v => v - 3); const By1a = useTransform(By1, v => v - 3);
  const Bx1b = useTransform(Bx1, v => v + 3); const By1b = useTransform(By1, v => v + 3);
  const Bx2a = useTransform(Bx2, v => v - 3); const By2a = useTransform(By2, v => v - 3);
  const Bx2b = useTransform(Bx2, v => v + 3); const By2b = useTransform(By2, v => v + 3);
  const Blx = useTransform(G, g => g.x0 + g.B / 2 - BO - 6);
  const Bly = useTransform(G, g => g.y0 - g.B / 2 - BO - 6);

  // Weight dial — cube-root scale so even a 10 g change moves the needle.
  // Auto-ranging scale: pick the smallest "nice" full-scale that keeps the weight comfortably
  // on the dial (with headroom), so the needle is never pegged/overflowed and always reads a
  // meaningful position — from 10 g to hundreds of kg.
  const wKg = Math.max(Number(deadKg) || 0, 0);
  const NICE_MAX = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000];
  const dynMax = NICE_MAX.find(m => m >= wKg * 1.3) || NICE_MAX[NICE_MAX.length - 1];
  const frac = Math.min(Math.max(Math.cbrt(wKg / dynMax), 0), 1);
  const angle = useSpring(-122, { stiffness: 90, damping: 15 });
  useEffect(() => { angle.set(-122 + frac * 244); }, [frac, angle]);
  const CX = 92, CY = 94, R = 58;
  const nx = useTransform(angle, (a) => CX + Math.sin((a * Math.PI) / 180) * (R - 8));
  const ny = useTransform(angle, (a) => CY - Math.cos((a * Math.PI) / 180) * (R - 8));
  const polar = (d, r) => [CX + Math.sin((d * Math.PI) / 180) * r, CY - Math.cos((d * Math.PI) / 180) * r];
  const arc = (fromDeg, toDeg) => {
    const [x1, y1] = polar(fromDeg, R); const [x2, y2] = polar(toDeg, R);
    return `M ${x1} ${y1} A ${R} ${R} 0 ${Math.abs(toDeg - fromDeg) > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const ticks = Array.from({ length: 9 }, (_, i) => -122 + (i / 8) * 244);
  const chargedKg = billedKg != null ? billedKg : (Number(deadKg) || 0);
  const DIM = "#3877FC";

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-1 gap-4 rounded-2xl border border-[#E4E9F1] bg-gradient-to-b from-[#FAFBFF] to-[#F3F6FC] p-5 md:grid-cols-[minmax(0,1.2fr)_auto_minmax(0,1fr)] md:items-center">
        {/* Cardboard box with brand + technical dimension callouts */}
        <svg viewBox="0 0 360 300" className="h-[268px] w-full" aria-hidden="true">
          <defs>
            <linearGradient id="boxTop" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#F0DDB4" /><stop offset="1" stopColor="#E2C793" /></linearGradient>
            <linearGradient id="boxFront" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E4C893" /><stop offset="1" stopColor="#D2AE72" /></linearGradient>
            <linearGradient id="boxSide" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#C6A268" /><stop offset="1" stopColor="#B08A50" /></linearGradient>
          </defs>
          <motion.polygon points={sidePts} fill="url(#boxSide)" stroke="#8F7038" strokeWidth="1.5" strokeLinejoin="round" />
          <motion.polygon points={topPts} fill="url(#boxTop)" stroke="#8F7038" strokeWidth="1.5" strokeLinejoin="round" />
          <motion.polygon points={frontPts} fill="url(#boxFront)" stroke="#8F7038" strokeWidth="1.5" strokeLinejoin="round" />
          <motion.image href="/logo.png" x={logoX} y={logoY} width={logoW} height={logoH} preserveAspectRatio="xMidYMid meet" opacity="0.9" />

          {/* L */}
          <motion.line x1={Lx1} y1={Ley} x2={Lx1} y2={Ly} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Lx2} y1={Ley} x2={Lx2} y2={Ly} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Lx1} y1={Ly} x2={Lx2} y2={Ly} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Lx1} y1={LyT} x2={Lx1} y2={LyB} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Lx2} y1={LyT} x2={Lx2} y2={LyB} stroke={DIM} strokeWidth="2" />
          <motion.text x={Lmx} y={Lly} textAnchor="middle" className="fill-slate-800" style={{ fontSize: 12, fontWeight: 800 }}>{`L · ${l || 0} ${unitL}`}</motion.text>

          {/* H */}
          <motion.line x1={Hex} y1={Hy1} x2={Hx} y2={Hy1} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Hex} y1={Hy2} x2={Hx} y2={Hy2} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Hx} y1={Hy1} x2={Hx} y2={Hy2} stroke={DIM} strokeWidth="2" />
          <motion.line x1={HxL} y1={Hy1} x2={HxR} y2={Hy1} stroke={DIM} strokeWidth="2" />
          <motion.line x1={HxL} y1={Hy2} x2={HxR} y2={Hy2} stroke={DIM} strokeWidth="2" />
          <motion.text x={Hlx} y={Hmy} textAnchor="end" dominantBaseline="middle" className="fill-slate-800" style={{ fontSize: 12, fontWeight: 800 }}>{`H · ${h || 0} ${unitH}`}</motion.text>

          {/* B — depth dimension line (top-left depth edge, offset up-left) */}
          <motion.line x1={Bex0} y1={Bey0} x2={Bx1} y2={By1} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Bex1} y1={Bey1} x2={Bx2} y2={By2} stroke={DIM} strokeWidth="1.3" opacity="0.55" />
          <motion.line x1={Bx1} y1={By1} x2={Bx2} y2={By2} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Bx1a} y1={By1a} x2={Bx1b} y2={By1b} stroke={DIM} strokeWidth="2" />
          <motion.line x1={Bx2a} y1={By2a} x2={Bx2b} y2={By2b} stroke={DIM} strokeWidth="2" />
          <motion.text x={Blx} y={Bly} textAnchor="end" dominantBaseline="middle" className="fill-slate-800" style={{ fontSize: 12, fontWeight: 800 }}>{`B · ${b || 0} ${unitB}`}</motion.text>
        </svg>

        {/* Weight dial */}
        <div className="flex flex-col items-center justify-center">
          <svg viewBox="0 0 184 126" className="h-[116px] w-full max-w-[184px]" aria-hidden="true">
            <path d={arc(-122, 122)} fill="none" stroke="#E2EAF8" strokeWidth="9" strokeLinecap="round" />
            {frac > 0 && <path d={arc(-122, -122 + frac * 244)} fill="none" stroke="#3877FC" strokeWidth="9" strokeLinecap="round" />}
            {ticks.map((t, i) => {
              const [x1, y1] = polar(t, R - 14); const [x2, y2] = polar(t, R - 9);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#AFC0DE" strokeWidth="1.5" strokeLinecap="round" />;
            })}
            <motion.line x1={CX} y1={CY} x2={nx} y2={ny} stroke="#1E3A8A" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx={CX} cy={CY} r="5.5" fill="#1E3A8A" />
            <circle cx={CX} cy={CY} r="2" fill="#fff" />
          </svg>
          <div className="-mt-2 text-center">
            <div className="flex items-baseline justify-center gap-1">
              <AnimatedNumber value={weightDisp} decimals={unitW === "kg" ? 3 : 0} className="text-[22px] font-bold tabular-nums text-slate-900" />
              <span className="text-[13px] font-semibold text-slate-500">{unitW}</span>
            </div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">Dead weight</div>
            <div className="mt-0.5 text-[9.5px] font-medium tabular-nums text-slate-400">scale 0–{dynMax} kg</div>
          </div>
        </div>

        {/* Charged weight */}
        <div className="rounded-xl bg-white/80 px-4 py-3.5 ring-1 ring-inset ring-[#E7ECF5]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Chargeable weight</div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <AnimatedNumber value={chargedKg} decimals={2} className="text-[28px] font-extrabold leading-none tracking-tight tabular-nums text-[#2A59BD]" />
            <span className="text-[15px] font-bold text-[#2A59BD]">kg</span>
          </div>
          <div className="mt-2.5 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-slate-100 pt-2.5 text-[12.5px] font-medium text-slate-500">
            <span>Dead <b className={`tabular-nums ${billedBasis === "dead" ? "text-[#2A59BD]" : "text-slate-700"}`}>{(deadKg || 0).toFixed(2)}</b> kg</span>
            {volKg != null && <span>Volumetric <b className={`tabular-nums ${billedBasis === "vol" ? "text-[#2A59BD]" : "text-slate-700"}`}>{volKg.toFixed(2)}</b> kg</span>}
            {volKg == null && <span className="text-slate-400">Add L×B×H for volumetric</span>}
          </div>
          <p className="mt-1.5 text-[11px] font-medium leading-relaxed text-slate-400">Billed on the higher of dead vs volumetric — (L × B × H) / 5000.</p>
        </div>
      </div>

      {/* Tip */}
      <div className="flex items-start gap-3 rounded-xl border border-amber-200/70 bg-amber-50/70 px-4 py-3">
        <span className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg bg-amber-100 text-amber-600"><LightbulbIcon className="h-3.5 w-3.5" /></span>
        <p className="text-[12.5px] font-medium leading-relaxed text-amber-800">Enter the <b>actual packed weight</b> and the <b>true L × B × H of the shipment box</b>. If the courier re-measures a bigger or heavier box after pickup, it raises a weight dispute and you're charged the difference.</p>
      </div>
    </div>
  );
}
